import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './TransactionTimeline.css';
import './TransactionDocuments.css'; // shared .pdf-viewer-* classes for the pre-approval preview modal
import RPAWizardModal from './RPAWizardModal';
import CBSWizardModal from './CBSWizardModal';
import TDSWizardModal from './TDSWizardModal';
import SPQWizardModal from './SPQWizardModal';
import SPDWizardModal from './SPDWizardModal';
import LoneWolfStageFormCard from './LoneWolfStageFormCard';
import CmaPricingCard from './CmaPricingCard';
import AssignServiceProviderModal from './AssignServiceProviderModal';
import ServiceRequestDetailsModal from './ServiceRequestDetailsModal';
import ChecklistModal from './ChecklistModal';
import NotesPanel from '../notes/NotesPanel';
import { auth } from '../../firebase/config';
import { API_ENDPOINTS, apiClient } from '../../config/api';
import { parseTransactionDocuments } from '../../services/ria.service';
import {
  areStagesLoaded,
  ensureStagesLoaded,
  getCachedStages,
} from '../../services/prospectStageCache';
import { getActiveListingStageMeta } from '../../services/activeListingStageCache';
import { getActiveBuyingStageMeta } from '../../services/activeBuyingStageCache';
import { getActiveRoleFromStorage } from '../../utils/getActiveRole';
import { formatEventForViewer, formatInstantForViewer, formatPlainDate, formatUtcNaiveInstantForViewer, getViewerTimezone, instantToWallClockInZone, resolveTimezoneFromState, eventLocalDateKey } from '../../utils/dateTime';
import stateCityData from '../../data/state_city.json';

// Requester-facing labels for the SP's current response on a service event.
// Same enum the SP mobile app writes via PATCH /users/me/service-requests/{id}/status.
const SR_STATUS_LABELS = {
  AWAITING_ASSIGNMENT: 'Awaiting Assignment',
  PENDING: 'Pending Acceptance',
  ACCEPTED: 'Accepted',
  IN_PROGRESS: 'In Progress',
  // Requester-side wording: COMPLETED is set by the SP ("Mark Job Complete"),
  // so say who marked it rather than a generic "Completed".
  COMPLETED: 'Vendor marked Complete',
  DECLINED: 'Declined',
  CANCELLED: 'Cancelled',
  EXPIRED: 'Expired',
};

/**
 * Resolves the display label for an SR status pill. AWAITING_ASSIGNMENT
 * splits three ways depending on the SR's providerId + routing:
 *   1. Open SR (no providerId AND no routedByCompanyId) — the requester
 *      created the SR without picking a provider up-front and needs to
 *      assign one. Label makes THAT actionability explicit rather than
 *      "waiting on someone else" (which is what plain "Awaiting Assignment"
 *      reads as).
 *   2. Company-routed, no bounce (routedByCompanyId set, previousProviderId
 *      null) — waiting on the company admin to pick an inspector.
 *   3. Company-routed, post-bounce (previousProviderId set) — inspector
 *      declined, company is reshuffling internally.
 * Every other status uses the base map above.
 */
const resolveSrStatusLabel = (status, linkedSr) => {
  if (status === 'AWAITING_ASSIGNMENT') {
    if (linkedSr && !linkedSr.providerId && !linkedSr.routedByCompanyId) {
      return 'Provider Not Assigned';
    }
    if (linkedSr && linkedSr.previousProviderId) {
      return 'Company Reassigning';
    }
    return 'Awaiting Company Assignment';
  }
  // Timeline-derived substatus: SP marked step_6 (Payment Received) complete
  // but hasn't hit step_7 (Mark Job Complete) yet — parent SR is still
  // IN_PROGRESS. Surface "Payment Received" on the pill so the requester
  // sees the SP recorded receipt of payment.
  if (status === 'IN_PROGRESS' && Array.isArray(linkedSr?.timeline)) {
    const step6 = linkedSr.timeline.find((e) => e.id === 'step_6');
    const step7 = linkedSr.timeline.find((e) => e.id === 'step_7');
    if (step6?.status === 'COMPLETED' && step7?.status !== 'COMPLETED') {
      return 'Payment Received';
    }
  }
  return SR_STATUS_LABELS[status] || status;
};

// Same lite pattern mobile's OfferSubmissionCard uses to validate TO chips.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Splits a comma-separated TO field into a deduped (case-insensitive, order-preserving)
// list of valid email addresses — mirrors mobile OfferSubmissionCard's chip commit logic,
// just applied to the whole string at send time instead of per-keystroke.
const parseEmailList = (raw) => {
  const seen = new Set();
  const result = [];
  for (const part of (raw || '').split(',')) {
    const email = part.trim();
    if (!email || !EMAIL_REGEX.test(email)) continue;
    const lower = email.toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);
    result.push(email);
  }
  return result;
};

const normalizeWizardStatus = (status) => String(status || '').toUpperCase().replace(/[\s-]+/g, '_');
const REVIEW_READY_WIZARD_STATUSES = new Set([
  'SUBMITTED',
  'COMPLETED',
  'SENT_FOR_REVIEW',
  'IN_REVIEW',
  'UNDER_REVIEW',
  'REVIEW_PENDING',
  'PENDING_REVIEW',
]);
const isWizardSubmittedForReview = (status) => REVIEW_READY_WIZARD_STATUSES.has(normalizeWizardStatus(status));
// Disclosure form stages that don't gate the next stage from unlocking.
// TDS + SPQ on CA (three sub-stages each) and SPD on CO (three sub-stages).
// Each remains individually actionable while incomplete, but downstream stages
// (NHD, AVID, Home Staging, …) unlock based on non-form prior stages only.
// Mirrors mobile app's TimelineTab.tsx NON_BLOCKING_STAGE_IDS.
const NON_BLOCKING_STAGE_IDS = new Set([
  'seller_tds_form', 'seller_tds_data_submission', 'seller_tds_form_creation',
  'seller_spq_form', 'seller_spq_data_submission', 'seller_spq_form_creation',
  'seller_spd_form', 'seller_spd_data_submission', 'seller_spd_form_creation',
]);
const getCompletedWizardSteps = (draft, totalSteps) => {
  if (!draft) return [];
  if (isWizardSubmittedForReview(draft.status)) {
    return Array.from({ length: totalSteps }, (_, index) => index + 1);
  }
  return Array.isArray(draft.completedSteps)
    ? draft.completedSteps
    : [];
};

// ── Google Places API ────────────────────────────────────────────────────────
// Primary: Places API (New) at places.googleapis.com — supports browser CORS.
//   Requires "Places API (New)" enabled in Google Cloud Console for the project.
// Fallback: Legacy Places API via CRA dev proxy (/google-places → maps.googleapis.com).
//   Works on localhost when the dev proxy in setupProxy.js is active.
const GOOGLE_PLACES_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';

// Restricts Smart Search predictions to the agent's licensed state — mirrors
// AddressAutocomplete.js / mobile app (agents can only select properties in
// the state they're licensed in).
const matchesStateInDescription = (description, stateCode) => {
  const re = new RegExp(`,\\s*${stateCode}(\\s*,|\\s*$)`, 'i');
  return re.test(description);
};

// ── Legacy fallback helpers (used when Places API New returns non-OK) ─────────

// Autocomplete via legacy Places API — routed through CRA dev proxy on localhost
const placesAutocompleteLegacy = async (input) => {
  const url = `/google-places/autocomplete/json?input=${encodeURIComponent(input)}&types=address&components=country:us&key=${GOOGLE_PLACES_KEY}&language=en`;
  const res = await fetch(url);
  const data = await res.json();
  if (!data.predictions) return [];
  // Normalize to match Places API (New) suggestion shape used downstream
  return data.predictions.map(p => ({
    placePrediction: {
      placeId: p.place_id,
      place: `places/${p.place_id}`,
      text: { text: p.description },
      structuredFormat: {
        mainText: { text: p.structured_formatting?.main_text || p.description },
        secondaryText: { text: p.structured_formatting?.secondary_text || '' },
      },
    },
  }));
};

// Place Details via legacy Places API — routed through CRA dev proxy on localhost
// Returns data in Places API (New) shape so parseNewAddressComponents works for both
const placesGetDetailsLegacy = async (placeId) => {
  const url = `/google-places/details/json?place_id=${encodeURIComponent(placeId)}&key=${GOOGLE_PLACES_KEY}&fields=address_component,formatted_address,geometry`;
  const res = await fetch(url);
  const data = await res.json();
  if (data.status !== 'OK' || !data.result) throw new Error(`Legacy Places details failed: ${data.status}`);
  // Convert legacy address_components to Places API (New) shape
  return {
    addressComponents: (data.result.address_components || []).map(c => ({
      longText: c.long_name,
      shortText: c.short_name,
      types: c.types,
    })),
    formattedAddress: data.result.formatted_address || '',
  };
};

// ── Primary: Places API (New) with automatic fallback to legacy ───────────────

// Autocomplete via Places API (New) — POST endpoint with CORS support
// Falls back to legacy proxy if the new API is not enabled (e.g. 403 on localhost dev)
const placesAutocomplete = async (input) => {
  try {
    const res = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': GOOGLE_PLACES_KEY,
      },
      body: JSON.stringify({
        input,
        includedRegionCodes: ['us'],
        languageCode: 'en',
      }),
    });
    if (!res.ok) throw new Error(`Places API (New) autocomplete failed: ${res.status}`);
    const data = await res.json();
    if (data.error) throw new Error(`Places API (New) error: ${data.error.status}`);
    return data.suggestions || [];
  } catch (err) {
    console.warn('Places API (New) unavailable, falling back to legacy proxy:', err.message);
    return await placesAutocompleteLegacy(input);
  }
};

// Place Details via Places API (New) — GET endpoint with CORS support
// Falls back to legacy proxy if the new API is not enabled (e.g. 403 on localhost dev)
const placesGetDetails = async (placeId) => {
  const fields = 'addressComponents,formattedAddress,location,displayName';
  try {
    const res = await fetch(
      `https://places.googleapis.com/v1/places/${encodeURIComponent(placeId)}?languageCode=en`,
      {
        headers: {
          'X-Goog-Api-Key': GOOGLE_PLACES_KEY,
          'X-Goog-FieldMask': fields,
        },
      }
    );
    if (!res.ok) throw new Error(`Places API (New) details failed: ${res.status}`);
    return await res.json();
  } catch (err) {
    console.warn('Places API (New) details unavailable, falling back to legacy proxy:', err.message);
    return await placesGetDetailsLegacy(placeId);
  }
};

// Parse Places API (New) addressComponents into street / city / state / zip
// The new API uses camelCase field names and a slightly different structure
const parseNewAddressComponents = (place) => {
  const components = place.addressComponents || [];
  const find = (type) => components.find(c => c.types?.includes(type));
  const streetNumber = find('street_number')?.longText || '';
  const route = find('route')?.longText || '';
  const street = streetNumber ? `${streetNumber} ${route}` : route;
  const city =
    find('locality')?.longText ||
    find('sublocality_level_1')?.longText ||
    find('sublocality')?.longText ||
    find('administrative_area_level_3')?.longText || '';
  const stateComp = find('administrative_area_level_1');
  const stateCode = stateComp?.shortText || '';
  const zip = find('postal_code')?.longText || '';
  return { street, city, stateCode, zip, formattedAddress: place.formattedAddress || '' };
};

const TransactionTimeline = ({
  transaction,
  milestones = [],
  participants = [],
  isLoading = false,
  isApplyingTemplate = false,
  // Called from the Upload PA stage card after the agent uploads a
  // listing agreement and the parser returns facts. Parent stashes
  // the payload and includes it in the final convertToActive call.
  onListingAgreementParsed,
  onShowCalendarModal,
  onCompleteMilestone,
  onSkipMilestone,
  calendarEntries = [],
  calendarEvents = [],
  onProspectStageComplete,
  onProspectStageSkip,
  onDeleteCalendarEntry,
  onCompleteCalendarEvent,
  onEditStageDate,
  onEditCalendarEventDate,
  onAssignServiceProvider,
  onSearchServiceProviders,
  onSrUpdated,
  srStatusById = {},
  srStatusByProviderEmail = {},
  srAttachmentsById = {},
  srByServiceType = {},
  srsByServiceType = {},
  srById = {},
  srByProviderEmail = {},
  sentSrsLoaded = false,
  accessControl,
  hasProperties = false,
  interestedPropertyId = null,
  onRefreshMilestones,
  onStartActiveListing,
  onActiveListingStageComplete,
  onOpenPurchaseAgreementPicker,
  purchaseAgreementRefreshToken,
  onActiveListingStageSkip,
  activeListingStagesReady = false,
  activeBuyingStagesReady = false,
  isConvertingToActiveListing = false,
  activeListingMlsIdInput = '',
  onActiveListingMlsIdInputChange,
  isSavingMlsId = false,
  onConvertToClosed,
  isConvertingToClosed = false,
  // Called when the agent submits the acceptance-pending banner's date input.
  // Parent PATCHes /api/v1/transactions/{id}/acceptance-date, then refetches.
  onSetAcceptanceDate,
  isSettingAcceptanceDate = false,
}) => {
  // Two-letter US state code that drives this transaction's timeline (CA / CO / ...).
  // Defaults to CA when agentLicenseState is absent — matches mobile txnState derivation.
  const txnState = transaction?.agentLicenseState || 'CA';

  // CO uses an 11-step CBS wizard; all other states use the 10-step RPA wizard.
  const rpaWizardTotalSteps = txnState === 'CO' ? 11 : 10;

  // Ensure prospect stage definitions are loaded for this transaction's state.
  // Re-checks on every txnState change so CO transactions load CO stages after the
  // second fetch returns agentLicenseState — matches mobile useEffect on [txnState].
  const [stagesReady, setStagesReady] = useState(areStagesLoaded(txnState));
  useEffect(() => {
    if (areStagesLoaded(txnState)) {
      setStagesReady(true);
      return;
    }
    let cancelled = false;
    setStagesReady(false);
    ensureStagesLoaded(txnState).then(() => { if (!cancelled) setStagesReady(true); });
    return () => { cancelled = true; };
  }, [txnState]); // eslint-disable-line react-hooks/exhaustive-deps
  const [openMarkAsId, setOpenMarkAsId] = useState(null);
  const markAsRef = useRef(null);
  // "Convert to Closed?" confirmation popup — Close of Escrow milestone completion
  // stays independent of the actual transaction status change (see showConvertToClosedCard below).
  const [showConvertClosedModal, setShowConvertClosedModal] = useState(false);
  // Prospect stage or calendar event whose date is being edited — same
  // date-button + "Change Date" modal UX as active-milestone dates.
  // { kind: 'stage' | 'event', id, name, date }
  const [editingDateEntry, setEditingDateEntry] = useState(null);
  // Calendar-service entry currently open in the "Assign Service Provider" sheet, or null.
  const [assignSpEntry, setAssignSpEntry] = useState(null);
  // Currently-open SR details modal target — set when the requester clicks
  // "View Details" on a timeline row; cleared on modal dismiss.
  const [srDetailsTarget, setSrDetailsTarget] = useState(null);
  // Currently-open Checklist modal target — { srId, attachmentId, mode,
  // reportFileUrl, reportFileName, sr }. mode 'generate' on first click,
  // 'view' on "View Generated" (same popup component either way).
  const [checklistTarget, setChecklistTarget] = useState(null);
  const navigate = useNavigate();

  // View Details click — open the modal instantly with the cached SR from
  // the sentSrs list, then fetch fresh in the background and merge over.
  // The sentSrs cache only refreshes on tab-focus, but billedAmount +
  // finalPaymentAmount routinely mutate mid-flight (SP records invoice,
  // requester logs payment) so the modal shouldn't show stale numbers on
  // open. Filtering out null response fields on merge is intentional so
  // a Jackson-dropped BigDecimal on the response doesn't clobber good
  // cached values — mirrors the defensive merge onSave uses.
  const openSrDetails = React.useCallback(async (linkedSr, entry) => {
    if (!linkedSr) return;
    // Instant open — no blank state while the fresh fetch resolves.
    setSrDetailsTarget({ sr: linkedSr, entry });
    if (!linkedSr.id) return;
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.get(
        API_ENDPOINTS.sentServiceRequestById(linkedSr.id),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const fresh = res.data?.data || res.data || null;
      if (!fresh) return;
      const cleaned = Object.fromEntries(
        Object.entries(fresh).filter(([, v]) => v !== null && v !== undefined),
      );
      setSrDetailsTarget((prev) => {
        // Only apply the fresh data if the modal is still open on the
        // same SR — the user may have closed / opened a different row
        // while the network round-trip was in flight.
        if (!prev || prev.sr?.id !== linkedSr.id) return prev;
        return { ...prev, sr: { ...prev.sr, ...cleaned } };
      });
    } catch (err) {
      // Non-fatal — modal already shows the cached SR. Log for visibility.
      console.warn('[SR details] fresh fetch failed, staying on cached copy:', err);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Per-event ids whose "Documents (N)" collapsible is currently expanded on
  // SR event cards (spec §4). Collapsed by default so the timeline stays
  // compact regardless of how many files a provider has shared.
  const [expandedSrDocs, setExpandedSrDocs] = useState(() => new Set());
  // Post-Closing UI: when the transaction is CLOSED and the backend has seeded
  // POST_CLOSING-phase milestones, the pre-close ("history") milestones collapse
  // above the currently-actionable Post-Closing set. Default collapsed so the
  // Post-Closing timeline is what the agent lands on. Only rendered when phase
  // grouping is actually applicable (see hasPhaseGrouping below).
  const [historyCollapsed, setHistoryCollapsed] = useState(true);
  // Converted-from-Prospect audit trail is read-only and secondary — collapsed
  // by default so the active work (milestones + Active Listing checklist) is
  // the first thing an agent lands on. Toggle expands the prospect stages
  // in-place at the TOP; no stages are editable in either state.
  const [prospectCollapsed, setProspectCollapsed] = useState(true);
  // Attachment ids currently being marked-as-transaction-document — the
  // Mark button spins + stays disabled during the round-trip on that row.
  const [filingAttachments, setFilingAttachments] = useState(() => new Set());
  // Attachment ids that just got filed successfully in this session. The
  // srAttachmentsById prop lags behind (owned by the parent), so we track
  // "just filed" locally and OR it against the prop's filedAsTransactionDocumentId
  // to flip the row to a Filed pill immediately.
  const [justFiledAttachments, setJustFiledAttachments] = useState(() => new Set());
  const [showRPAWizard, setShowRPAWizard] = useState(false);
  const [rpaNoPropertyError, setRpaNoPropertyError] = useState(false);
  const [isLaunchingRPA, setIsLaunchingRPA] = useState(false);
  const [isResumingRPA, setIsResumingRPA] = useState(false);
  const [rpaLaunchError, setRpaLaunchError] = useState(null);
  const [rpaDraft, setRpaDraft] = useState(null);
  // Selected interested-property object, passed to the RPA wizard for first-launch auto-population
  const [rpaInterestedProperty, setRpaInterestedProperty] = useState(null);
  // TDS Wizard state
  const [showTDSWizard, setShowTDSWizard] = useState(false);
  const [isLaunchingTDS, setIsLaunchingTDS] = useState(false);
  const [isResumingTDS, setIsResumingTDS] = useState(false);
  const [tdsLaunchError, setTdsLaunchError] = useState(null);
  const [tdsDraft, setTdsDraft] = useState(null);
  const [tdsProgress, setTdsProgress] = useState(null);
  // SPQ Wizard state
  const [showSPQWizard, setShowSPQWizard] = useState(false);
  const [isLaunchingSPQ, setIsLaunchingSPQ] = useState(false);
  const [isResumingSPQ, setIsResumingSPQ] = useState(false);
  const [spqLaunchError, setSpqLaunchError] = useState(null);
  const [spqDraft, setSpqDraft] = useState(null);
  const [spqProgress, setSpqProgress] = useState(null);
  // SPD Wizard state (CO only — Seller's Property Disclosure)
  const [showSPDWizard, setShowSPDWizard] = useState(false);
  const [isLaunchingSPD, setIsLaunchingSPD] = useState(false);
  const [isResumingSPD, setIsResumingSPD] = useState(false);
  const [spdLaunchError, setSpdLaunchError] = useState(null);
  const [spdDraft, setSpdDraft] = useState(null);
  const [spdProgress, setSpdProgress] = useState(null);
  const sellerParticipant = useMemo(
    () => participants?.find(p =>
      (p.role || p.participantRole || p.userRole || '').toUpperCase() === 'SELLER'
    ),
    [participants]
  );
  const sellerFullName = useMemo(() => {
    if (sellerParticipant) {
      return sellerParticipant.name
        || sellerParticipant.fullName
        || [sellerParticipant.firstName, sellerParticipant.lastName].filter(Boolean).join(' ')
        || '';
    }
    return transaction?.sellerName || '';
  }, [sellerParticipant, transaction]);
  const listingAgentParticipant = useMemo(
    () => participants?.find(p => {
      const role = (p.role || p.participantRole || p.userRole || p.type || '')
        .toUpperCase()
        .replace(/[\s-]+/g, '_');
      return role === 'LISTING_AGENT' || role === 'CO_LISTING_AGENT';
    }),
    [participants]
  );
  const listingAgentFullName = useMemo(() => {
    if (listingAgentParticipant) {
      return listingAgentParticipant.name
        || listingAgentParticipant.fullName
        || [listingAgentParticipant.firstName, listingAgentParticipant.lastName].filter(Boolean).join(' ')
        || '';
    }
    return transaction?.listingAgentName || transaction?.listingAgent?.name || '';
  }, [listingAgentParticipant, transaction]);
  const listingAgentBrokerage = useMemo(() => {
    if (listingAgentParticipant) {
      return listingAgentParticipant.brokerageName
        || listingAgentParticipant.company
        || listingAgentParticipant.companyName
        || listingAgentParticipant.organization
        || '';
    }
    return transaction?.listingBrokerage
      || transaction?.listingOfficeName
      || transaction?.listingAgent?.company
      || '';
  }, [listingAgentParticipant, transaction]);
  const enrichedSellerTransaction = useMemo(() => {
    if (!transaction) return transaction;
    return {
      ...transaction,
      sellerName: sellerFullName,
      listingAgentName: listingAgentFullName,
      listingBrokerage: listingAgentBrokerage,
      county: transaction.county || transaction.propertyCounty || '',
    };
  }, [transaction, sellerFullName, listingAgentFullName, listingAgentBrokerage]);
  // NHD upload state (mirrors mobile TimelineTab)
  const [isUploadingNhd, setIsUploadingNhd] = useState(false);
  const [nhdUploadError, setNhdUploadError] = useState(null);
  const [nhdDocuments, setNhdDocuments] = useState([]);
  const [isDeletingNhdDoc, setIsDeletingNhdDoc] = useState(null);
  const [viewingNhdDocId, setViewingNhdDocId] = useState(null);
  const nhdFileInputRef = useRef(null);
  // Purchase Agreement upload state (Active Listing checklist, upload_purchase_agreement
  // stage) — mirrors mobile TimelineTab's purchaseAgreementDocuments pattern (same as NHD).
  const [isUploadingPurchaseAgreement, setIsUploadingPurchaseAgreement] = useState(false);
  const [purchaseAgreementUploadError, setPurchaseAgreementUploadError] = useState(null);
  const [purchaseAgreementDocuments, setPurchaseAgreementDocuments] = useState([]);
  const [isDeletingPurchaseAgreementDoc, setIsDeletingPurchaseAgreementDoc] = useState(null);
  const purchaseAgreementFileInputRef = useRef(null);
  // Per-doc label (RPA / COUNTER_OFFER / LISTING_AGREEMENT / OTHER) chosen
  // inline in the Upload PA stage card. Keyed by documentId. Default is
  // inferred from the filename on first render; agent can change via the
  // dropdown. The picker on Mark As uses this as the starting selection.
  const [purchaseAgreementDocLabels, setPurchaseAgreementDocLabels] = useState({});
  const [isDraggingUploadZone, setIsDraggingUploadZone] = useState(false);
  // Offer version-chain preview on the Upload PA stage. Listing-side only —
  // fetched lazily so the agent sees existing PA + counters at a glance
  // before marking the stage complete.
  // Parsed listing-agreement payload — stashed after agent uploads a listing
  // agreement on the Upload PA stage. Passed through to convertToActive so
  // the backend's applyListingAgreementOverrides fires at the same commit
  // that persists the active timeline.
  const [parsedListingAgreement, setParsedListingAgreement] = useState(null);
  const [isUploadingListingAgreement, setIsUploadingListingAgreement] = useState(false);
  const listingAgreementFileInputRef = useRef(null);
  // AVID mark-complete state
  const [isMarkingAvidComplete, setIsMarkingAvidComplete] = useState(false);
  const [avidCompleteError, setAvidCompleteError] = useState(null);
  // Confirm Property stage
  const [confirmProperties, setConfirmProperties] = useState([]);
  const [confirmPropertyId, setConfirmPropertyId] = useState('');
  const [confirmPropsLoading, setConfirmPropsLoading] = useState(false);
  const [isFetchingWizard, setIsFetchingWizard] = useState(false);
  // Smart Search state
  const [smartSearchOpen, setSmartSearchOpen] = useState(false);
  const [smartSearchQuery, setSmartSearchQuery] = useState('');
  const [smartSearchResults, setSmartSearchResults] = useState([]);
  const [smartSearchLoading, setSmartSearchLoading] = useState(false);
  const [smartSearchError, setSmartSearchError] = useState('');
  const [addingSmartProperty, setAddingSmartProperty] = useState(false);
  const smartDebounceRef = useRef(null);
  // Google Places autocomplete state
  const [placePredictions, setPlacePredictions] = useState([]);
  const [placesLoading, setPlacesLoading] = useState(false);
  const [attomLoading, setAttomLoading] = useState(false);
  // Agent's licensed state — restricts Smart Search address predictions (matches mobile app)
  const [userLicenseState, setUserLicenseState] = useState('');
  // Milestone date editing (matches mobile TimelineTab)
  const [editingDateMilestone, setEditingDateMilestone] = useState(null);
  const [editingDate, setEditingDate] = useState('');
  const [isDateSaving, setIsDateSaving] = useState(false);
  // Offer Submission state
  const [offerSubmissionDraft, setOfferSubmissionDraft] = useState(null);
  const [offerPreApprovalFile, setOfferPreApprovalFile] = useState(null);
  const [offerRpaSigned, setOfferRpaSigned] = useState(false);
  const [isUploadingPreApproval, setIsUploadingPreApproval] = useState(false);
  // offerPreApprovalFile grows a `gcsDocumentId` field for the GCS migration (see below)
  const [isClearingPreApproval, setIsClearingPreApproval] = useState(false);
  const [preApprovalPreviewTier, setPreApprovalPreviewTier] = useState(null); // 'local' | 'gcs' | 'legacy'
  const [preApprovalLocalFile, setPreApprovalLocalFile] = useState(null);
  const [preApprovalPreviewUrl, setPreApprovalPreviewUrl] = useState(null);
  const [showPreApprovalPreview, setShowPreApprovalPreview] = useState(false);
  const [isLoadingPreApprovalPreview, setIsLoadingPreApprovalPreview] = useState(false);
  const [isPreApprovalDragging, setIsPreApprovalDragging] = useState(false);
  const [isSendingOffer, setIsSendingOffer] = useState(false);
  const [showConfirmSendOfferModal, setShowConfirmSendOfferModal] = useState(false);
  const [isShareOfferLoading, setIsShareOfferLoading] = useState(false);
  const [offerSent, setOfferSent] = useState(false);
  const [offerEmailBody, setOfferEmailBody] = useState('');
  const [offerToEmail, setOfferToEmail] = useState('');
  const [offerEmailSubject, setOfferEmailSubject] = useState('');
  const [showOfferEmailModal, setShowOfferEmailModal] = useState(false);
  const [offerEmailDraft, setOfferEmailDraft] = useState('');
  const [offerFlashError, setOfferFlashError] = useState(false);
  const [offerSendError, setOfferSendError] = useState(null);
  const [offerAutoAttachments, setOfferAutoAttachments] = useState([]);
  const [offerExcludedAutoNames, setOfferExcludedAutoNames] = useState([]);
  const [offerExtraAttachments, setOfferExtraAttachments] = useState([]);
  const preApprovalInputRef = useRef(null);
  const offerExtraFileInputRef = useRef(null);

  // Mark-as-Transaction-Document (spec §5). Copies the shared attachment
  // into the transaction's Docs tab under the shared service-request folder.
  // Server-side idempotent — a double-click won't create duplicates.
  const handleMarkAsTransactionDocument = async (srId, att) => {
    if (!srId || !att?.id) return;
    if (filingAttachments.has(att.id)) return;
    setFilingAttachments(prev => new Set(prev).add(att.id));
    try {
      const token = await getFirebaseToken();
      await apiClient.post(
        API_ENDPOINTS.fileServiceAttachmentToTransaction(srId, att.id),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJustFiledAttachments(prev => new Set(prev).add(att.id));
    } catch (err) {
      const status = err?.response?.status;
      const msg = status === 403
        ? 'The provider needs to share this file with you before you can file it.'
        : 'Could not file this document. Please try again.';
      window.alert(msg);
    } finally {
      setFilingAttachments(prev => {
        const next = new Set(prev);
        next.delete(att.id);
        return next;
      });
    }
  };

  const toggleSrDocsExpanded = (eventId) => {
    setExpandedSrDocs(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId); else next.add(eventId);
      return next;
    });
  };

  const getFirebaseToken = () => new Promise((resolve, reject) => {
    if (auth.currentUser) {
      // false = use cached token if still valid; Firebase auto-refreshes only when expired
      auth.currentUser.getIdToken(false).then(resolve).catch(reject);
    } else {
      const { onAuthStateChanged } = require('firebase/auth');
      const unsub = onAuthStateChanged(auth, user => {
        unsub();
        if (user) user.getIdToken(false).then(resolve).catch(reject);
        else reject(new Error('Not authenticated'));
      });
      setTimeout(() => reject(new Error('Auth timeout')), 10000);
    }
  });

  // Derive the logged-in agent's licensed state for Smart Search address restriction
  // (matches mobile app / AddressAutocomplete.js on Purchase/Listing/Profile pages)
  useEffect(() => {
    try {
      const stored = localStorage.getItem('user');
      if (!stored) return;
      const profile = JSON.parse(stored);
      let licenseState = profile.licenseState || profile.stateCode || profile.state || '';
      if (licenseState && licenseState.length > 2) {
        const found = stateCityData.find(
          item => item.state.toLowerCase() === licenseState.toLowerCase()
        );
        licenseState = found ? found.stateCode : '';
      }
      setUserLicenseState(licenseState.toUpperCase() || '');
    } catch (e) {
      // ignore parse errors
    }
  }, []);

  // Fetch interested properties for the Confirm Property stage
  // Uses the same API + MLS-enrichment approach as the Properties tab
  useEffect(() => {
    const txnStatus = transaction?.status?.toUpperCase();
    const txnSide = (transaction?.side || transaction?.type || '').toUpperCase();
    const isBuyingTxn = txnSide === 'BUYING' || txnSide === 'PURCHASE';
    if (txnStatus !== 'PENDING' || !isBuyingTxn || !transaction?.id) return;

    let cancelled = false;

    const enrichWithMls = async (list, token) => {
      return Promise.all(list.map(async (prop) => {
        // Skip enrichment if property already has a photo / name data
        const hasData = prop.address || prop.streetAddress || prop.fullAddress || prop.propertyAddress;
        const mlsId = prop.mlsId || prop.mlsNumber || prop.listingId;
        if (hasData || !mlsId) return prop;
        try {
          const res = await apiClient.get(
            API_ENDPOINTS.mlsProperty(mlsId),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const mlsData = Array.isArray(res.data) ? res.data[0] : res.data;
          if (mlsData) return { ...prop, ...mlsData, id: prop.id, status: prop.status };
        } catch {
          // best-effort — return original if MLS lookup fails
        }
        return prop;
      }));
    };

    const fetchProps = async () => {
      setConfirmPropsLoading(true);
      try {
        const token = await getFirebaseToken();
        const res = await apiClient.get(
          API_ENDPOINTS.interestedProperties(transaction.id),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        const raw = res.data;
        let list = [];
        if (Array.isArray(raw)) list = raw;
        else if (Array.isArray(raw?.data)) list = raw.data;
        else if (Array.isArray(raw?.properties)) list = raw.properties;
        else if (Array.isArray(raw?.results)) list = raw.results;
        else if (Array.isArray(raw?.content)) list = raw.content;
        else if (raw?.id) list = [raw];
        const enriched = await enrichWithMls(list, token);
        if (!cancelled) {
          setConfirmProperties(enriched);
          // Seed previously selected property on first load (mirrors mobile loadProperties)
          // Priority 1: selectedForTransaction flag on property (set by selectPropertyForTransaction API)
          // Priority 2: interestedPropertyId stored in prospect stage metadata
          setConfirmPropertyId(prev => {
            if (prev) return prev; // already set — don't overwrite user choice
            const flagged = enriched.find(p => p.selectedForTransaction || p.selected || p.isSelected || p.selectedAt);
            if (flagged) return flagged.id || flagged.interestedPropertyId || '';
            const stageData = (transaction?.metadata?.prospectTimeline?.stages || [])
              .find(s => s.stageId === 'buyer_confirm_property');
            return stageData?.interestedPropertyId || '';
          });
        }
      } catch (e) {
        console.warn('[Confirm Property] fetch failed:', e.message);
      } finally {
        if (!cancelled) setConfirmPropsLoading(false);
      }
    };

    fetchProps();
    return () => { cancelled = true; };
  }, [transaction?.id, transaction?.status, transaction?.side, transaction?.type]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch existing TDS draft on mount for listing PENDING transactions
  useEffect(() => {
    const txnStatus = transaction?.status?.toUpperCase();
    const txnSide = (transaction?.side || transaction?.type || '').toUpperCase();
    const isListingTxn = txnSide === 'LISTING' || txnSide === 'SELLING';
    if (txnStatus !== 'PENDING' || !isListingTxn || !transaction?.id) return;

    let cancelled = false;
    const fetchTdsDraft = async () => {
      try {
        const token = await getFirebaseToken();
        const res = await apiClient.get(
          API_ENDPOINTS.tdsWizardDrafts(transaction.id),
          { headers: { Authorization: `Bearer ${token}` }, params: { interestedPropertyId: transaction?.propertyId || interestedPropertyId } }
        );
        if (cancelled) return;
        const raw = res.data?.data || res.data;
        const draft = Array.isArray(raw) ? raw[0] : raw;
        if (draft?.id || draft?.wizardId) {
          setTdsDraft(draft);
          const completedNums = getCompletedWizardSteps(draft, 6);
          if (completedNums.length > 0) {
            setTdsProgress({ completedSteps: completedNums, resumeAtStep: draft?.currentStep || (completedNums.length + 1) });
          }
        }
      } catch {
        // no existing draft — leave null so Launch button shows
      }
    };
    fetchTdsDraft();
    return () => { cancelled = true; };
  }, [transaction?.id, transaction?.status, transaction?.side, transaction?.type]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch existing SPQ draft on mount for listing PENDING transactions
  useEffect(() => {
    const txnStatus = transaction?.status?.toUpperCase();
    const txnSide = (transaction?.side || transaction?.type || '').toUpperCase();
    const isListingTxn = txnSide === 'LISTING' || txnSide === 'SELLING';
    if (txnStatus !== 'PENDING' || !isListingTxn || !transaction?.id) return;

    let cancelled = false;
    const fetchSpqDraft = async () => {
      try {
        const token = await getFirebaseToken();
        const res = await apiClient.get(
          API_ENDPOINTS.spqWizardDrafts(transaction.id),
          { headers: { Authorization: `Bearer ${token}` }, params: { interestedPropertyId: transaction?.propertyId || interestedPropertyId } }
        );
        if (cancelled) return;
        const raw = res.data?.data || res.data;
        const draft = Array.isArray(raw) ? raw[0] : raw;
        if (draft?.id || draft?.wizardId) {
          setSpqDraft(draft);
          const completedNums = getCompletedWizardSteps(draft, 9);
          if (completedNums.length > 0) {
            setSpqProgress({ completedSteps: completedNums, resumeAtStep: draft?.currentStep || (completedNums.length + 1) });
          }
        }
      } catch {
        // no existing draft — leave null so Launch button shows
      }
    };
    fetchSpqDraft();
    return () => { cancelled = true; };
  }, [transaction?.id, transaction?.status, transaction?.side, transaction?.type]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch existing SPD draft on mount for CO listing PENDING transactions
  useEffect(() => {
    const txnStatus = transaction?.status?.toUpperCase();
    const txnSide = (transaction?.side || transaction?.type || '').toUpperCase();
    const isListingTxn = txnSide === 'LISTING' || txnSide === 'SELLING';
    const isCO = (transaction?.agentLicenseState || '') === 'CO';
    if (txnStatus !== 'PENDING' || !isListingTxn || !isCO || !transaction?.id) return;

    let cancelled = false;
    const fetchSpdDraft = async () => {
      try {
        const token = await getFirebaseToken();
        const res = await apiClient.get(
          API_ENDPOINTS.spdWizardDrafts(transaction.id),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        const raw = res.data?.data || res.data;
        const draft = Array.isArray(raw) ? raw[0] : raw;
        if (draft?.id || draft?.wizardId) {
          setSpdDraft(draft);
          const completedNums = getCompletedWizardSteps(draft, 10);
          if (completedNums.length > 0) {
            setSpdProgress({ completedSteps: completedNums, resumeAtStep: draft?.currentStep || (completedNums.length + 1) });
          }
        }
      } catch {
        // no existing draft — leave null so Launch button shows
      }
    };
    fetchSpdDraft();
    return () => { cancelled = true; };
  }, [transaction?.id, transaction?.status, transaction?.side, transaction?.type, transaction?.agentLicenseState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch existing CBS/RPA wizard draft on mount for buying PENDING transactions
  // Runs whenever confirmPropertyId or interestedPropertyId is available (including auto-seed from stage metadata)
  useEffect(() => {
    const txnStatus = transaction?.status?.toUpperCase();
    const txnSide = (transaction?.side || transaction?.type || '').toUpperCase();
    const isBuyingTxn = txnSide === 'BUYING' || txnSide === 'PURCHASE';
    const propertyId = confirmPropertyId || interestedPropertyId;
    if (txnStatus !== 'PENDING' || !isBuyingTxn || !transaction?.id || !propertyId) return;

    let cancelled = false;
    const fetchWizardDraft = async () => {
      try {
        const token = await getFirebaseToken();
        const draftEndpoint = txnState === 'CO' ? API_ENDPOINTS.cbsWizardDrafts : API_ENDPOINTS.rpaWizardDrafts;
        const res = await apiClient.get(
          draftEndpoint(transaction.id),
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { interestedPropertyId: propertyId },
          }
        );
        if (cancelled) return;
        const raw = res.data?.data || res.data;
        const draft = Array.isArray(raw) ? raw[0] : raw;
        if (draft?.id || draft?.wizardId) setRpaDraft(draft);
      } catch {
        // no draft yet — leave null so Launch button shows
      }
    };
    fetchWizardDraft();
    return () => { cancelled = true; };
  }, [transaction?.id, transaction?.status, transaction?.side, transaction?.type, transaction?.agentLicenseState, confirmPropertyId, interestedPropertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror mobile OfferSubmissionCard: check DocuSign signing status for RPA/CBS checklist item.
  // Strategy: fetch transaction DocuSign docs → short-circuit on isSigned flag → fall through to
  // per-envelope status/recipient check, matching buyer names from the wizard draft.
  useEffect(() => {
    const txnStatus = transaction?.status?.toUpperCase();
    const txnSide = (transaction?.side || transaction?.type || '').toUpperCase();
    const isBuyingTxn = txnSide === 'BUYING' || txnSide === 'PURCHASE';
    // DocuSign is COLORADO-ONLY — this envelope/recipient check fetches DocuSign documents, so
    // skip it for non-CO transactions (Lone Wolf, no DocuSign envelopes).
    const isCOTxn = (transaction?.propertyState || transaction?.agentLicenseState || '').toUpperCase() === 'CO';
    if (txnStatus !== 'PENDING' || !isBuyingTxn || !transaction?.id || !isCOTxn) return;

    setOfferRpaSigned(false);
    let cancelled = false;

    const run = async () => {
      try {
        const token = await getFirebaseToken();
        const headers = { Authorization: `Bearer ${token}` };

        // Build buyer name list from current draft for recipient matching
        const draftSteps = offerSubmissionDraft?.steps || {};
        const s2data = draftSteps['2']?.data || {};
        const _norm = (s) => (s || '').toLowerCase().trim().replace(/\s+/g, ' ');
        const rpaBuyerNames = [];
        if (s2data.buyer1Name) {
          rpaBuyerNames.push(_norm(s2data.buyer1Name));
          if (s2data.buyer2Name) rpaBuyerNames.push(_norm(s2data.buyer2Name));
        } else if (Array.isArray(s2data.buyers)) {
          s2data.buyers.forEach(b => {
            const n = typeof b === 'string' ? b : [b.firstName, b.lastName].filter(Boolean).join(' ');
            if (n) rpaBuyerNames.push(_norm(n));
          });
        }

        const isRpaDoc = (d) => {
          const name = (d?.name ?? '').toLowerCase();
          const type = (d?.documentType ?? '').toLowerCase();
          return name.includes('rpa') || name.includes('purchase agreement') ||
                 name.includes('residential purchase') || type.includes('rpa') || type.includes('purchase');
        };

        const buyersSigned = (recipients) => {
          const signers = recipients?.signers ?? [];
          const relevant = rpaBuyerNames.length === 0
            ? signers
            : signers.filter(s => rpaBuyerNames.includes(_norm(s?.name)));
          return relevant.length > 0 && relevant.every(s => s?.status === 'completed' || s?.status === 'signed');
        };

        const checkEnvelope = async (eid) => {
          try {
            const envResp = await apiClient.get(API_ENDPOINTS.envelopeStatus(eid), { headers });
            if (envResp.data?.status === 'completed') return true;
            const recResp = await apiClient.get(API_ENDPOINTS.envelopeRecipients(eid), { headers });
            return buyersSigned(recResp.data);
          } catch { return false; }
        };

        const candidateIds = new Set();
        try {
          const resp = await apiClient.get(API_ENDPOINTS.transactionDocusignDocuments(transaction.id), { headers });
          const flat = Array.isArray(resp.data)
            ? resp.data
            : Array.isArray(resp.data?.documents)
              ? resp.data.documents
              : Array.isArray(resp.data?.envelopes)
                ? resp.data.envelopes.flatMap(e => (e.documents ?? []).map(d => ({ ...d, envelopeId: e.envelopeId })))
                : [];

          for (const d of flat) {
            if (!isRpaDoc(d)) continue;
            if (d?.isSigned === true) {
              if (!cancelled) setOfferRpaSigned(true);
              return;
            }
            if (d?.envelopeId) candidateIds.add(d.envelopeId);
          }
        } catch { /* document listing failed — fall through to envelope checks */ }

        for (const eid of candidateIds) {
          if (await checkEnvelope(eid)) {
            if (!cancelled) setOfferRpaSigned(true);
            return;
          }
        }
      } catch { /* silent — checklist stays unchecked on error */ }
    };
    run();
    return () => { cancelled = true; };
  }, [transaction?.id, transaction?.status, transaction?.side, transaction?.type, offerSubmissionDraft?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Offer Submission helpers ──────────────────────────────────────────────────

  const parseOfferAmount = (val) => (val ? parseFloat(String(val).replace(/,/g, '')) : 0);

  const fmtOfferCurrency = (n) => {
    if (!n) return '';
    return n >= 1000 ? `$${Number(n).toLocaleString('en-US')}` : `$${n}`;
  };

  const buildOfferEmailBody = (steps) => {
    const s1 = steps['1']?.data || {};
    const s2 = steps['2']?.data || {};
    const s3 = steps['3']?.data || {};
    const s4 = steps['4']?.data || {};
    const s6 = steps['6']?.data || {};
    const s9 = steps['9']?.data || {};

    const address = [s1.address, s1.city].filter(Boolean).join(', ');

    // Buyer names — try flat keys first (what the API round-trips), then buyers array
    const buyerNames = [];
    if (s2.buyer1Name) {
      buyerNames.push(s2.buyer1Name);
      if (s2.buyer2Name) buyerNames.push(s2.buyer2Name);
    } else if (Array.isArray(s2.buyers)) {
      s2.buyers.forEach(b => {
        const name = [b.firstName, b.lastName].filter(Boolean).join(' ');
        if (name) buyerNames.push(name);
      });
    }
    const buyers = buyerNames.join(' & ');

    const price = parseOfferAmount(s3.offerPrice);
    const emd = parseOfferAmount(s3.emdAmount || s3.initialDeposit);
    const emdDays = s3.emdDueDays || s3.depositDays || '';
    const financing = s4.financingType || '';
    const loanAmount = parseOfferAmount(s4.firstLoanAmount || s4.loanAmount);
    const loanTerm = s4.loanTerm || '';
    const coe = s6.coeDaysAfterAcceptance || '';
    const expiryDate = s9.offerExpirationDate || '';
    const expiryTime = s9.offerExpirationTime || '';
    const agentName = s2.agentName || s2.buyerAgentName || '';
    const agentDRE = s2.agentDRE || s2.buyerAgentLicense || '';
    const brokerage = s2.brokerageName || s2.buyerBrokerage || '';
    const agentEmail = s2.agentEmail || s2.buyerAgentEmail || '';
    const agentPhone = s2.agentPhone || s2.buyerAgentPhone || '';

    const listingAgentName = s2.listingAgentName || '';
    const listingAgentFirstName = listingAgentName.split(' ')[0] || 'Agent';

    const lines = [];
    lines.push(`Dear ${listingAgentFirstName},`);
    lines.push('');
    if (buyers) {
      lines.push(`Please find attached an offer on behalf of my clients, ${buyers}, for the property at ${address}.`);
    } else {
      lines.push(`Please find attached an offer for the property at ${address}.`);
    }
    lines.push('');
    if (price) lines.push(`Offer Price:     ${fmtOfferCurrency(price)}`);
    if (emd) lines.push(`Earnest Money:   ${fmtOfferCurrency(emd)}${emdDays ? ` — due within ${emdDays} business days` : ''}`);
    if (financing) {
      let finLine = `Financing:       ${financing}`;
      if (loanAmount) finLine += ` · ${fmtOfferCurrency(loanAmount)}`;
      if (loanTerm) finLine += ` · ${loanTerm} yr`;
      lines.push(finLine);
    }
    if (coe) lines.push(`Close of Escrow: ${coe} days from acceptance`);
    if (expiryDate) lines.push(`Offer Expires:   ${expiryDate}${expiryTime ? ` at ${expiryTime}` : ''}`);
    lines.push('');
    lines.push('Docs attached: Purchase Agreement, Pre-Approval');
    lines.push('');
    lines.push('Please confirm receipt. I look forward to working with you.');
    lines.push('');
    let sig = agentName;
    if (agentDRE) sig += `  ·  DRE# ${agentDRE}`;
    if (sig.trim()) lines.push(sig);
    if (brokerage) lines.push(brokerage);
    const contact = [agentPhone, agentEmail].filter(Boolean).join('  ·  ');
    if (contact) lines.push(contact);
    return lines.join('\n');
  };

  // Fetch RPA draft data to populate offer submission section
  useEffect(() => {
    const txnStatus = transaction?.status?.toUpperCase();
    const txnSide = (transaction?.side || transaction?.type || '').toUpperCase();
    const isBuyingTxn = txnSide === 'BUYING' || txnSide === 'PURCHASE';
    if (txnStatus !== 'PENDING' || !isBuyingTxn || !transaction?.id) return;

    let cancelled = false;
    const fetchOfferDraft = async () => {
      try {
        const token = await getFirebaseToken();
        const propertyId = confirmPropertyId || interestedPropertyId;
        const draftEndpoint = txnState === 'CO' ? API_ENDPOINTS.cbsWizardDrafts : API_ENDPOINTS.rpaWizardDrafts;
        const res = await apiClient.get(
          draftEndpoint(transaction.id),
          {
            headers: { Authorization: `Bearer ${token}` },
            ...(propertyId ? { params: { interestedPropertyId: propertyId } } : {}),
          }
        );
        if (cancelled) return;
        const raw = res.data?.data || res.data;
        const draft = Array.isArray(raw) ? raw[0] : raw;
        if (draft?.id || draft?.wizardId) setOfferSubmissionDraft(draft);
      } catch {
        // no draft yet
      }
    };
    fetchOfferDraft();
    return () => { cancelled = true; };
  }, [transaction?.id, transaction?.status, transaction?.side, transaction?.type, confirmPropertyId, interestedPropertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build email body / subject / TO whenever the RPA draft changes
  useEffect(() => {
    if (!offerSubmissionDraft?.steps) return;
    const steps = offerSubmissionDraft.steps;
    const s1 = steps['1']?.data || {};
    const s2 = steps['2']?.data || {};
    const s3 = steps['3']?.data || {};

    const body = buildOfferEmailBody(steps);
    setOfferEmailBody(body);

    const address = [s1.address, s1.city].filter(Boolean).join(', ');
    const price = parseOfferAmount(s3.offerPrice);
    const subject = `Offer — ${address}${price ? ` · ${fmtOfferCurrency(price)}` : ''}`;
    if (!offerEmailSubject && subject.trim() !== 'Offer —') setOfferEmailSubject(subject);

    const listingAgentEmail = s2.listingAgentEmail || '';
    if (!offerToEmail && listingAgentEmail) setOfferToEmail(listingAgentEmail);
  }, [offerSubmissionDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load existing pre-approval from backend
  useEffect(() => {
    const propertyId = confirmPropertyId || interestedPropertyId;
    if (!transaction?.id || !propertyId) return;
    let cancelled = false;
    const fetchPreApproval = async () => {
      try {
        const token = await getFirebaseToken();
        const res = await apiClient.get(
          API_ENDPOINTS.interestedProperties(transaction.id),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        const raw = res.data;
        const list = Array.isArray(raw) ? raw
          : Array.isArray(raw?.data) ? raw.data
          : Array.isArray(raw?.content) ? raw.content
          : raw?.id ? [raw] : [];
        const prop = list.find(p => p.id === propertyId);
        if (prop?.preApprovalGcsDocumentId || prop?.preApprovalFileName || prop?.preApprovalDocumentId) {
          setOfferPreApprovalFile({
            name: prop.preApprovalFileName || 'Pre-Approval Letter.pdf',
            gcsDocumentId: prop.preApprovalGcsDocumentId || null, // null → legacy base64-only record
          });
        }
      } catch {
        // ignore
      }
    };
    fetchPreApproval();
    return () => { cancelled = true; };
  }, [transaction?.id, confirmPropertyId, interestedPropertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load DocuSign room documents — shown as auto-attached in the offer submission section
  // Mirrors mobile OfferSubmissionCard's auto-attachment loading + name normalization
  useEffect(() => {
    const txnStatus = transaction?.status?.toUpperCase();
    const txnSide = (transaction?.side || transaction?.type || '').toUpperCase();
    const isBuyingTxn = txnSide === 'BUYING' || txnSide === 'PURCHASE';
    // DocuSign is COLORADO-ONLY — this auto-attachment fetch hits transactionDocusignDocuments,
    // so skip it for non-CO transactions (Lone Wolf, no DocuSign documents).
    const isCOTxn = (transaction?.propertyState || transaction?.agentLicenseState || '').toUpperCase() === 'CO';
    if (txnStatus !== 'PENDING' || !isBuyingTxn || !transaction?.id || !isCOTxn) return;

    let cancelled = false;
    const fetchAutoAttachments = async () => {
      try {
        const token = await getFirebaseToken();
        const res = await apiClient.get(
          API_ENDPOINTS.transactionDocusignDocuments(transaction.id),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        const raw = res.data;
        const docs = Array.isArray(raw) ? raw
          : Array.isArray(raw?.documents) ? raw.documents
          : Array.isArray(raw?.data) ? raw.data
          : [];
        // Mirror mobile name normalization: strip non-alphanum chars, ensure .pdf suffix
        const names = docs
          .map(d => (d?.name ?? '').toString().trim())
          .filter(Boolean)
          .map(n => {
            const cleaned = n.replace(/[^a-zA-Z0-9._\- ]/g, '');
            return cleaned.toLowerCase().endsWith('.pdf') ? cleaned : `${cleaned}.pdf`;
          });
        if (!cancelled) setOfferAutoAttachments(names.map(name => ({ name })));
      } catch (err) {
        console.warn('[OfferSubmission] Failed to load room documents:', err?.message);
        if (!cancelled) setOfferAutoAttachments([]);
      }
    };
    fetchAutoAttachments();
    return () => { cancelled = true; };
  }, [transaction?.id, transaction?.status, transaction?.side, transaction?.type]); // eslint-disable-line react-hooks/exhaustive-deps

  // Offer Submission handlers
  // Two-step GCS upload (mirrors mobile OfferSubmissionCard): upload PDF to the
  // pre-approval folder, then attach the returned documentId to the property.
  // No client-side size cap — server enforces up to 50MB; mobile has no cap either.
  const uploadPreApprovalFile = async (file) => {
    if (file.type !== 'application/pdf') {
      setOfferSendError('Please upload a PDF file.');
      return;
    }
    setIsUploadingPreApproval(true);
    setOfferSendError(null);
    const propertyId = confirmPropertyId || interestedPropertyId;
    const previousState = offerPreApprovalFile;
    try {
      if (!propertyId) throw new Error('No property selected. Please complete the Confirm Property step first.');
      const token = await getFirebaseToken();

      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('folder', 'pre-approval');
      const uploadRes = await axios.post(
        API_ENDPOINTS.uploadTransactionFile(transaction.id),
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const gcsDocumentId = uploadRes.data?.documentId || uploadRes.data?.id;
      if (!gcsDocumentId) throw new Error('Upload succeeded but no document id was returned.');

      await apiClient.post(
        API_ENDPOINTS.savePreApproval(transaction.id, propertyId),
        { gcsDocumentId, fileName: file.name },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Local-file preview available immediately for this session
      if (preApprovalPreviewUrl) window.URL.revokeObjectURL(preApprovalPreviewUrl);
      setPreApprovalLocalFile(file);
      setPreApprovalPreviewUrl(window.URL.createObjectURL(file));
      setOfferPreApprovalFile({ name: file.name, gcsDocumentId });
    } catch (err) {
      setOfferPreApprovalFile(previousState); // rollback
      setOfferSendError(err?.response?.data?.message || err.message || 'Failed to upload pre-approval file.');
    } finally {
      setIsUploadingPreApproval(false);
    }
  };

  const handlePickPreApproval = (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // reset input so the same file can be re-selected
    if (file) uploadPreApprovalFile(file);
  };

  // Preview — 3-tier fallback: local File from this session, then GCS download,
  // then a "re-upload to preview" message for legacy base64-only records.
  const handlePreviewPreApproval = async () => {
    if (!offerPreApprovalFile) return;
    setShowPreApprovalPreview(true);
    if (preApprovalLocalFile && preApprovalPreviewUrl) {
      setPreApprovalPreviewTier('local');
      return;
    }
    if (offerPreApprovalFile.gcsDocumentId) {
      setIsLoadingPreApprovalPreview(true);
      try {
        const token = await getFirebaseToken();
        const res = await apiClient.get(
          API_ENDPOINTS.downloadTransactionFile(transaction.id, offerPreApprovalFile.gcsDocumentId),
          { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
        );
        const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/pdf' });
        if (preApprovalPreviewUrl) window.URL.revokeObjectURL(preApprovalPreviewUrl);
        setPreApprovalPreviewUrl(window.URL.createObjectURL(blob));
        setPreApprovalPreviewTier('gcs');
      } catch (err) {
        console.error('Pre-approval preview fetch failed:', err);
        setPreApprovalPreviewTier('legacy');
      } finally {
        setIsLoadingPreApprovalPreview(false);
      }
      return;
    }
    setPreApprovalPreviewTier('legacy'); // no gcsDocumentId at all — legacy base64-only record
  };

  const closePreApprovalPreview = () => setShowPreApprovalPreview(false);

  // Real clear — replaces the old client-side-only exclusion; actually detaches the
  // letter server-side (backend auto-deletes the previous GCS doc).
  const handleClearPreApproval = async () => {
    const propertyId = confirmPropertyId || interestedPropertyId;
    if (!propertyId || !transaction?.id) return;
    const previous = offerPreApprovalFile;
    setOfferPreApprovalFile(null); // optimistic clear
    setIsClearingPreApproval(true);
    try {
      const token = await getFirebaseToken();
      await apiClient.post(
        API_ENDPOINTS.savePreApproval(transaction.id, propertyId),
        { gcsDocumentId: '', fileName: '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (preApprovalPreviewUrl) window.URL.revokeObjectURL(preApprovalPreviewUrl);
      setPreApprovalLocalFile(null);
      setPreApprovalPreviewUrl(null);
    } catch (err) {
      setOfferPreApprovalFile(previous); // rollback
      setOfferSendError(err?.response?.data?.message || err.message || 'Failed to remove pre-approval letter.');
    } finally {
      setIsClearingPreApproval(false);
    }
  };

  // Drag-and-drop onto the pre-approval upload row — no idle-state visual change
  const handlePreApprovalDrop = (e) => {
    e.preventDefault();
    setIsPreApprovalDragging(false);
    const file = Array.from(e.dataTransfer.files)[0];
    if (file) uploadPreApprovalFile(file);
  };

  const handleSendOffer = async () => {
    // Comma-separated TO field → multi-recipient list (mirrors mobile OfferSubmissionCard's
    // chip commit). `toEmails` is the backend's preferred multi-recipient field; `toEmail`
    // is a legacy single-recipient fallback the backend ignores once `toEmails` is present.
    const toEmails = parseEmailList(offerToEmail);
    if (toEmails.length === 0) {
      setOfferFlashError(true);
      setTimeout(() => setOfferFlashError(false), 2000);
      return;
    }
    setIsSendingOffer(true);
    setOfferSendError(null);
    try {
      const token = await getFirebaseToken();
      const payload = { emailBody: offerEmailBody, toEmail: toEmails[0], toEmails, emailSubject: offerEmailSubject };
      if (offerExtraAttachments.length > 0) {
        payload.extraAttachments = offerExtraAttachments.map(f => ({ name: f.name, base64: f.base64, mimeType: f.type }));
      }
      if (offerExcludedAutoNames.length > 0) {
        payload.excludedAttachments = offerExcludedAutoNames;
      }
      await apiClient.post(
        API_ENDPOINTS.submitOffer(transaction.id),
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOfferSent(true);
      onProspectStageComplete && onProspectStageComplete('buyer_offer_submission');
    } catch (err) {
      setOfferSendError(err?.response?.data?.message || err.message || 'Failed to send offer. Please try again.');
    } finally {
      setIsSendingOffer(false);
    }
  };

  // "Send Offer Package" click — shows a confirmation popup before actually sending
  // (mirrors mobile OfferSubmissionCard's handleConfirmSend/handleConfirmYes/handleConfirmNo).
  const handleConfirmSendOffer = () => {
    if (isSendingOffer) return;
    setShowConfirmSendOfferModal(true);
  };

  const handleConfirmSendOfferYes = () => {
    setShowConfirmSendOfferModal(false);
    handleSendOffer();
  };

  const handleConfirmSendOfferNo = () => {
    setShowConfirmSendOfferModal(false);
  };

  // Re-send offer package (from buyer_rpa_signed stage — matches mobile "Share Offer" / onSendMail)
  const handleShareOffer = async () => {
    const toEmails = parseEmailList(offerToEmail);
    if (toEmails.length === 0) return;
    setIsShareOfferLoading(true);
    try {
      const token = await getFirebaseToken();
      const payload = { emailBody: offerEmailBody, toEmail: toEmails[0], toEmails, emailSubject: offerEmailSubject };
      if (offerExtraAttachments.length > 0) {
        payload.extraAttachments = offerExtraAttachments.map(f => ({ name: f.name, base64: f.base64, mimeType: f.type }));
      }
      if (offerExcludedAutoNames.length > 0) {
        payload.excludedAttachments = offerExcludedAutoNames;
      }
      await apiClient.post(
        API_ENDPOINTS.submitOffer(transaction.id),
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error('Share Offer failed:', err);
    } finally {
      setIsShareOfferLoading(false);
    }
  };

  const handleOpenOfferEmailEdit = () => {
    setOfferEmailDraft(offerEmailBody);
    setShowOfferEmailModal(true);
  };

  const handleSaveOfferEmailEdit = () => {
    setOfferEmailBody(offerEmailDraft);
    setShowOfferEmailModal(false);
  };

  const handleRemoveAutoAttachment = (name) => {
    setOfferExcludedAutoNames(prev => prev.includes(name) ? prev : [...prev, name]);
  };

  const handleAddExtraAttachment = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    if (file.size > 10 * 1024 * 1024) {
      setOfferSendError('Attachment is too large. Maximum 10 MB per file.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      setOfferExtraAttachments(prev => [...prev, { name: file.name, base64, type: file.type, size: file.size }]);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveExtraAttachment = (index) => {
    setOfferExtraAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatOfferFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Called when the property dropdown changes — checks if a wizard already exists for that property
  const handlePropertyChange = async (propertyId) => {
    setConfirmPropertyId(propertyId);
    setRpaDraft(null);
    setRpaLaunchError(null);
    if (!propertyId || !transaction?.id) return;

    setIsFetchingWizard(true);
    try {
      const token = await getFirebaseToken();
      const draftEndpoint = txnState === 'CO' ? API_ENDPOINTS.cbsWizardDrafts : API_ENDPOINTS.rpaWizardDrafts;
      const res = await apiClient.get(
        draftEndpoint(transaction.id),
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { interestedPropertyId: propertyId },
        }
      );
      const raw = res.data?.data || res.data;
      const draft = Array.isArray(raw) ? raw[0] : raw;
      if (draft?.id || draft?.wizardId) {
        console.log('[CBS/RPA Wizard] Existing wizard found for property:', propertyId, draft);
        setRpaDraft(draft);
      }
    } catch {
      // 404 or empty means no wizard yet — leave rpaDraft null so Launch button shows
    } finally {
      setIsFetchingWizard(false);
    }
  };

  // parseAddressComponents kept as alias for the module-level parseNewAddressComponents
  const parseAddressComponents = parseNewAddressComponents;

  // Normalise an ATTOM response object into the shape the UI/select handler expects
  const normaliseAttomResult = (d) => ({
    mlsId: d.mlsId ? String(d.mlsId) : '',
    address: d.streetAddress || d.fullAddress || d.address || '',
    city: d.city || '',
    state: d.state || '',
    zip: d.postalCode || d.zip || '',
    listPrice: d.listPrice || d.price || null,
    bedrooms: d.bedrooms || d.beds || null,
    bathrooms: d.bathrooms || d.baths || null,
    squareFeet: d.squareFeet || d.sqft || null,
    primaryPhoto: d.primaryPhoto || null,
    sellerName: d.ownerName || null,
    propertyType: d.propertyType || null,
  });

  // ── Milestone date editing (matches mobile TimelineTab) ──────────────────────
  // Extract a YYYY-MM-DD value from a milestone date field. Backend sends
  // UTC ISO strings (e.g. "2026-09-19T18:30:00Z" for Sep-20 IST midnight);
  // .split('T')[0] would return the UTC portion "2026-09-19" and drift
  // off-by-one against the timeline row's formatDate() output, which uses
  // local timezone. Use local Y-M-D so the date input matches what the row
  // above the modal displays.
  const isoToLocalYmd = (raw) => {
    if (!raw) return '';
    // Bare YYYY-MM-DD without time — pass through untouched, no timezone
    // math needed (backend/UI both treat it as a floating date).
    if (typeof raw === 'string' && !raw.includes('T')) return raw.slice(0, 10);
    const d = new Date(raw);
    if (isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleDatePress = (milestone) => {
    const dateStr = milestone.dueDate || milestone.completedDate || milestone.createdAt || '';
    setEditingDate(isoToLocalYmd(dateStr));
    setEditingDateMilestone(milestone);
  };

  const saveMilestoneDate = async () => {
    if (!editingDateMilestone) { setEditingDateMilestone(null); return; }
    const oldDateStr = isoToLocalYmd(editingDateMilestone.dueDate);
    if (editingDate === oldDateStr) { setEditingDateMilestone(null); return; }
    try {
      setIsDateSaving(true);
      const token = await getFirebaseToken();
      await apiClient.patch(
        API_ENDPOINTS.updateMilestone(transaction.id, editingDateMilestone.id),
        { dueDate: editingDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onRefreshMilestones && await onRefreshMilestones();
    } catch (err) {
      alert(err.message || 'Failed to update date');
    } finally {
      setIsDateSaving(false);
      setEditingDateMilestone(null);
    }
  };

  // Smart Search — Google Places autocomplete on each keystroke (debounced 300ms, min 3 chars)
  // Uses Places API (New) at places.googleapis.com — supports CORS in all environments
  const handleSmartSearchInput = (value) => {
    setSmartSearchQuery(value);
    setPlacePredictions([]);
    setSmartSearchResults([]);
    setSmartSearchError('');
    if (smartDebounceRef.current) clearTimeout(smartDebounceRef.current);
    if (!value.trim() || value.trim().length < 3) {
      setPlacesLoading(false);
      return;
    }
    setPlacesLoading(true);
    smartDebounceRef.current = setTimeout(async () => {
      try {
        const suggestions = await placesAutocomplete(value);
        // Map new API suggestions to a shape similar to legacy predictions
        const mapped = suggestions.slice(0, 5).map(s => ({
          place_id: s.placePrediction?.placeId || s.placePrediction?.place?.split('/').pop() || '',
          description: s.placePrediction?.text?.text || '',
          structured_formatting: {
            main_text: s.placePrediction?.structuredFormat?.mainText?.text || s.placePrediction?.text?.text || '',
            secondary_text: s.placePrediction?.structuredFormat?.secondaryText?.text || '',
          },
        }));
        // Restrict to the agent's licensed state (matches mobile app)
        const filtered = userLicenseState
          ? mapped.filter(p => matchesStateInDescription(p.description, userLicenseState))
          : mapped;
        setPlacePredictions(filtered);
      } catch (err) {
        console.error('Places autocomplete error:', err);
        setPlacePredictions([]);
      } finally {
        setPlacesLoading(false);
      }
    }, 300);
  };

  // User selects a Google Places prediction:
  // 1. Fetch place details via Places API (New) → parse address
  // 2. Call ATTOM API with parsed address (mirrors mobile lookupPropertyByAddress)
  const handlePlaceSelect = async (prediction, stageId, isCompleted) => {
    const mainText = prediction.structured_formatting?.main_text || prediction.description;
    setSmartSearchQuery(mainText);
    setPlacePredictions([]);
    setSmartSearchResults([]);
    setSmartSearchError('');
    setAttomLoading(true);

    let parsed = null;
    try {
      // Step 1: Google Places Details (New API) → parse address
      const detailResult = await placesGetDetails(prediction.place_id);
      parsed = parseAddressComponents(detailResult);
    } catch (detailErr) {
      console.error('Place details error:', detailErr);
      setSmartSearchError('Could not load address details. Please try again.');
      setAttomLoading(false);
      return;
    }

    // Defensive re-check: reject out-of-state addresses even if they slipped past
    // the prediction-list filter (e.g. stale suggestions, secondary_text edge cases).
    if (userLicenseState && parsed.stateCode && parsed.stateCode.toUpperCase() !== userLicenseState) {
      setSmartSearchError(`Address must be in ${userLicenseState}. You can only select properties in your licensed state.`);
      setSmartSearchQuery('');
      setAttomLoading(false);
      return;
    }

    const address1 = parsed.street;
    const address2Parts = [parsed.city, parsed.stateCode, parsed.zip].filter(Boolean).join(', ');

    try {
      // Step 2: ATTOM API lookup with parsed address
      // Always use getFirebaseToken() for a fresh token, then sync to localStorage
      // so the apiClient request interceptor doesn't reject with "Session expired"
      const token = await getFirebaseToken();
      localStorage.setItem('firebaseToken', token);
      const params = { address1 };
      if (address2Parts) params.address2 = address2Parts;

      const attomRes = await apiClient.get(API_ENDPOINTS.attomAddressLookup, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      const attomData = attomRes.data;
      let result = null;
      if (Array.isArray(attomData) && attomData.length > 0) {
        result = normaliseAttomResult(attomData[0]);
      } else if (attomData && typeof attomData === 'object' && (attomData.streetAddress || attomData.fullAddress || attomData.mlsId)) {
        result = normaliseAttomResult(attomData);
      }

      if (!result) {
        // ATTOM has no data — fall back to parsed address from Google Places
        result = {
          mlsId: `manual_${Date.now()}`,
          address: parsed.street,
          city: parsed.city,
          state: parsed.stateCode,
          zip: parsed.zip,
          listPrice: null, bedrooms: null, bathrooms: null,
          squareFeet: null, primaryPhoto: null, sellerName: null, propertyType: null,
        };
      }

      setSmartSearchResults([result]);
    } catch (err) {
      console.error('Smart search (ATTOM) failed:', err?.response?.data || err?.message);
      const status = err?.response?.status;
      if (status === 503 || status === 502 || status === 504) {
        setSmartSearchError('Property not added — MLS service is currently unavailable. Please try again later.');
      } else {
        setSmartSearchError('Property not added — could not find property details for this address. Please try a different address.');
      }
      setSmartSearchResults([]);
    } finally {
      setAttomLoading(false);
    }
  };

  // Add a Smart Search result as an interested property and select it
  const handleSmartSearchSelect = async (result, stageId, isCompleted) => {
    if (addingSmartProperty) return;
    setAddingSmartProperty(true);
    try {
      const token = await getFirebaseToken();
      localStorage.setItem('firebaseToken', token);

      // Check if already in the interested properties list
      const normalise = (s) => (s || '').trim().toLowerCase();
      const existing = confirmProperties.find(p =>
        normalise(p.propertyAddress || p.address) === normalise(result.address) ||
        (result.mlsId && (p.mlsId === result.mlsId || String(p.mlsId) === result.mlsId))
      );

      let propertyId;
      if (existing) {
        propertyId = existing.id || existing.interestedPropertyId;
      } else {
        // Add as new interested property
        const payload = {
          propertyAddress: result.address,
          propertyCity: result.city,
          propertyState: result.state,
          propertyZip: result.zip,
        };
        if (result.mlsId) payload.mlsId = result.mlsId;
        if (result.listPrice) payload.listPrice = result.listPrice;
        if (result.bedrooms) payload.bedrooms = result.bedrooms;
        if (result.bathrooms) payload.bathrooms = result.bathrooms;
        if (result.squareFeet) payload.squareFeet = result.squareFeet;
        if (result.primaryPhoto) payload.primaryPhoto = result.primaryPhoto;
        if (result.sellerName) payload.sellerName = result.sellerName;

        const addRes = await apiClient.post(
          API_ENDPOINTS.interestedProperties(transaction.id),
          payload,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        const added = addRes.data;
        propertyId = added.id || added.interestedPropertyId;
        // Update local list
        setConfirmProperties(prev => [...prev, added]);
      }

      // Mark as selected on backend
      try {
        await apiClient.post(
          API_ENDPOINTS.selectInterestedProperty(transaction.id, propertyId),
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch {
        // Non-fatal — selection may not have this endpoint yet
      }

      // Update local selection and trigger wizard lookup
      await handlePropertyChange(propertyId);
      if (!isCompleted) {
        onProspectStageComplete && onProspectStageComplete(stageId);
      }

      // Close smart search
      setSmartSearchOpen(false);
      setSmartSearchQuery('');
      setSmartSearchResults([]);
      setSmartSearchError('');
    } catch (err) {
      console.error('Failed to add smart search property:', err);
      setSmartSearchError('Failed to save property. Please try again.');
    } finally {
      setAddingSmartProperty(false);
    }
  };

  const handleLaunchRPA = async () => {
    if (!hasProperties && !confirmPropertyId && !interestedPropertyId) {
      setRpaNoPropertyError(true);
      return;
    }
    setRpaNoPropertyError(false);
    setRpaLaunchError(null);
    setIsLaunchingRPA(true);
    try {
      const token = await getFirebaseToken();

      // Use the property selected in Confirm Property dropdown first,
      // then fall back to the prop passed from the parent, then fetch.
      let resolvedPropertyId = confirmPropertyId || interestedPropertyId;
      // Always fetch the interested-properties list so we can pass the full selected
      // property object to the wizard for first-launch auto-population (address, price,
      // listing agent, etc.). Also resolves the property ID when none was pre-selected.
      try {
        const propRes = await apiClient.get(
          API_ENDPOINTS.interestedProperties(transaction.id),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const raw = propRes.data;
        const list = Array.isArray(raw) ? raw
          : Array.isArray(raw?.data)    ? raw.data
          : Array.isArray(raw?.content) ? raw.content
          : Array.isArray(raw?.results) ? raw.results
          : raw?.id ? [raw] : [];
        const propId = (p) => p?.id || p?.interestedPropertyId || null;
        const selectedProp =
          (resolvedPropertyId && list.find(p => propId(p) === resolvedPropertyId)) ||
          list.find(p => p.selectedForTransaction || p.selected || p.isSelected || p.selectedAt) ||
          list[0] ||
          null;
        setRpaInterestedProperty(selectedProp);
        if (!resolvedPropertyId) resolvedPropertyId = propId(selectedProp);
      } catch (e) {
        console.warn('[RPA Wizard] Could not fetch properties to resolve ID:', e.message);
      }

      if (!resolvedPropertyId) {
        setRpaLaunchError('No property found for this transaction. Please add a property first.');
        return;
      }

      console.log('[RPA Wizard] Creating draft with interestedPropertyId:', resolvedPropertyId);

      const response = await apiClient.post(
        API_ENDPOINTS.rpaWizardDrafts(transaction.id),
        { interestedPropertyId: resolvedPropertyId, formType: 'PARTIAL_RPA' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const draft = response.data?.data || response.data;
      setRpaDraft(draft);
      setShowRPAWizard(true);
    } catch (err) {
      console.error('[RPA Wizard] Failed to create draft:', err?.response?.data || err?.message);
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message || err?.response?.data?.error || '';

      if (status === 400) {
        // If a draft already exists the API returns a message containing the Draft ID.
        // Extract it and switch to Resume mode instead of showing an error.
        const existingDraftMatch = serverMsg.match(/[Dd]raft\s+[Ii][Dd][:]\s*([0-9a-f-]{36})/i);
        if (existingDraftMatch) {
          const existingId = existingDraftMatch[1];
          console.log('[RPA Wizard] Draft already exists, switching to resume. ID:', existingId);
          setRpaDraft({ id: existingId });
          setRpaLaunchError(null);
        } else {
          setRpaLaunchError('RPA wizard draft already exists for this property.');
        }
      } else if (status === 401) {
        setRpaLaunchError('Invalid or missing authentication token. Please sign in again.');
      } else if (status === 403) {
        setRpaLaunchError('You do not have access to this transaction.');
      } else if (status === 404) {
        setRpaLaunchError('Transaction or property not found.');
      } else {
        setRpaLaunchError(serverMsg || 'Failed to start RPA Wizard. Please try again.');
      }
    } finally {
      setIsLaunchingRPA(false);
    }
  };

  const handleResumeRPA = async () => {
    if (!rpaDraft?.id) return;
    setRpaLaunchError(null);
    setIsResumingRPA(true);
    try {
      const token = await getFirebaseToken();

      // If the wizard was already fully submitted/completed, reopen it so the
      // backend creates a fresh editable draft. The returned draft's id is then
      // used by RPAWizardModal for all subsequent PUT .../steps/{n}?completed=true calls.
      const isCompleted = ['COMPLETED', 'SUBMITTED'].includes(
        (rpaDraft.status || '').toUpperCase()
      );

      let draft;
      if (isCompleted) {
        const propertyId =
          rpaDraft.interestedPropertyId ||
          confirmPropertyId ||
          interestedPropertyId;
        console.log('[RPA Wizard] Reopening completed draft with interestedPropertyId:', propertyId);
        const reopenResp = await apiClient.post(
          API_ENDPOINTS.rpaWizardReopen(transaction.id, propertyId),
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        draft = reopenResp.data?.data || reopenResp.data;
        console.log('[RPA Wizard] Reopen returned new draft:', draft?.id);
      } else {
        // Still in progress — fetch existing draft as before
        const response = await apiClient.get(
          API_ENDPOINTS.rpaWizardDraftById(transaction.id, rpaDraft.id),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        draft = response.data?.data || response.data;
        console.log('[RPA Wizard] Resumed in-progress draft:', draft?.id);

        // Guard: the draft may have been submitted since we last loaded it
        // (e.g. rpaDraft was set from the 400-error fallback path with no status).
        // If so, reopen it so the user doesn't hit "already submitted" on the submit step.
        const fetchedStatus = (draft?.status || '').toUpperCase();
        const fetchedCompleted = Array.isArray(draft?.completedSteps) ? draft.completedSteps.length : 0;
        if (['SUBMITTED', 'COMPLETED'].includes(fetchedStatus) || fetchedCompleted >= 10) {
          const propertyId = draft?.interestedPropertyId || confirmPropertyId || interestedPropertyId;
          console.log('[RPA Wizard] Fetched draft is submitted — reopening. propertyId:', propertyId);
          const reopenResp = await apiClient.post(
            API_ENDPOINTS.rpaWizardReopen(transaction.id, propertyId),
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          draft = reopenResp.data?.data || reopenResp.data;
          console.log('[RPA Wizard] Reopen returned new draft:', draft?.id);
        }
      }

      setRpaDraft(draft);
      setShowRPAWizard(true);
    } catch (err) {
      console.error('[RPA Wizard] Failed to resume draft:', err?.response?.data || err?.message);
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message || err?.response?.data?.error || '';

      if (status === 400) {
        setRpaLaunchError('RPA wizard draft already exists for this property.');
      } else if (status === 401) {
        setRpaLaunchError('Invalid or missing authentication token. Please sign in again.');
      } else if (status === 403) {
        setRpaLaunchError('You do not have access to this transaction.');
      } else if (status === 404) {
        setRpaLaunchError('Transaction or property not found.');
      } else {
        setRpaLaunchError(serverMsg || 'Failed to resume RPA Wizard. Please try again.');
      }
    } finally {
      setIsResumingRPA(false);
    }
  };

  const handleLaunchCBS = async () => {
    if (!hasProperties && !confirmPropertyId && !interestedPropertyId) {
      setRpaNoPropertyError(true);
      return;
    }
    setRpaNoPropertyError(false);
    setRpaLaunchError(null);
    setIsLaunchingRPA(true);
    try {
      const token = await getFirebaseToken();
      let resolvedPropertyId = confirmPropertyId || interestedPropertyId;
      if (!resolvedPropertyId) {
        try {
          const propRes = await apiClient.get(
            API_ENDPOINTS.interestedProperties(transaction.id),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const raw = propRes.data;
          const list = Array.isArray(raw) ? raw
            : Array.isArray(raw?.data)    ? raw.data
            : Array.isArray(raw?.content) ? raw.content
            : Array.isArray(raw?.results) ? raw.results
            : raw?.id ? [raw] : [];
          const selectedProp = list.find(p => p.selectedForTransaction || p.selected || p.isSelected || p.selectedAt);
          const fallbackProp = selectedProp || list[0];
          resolvedPropertyId = fallbackProp?.id || fallbackProp?.interestedPropertyId || null;
        } catch (e) {
          console.warn('[CBS Wizard] Could not fetch properties to resolve ID:', e.message);
        }
      }
      if (!resolvedPropertyId) {
        setRpaLaunchError('No property found for this transaction. Please add a property first.');
        return;
      }
      console.log('[CBS Wizard] Creating draft with interestedPropertyId:', resolvedPropertyId);
      const response = await apiClient.post(
        API_ENDPOINTS.cbsWizardDrafts(transaction.id),
        { interestedPropertyId: resolvedPropertyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const draft = response.data?.data || response.data;
      setRpaDraft(draft);
      setShowRPAWizard(true);
    } catch (err) {
      console.error('[CBS Wizard] Failed to create draft:', err?.response?.data || err?.message);
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message || err?.response?.data?.error || '';
      if (status === 400) {
        const existingDraftMatch = serverMsg.match(/[Dd]raft\s+[Ii][Dd][:]\s*([0-9a-f-]{36})/i);
        if (existingDraftMatch) {
          const existingId = existingDraftMatch[1];
          console.log('[CBS Wizard] Draft already exists, switching to resume. ID:', existingId);
          setRpaDraft({ id: existingId });
          setRpaLaunchError(null);
        } else {
          setRpaLaunchError('CBS wizard draft already exists for this property.');
        }
      } else if (status === 401) {
        setRpaLaunchError('Invalid or missing authentication token. Please sign in again.');
      } else if (status === 403) {
        setRpaLaunchError('You do not have access to this transaction.');
      } else if (status === 404) {
        setRpaLaunchError('Transaction or property not found.');
      } else {
        setRpaLaunchError(serverMsg || 'Failed to start CBS Wizard. Please try again.');
      }
    } finally {
      setIsLaunchingRPA(false);
    }
  };

  const handleResumeCBS = async () => {
    if (!rpaDraft?.id) return;
    setRpaLaunchError(null);
    setIsResumingRPA(true);
    try {
      const token = await getFirebaseToken();
      const isCompleted = ['COMPLETED', 'SUBMITTED'].includes(
        (rpaDraft.status || '').toUpperCase()
      );

      let draft;
      if (isCompleted) {
        const propertyId =
          rpaDraft.interestedPropertyId ||
          confirmPropertyId ||
          interestedPropertyId;
        console.log('[CBS Wizard] Reopening completed draft with interestedPropertyId:', propertyId);
        const reopenResp = await apiClient.post(
          API_ENDPOINTS.cbsWizardReopen(transaction.id, propertyId),
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        draft = reopenResp.data?.data || reopenResp.data;
        console.log('[CBS Wizard] Reopen returned new draft:', draft?.id);
      } else {
        const response = await apiClient.get(
          API_ENDPOINTS.cbsWizardDraftById(transaction.id, rpaDraft.id),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        draft = response.data?.data || response.data;
        console.log('[CBS Wizard] Resumed in-progress draft:', draft?.id);

        // Guard: draft may have been submitted since we last loaded it
        const fetchedStatus = (draft?.status || '').toUpperCase();
        const fetchedCompleted = Array.isArray(draft?.completedSteps) ? draft.completedSteps.length : 0;
        if (['SUBMITTED', 'COMPLETED'].includes(fetchedStatus) || fetchedCompleted >= 11) {
          const propertyId = draft?.interestedPropertyId || confirmPropertyId || interestedPropertyId;
          console.log('[CBS Wizard] Fetched draft is submitted — reopening. propertyId:', propertyId);
          const reopenResp = await apiClient.post(
            API_ENDPOINTS.cbsWizardReopen(transaction.id, propertyId),
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          draft = reopenResp.data?.data || reopenResp.data;
          console.log('[CBS Wizard] Reopen returned new draft:', draft?.id);
        }
      }

      setRpaDraft(draft);
      setShowRPAWizard(true);
    } catch (err) {
      const serverMsg = err?.response?.data?.message || err?.response?.data?.error || '';
      setRpaLaunchError(serverMsg || 'Failed to resume CBS Wizard. Please try again.');
    } finally {
      setIsResumingRPA(false);
    }
  };

const handleLaunchTDS = async () => {
    setTdsLaunchError(null);
    setIsLaunchingTDS(true);
    try {
      const token = await getFirebaseToken();
      const propId = transaction?.propertyId || interestedPropertyId;
      const res = await apiClient.post(
        API_ENDPOINTS.tdsWizardDrafts(transaction.id),
        { interestedPropertyId: propId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const raw = res.data?.data || res.data;
      setTdsDraft(raw);
      setShowTDSWizard(true);
    } catch (err) {
      setTdsLaunchError('Failed to launch TDS Wizard. Please try again.');
    } finally {
      setIsLaunchingTDS(false);
    }
  };

  const handleResumeTDS = async () => {
    if (!tdsDraft?.id && !tdsDraft?.wizardId) return;
    setTdsLaunchError(null);
    setIsResumingTDS(true);
    try {
      const token = await getFirebaseToken();
      const isSubmitted = isWizardSubmittedForReview(tdsDraft.status);
      let draft;
      if (isSubmitted) {
        const propertyId = tdsDraft.interestedPropertyId || transaction?.propertyId || interestedPropertyId;
        const reopenResp = await apiClient.post(
          API_ENDPOINTS.tdsWizardReopen(transaction.id, propertyId),
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        draft = reopenResp.data?.data || reopenResp.data;
      } else {
        const draftId = tdsDraft.id || tdsDraft.wizardId;
        const response = await apiClient.get(
          API_ENDPOINTS.tdsWizardDraftById(transaction.id, draftId),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        draft = response.data?.data || response.data;
      }
      setTdsDraft(draft);
      setShowTDSWizard(true);
    } catch (err) {
      const serverMsg = err?.response?.data?.message || err?.response?.data?.error || '';
      setTdsLaunchError(serverMsg || 'Failed to resume TDS Wizard. Please try again.');
    } finally {
      setIsResumingTDS(false);
    }
  };

  // SPD Wizard handlers — CO only, Seller role only
  const handleLaunchSPD = async () => {
    setSpdLaunchError(null);
    // Reuse draft loaded on mount instead of creating another duplicate
    if (spdDraft?.id || spdDraft?.wizardId) {
      setShowSPDWizard(true);
      return;
    }
    setIsLaunchingSPD(true);
    try {
      const token = await getFirebaseToken();
      const propId = transaction?.propertyId || interestedPropertyId;
      const res = await apiClient.post(
        API_ENDPOINTS.spdWizardDrafts(transaction.id),
        { interestedPropertyId: propId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const raw = res.data?.data || res.data;
      setSpdDraft(raw);
      setShowSPDWizard(true);
    } catch (err) {
      const serverMsg = err?.response?.data?.message || err?.response?.data?.error || '';
      setSpdLaunchError(serverMsg || 'Failed to launch SPD Wizard. Please try again.');
    } finally {
      setIsLaunchingSPD(false);
    }
  };

  const handleResumeSPD = async () => {
    if (!spdDraft?.id && !spdDraft?.wizardId) return;
    setSpdLaunchError(null);
    setIsResumingSPD(true);
    try {
      const token = await getFirebaseToken();
      const draftId = spdDraft.id || spdDraft.wizardId;
      const isSubmitted = isWizardSubmittedForReview(spdDraft.status);
      let draft;
      if (isSubmitted) {
        console.log('[SPD Wizard] Reopening submitted draft:', draftId);
        const reopenResp = await apiClient.post(
          API_ENDPOINTS.spdWizardReopen(transaction.id, draftId),
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        draft = reopenResp.data?.data || reopenResp.data;
        console.log('[SPD Wizard] Reopen returned new draft:', draft?.id);
      } else {
        const response = await apiClient.get(
          API_ENDPOINTS.spdWizardDraftById(transaction.id, draftId),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        draft = response.data?.data || response.data;
        console.log('[SPD Wizard] Resumed in-progress draft:', draft?.id);

        // Guard: draft may have been submitted since we last loaded it
        const fetchedStatus = (draft?.status || '').toUpperCase();
        const fetchedCompleted = Array.isArray(draft?.completedSteps) ? draft.completedSteps.length : 0;
        if (['SUBMITTED', 'COMPLETED'].includes(fetchedStatus) || fetchedCompleted >= 10) {
          console.log('[SPD Wizard] Fetched draft is submitted — reopening:', draftId);
          const reopenResp = await apiClient.post(
            API_ENDPOINTS.spdWizardReopen(transaction.id, draftId),
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          draft = reopenResp.data?.data || reopenResp.data;
          console.log('[SPD Wizard] Reopen returned new draft:', draft?.id);
        }
      }
      setSpdDraft(draft);
      setShowSPDWizard(true);
    } catch (err) {
      const serverMsg = err?.response?.data?.message || err?.response?.data?.error || '';
      setSpdLaunchError(serverMsg || 'Failed to resume SPD Wizard. Please try again.');
    } finally {
      setIsResumingSPD(false);
    }
  };

  const handleReviewSPD = async () => {
    if (!spdDraft) return;
    setSpdLaunchError(null);
    setIsResumingSPD(true);
    try {
      const token = await getFirebaseToken();
      const draftId = spdDraft.id || spdDraft.wizardId;
      const response = await apiClient.get(
        API_ENDPOINTS.spdWizardDraftById(transaction.id, draftId),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const draft = response.data?.data || response.data;
      setSpdDraft(draft);
      setShowSPDWizard(true);
    } catch (err) {
      setSpdLaunchError('Failed to load SPD for review. Please try again.');
    } finally {
      setIsResumingSPD(false);
    }
  };

  const handleCompleteSPDReview = async () => {
    setShowSPDWizard(false);
    setSpdDraft(prev => prev ? { ...prev, lockedByAgent: true, status: 'COMPLETED' } : prev);
    try {
      const token = await getFirebaseToken();
      const draftId = spdDraft?.id || spdDraft?.wizardId;
      if (draftId) {
        const response = await apiClient.get(
          API_ENDPOINTS.spdWizardDraftById(transaction.id, draftId),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const draft = response.data?.data || response.data;
        setSpdDraft(draft);
      }
    } catch (err) {
      console.warn('[SPD] Failed to refresh reviewed draft:', err?.message);
    }
  };

  const handleReviewTDS = async () => {
    if (!tdsDraft) return;
    setTdsLaunchError(null);
    setIsResumingTDS(true);
    try {
      const token = await getFirebaseToken();
      const draftId = tdsDraft.id || tdsDraft.wizardId;
      const response = await apiClient.get(
        API_ENDPOINTS.tdsWizardDraftById(transaction.id, draftId),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const draft = response.data?.data || response.data;
      setTdsDraft(draft);
      setShowTDSWizard(true);
    } catch (err) {
      setTdsLaunchError('Failed to load TDS for review. Please try again.');
    } finally {
      setIsResumingTDS(false);
    }
  };

  const handleCompleteTDSReview = async () => {
    setShowTDSWizard(false);
    setTdsDraft(prev => prev ? { ...prev, lockedByAgent: true, status: 'COMPLETED' } : prev);
    try {
      const token = await getFirebaseToken();
      const draftId = tdsDraft?.id || tdsDraft?.wizardId;
      if (draftId) {
        const response = await apiClient.get(
          API_ENDPOINTS.tdsWizardDraftById(transaction.id, draftId),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const draft = response.data?.data || response.data;
        setTdsDraft(draft);
      }
    } catch (err) {
      console.warn('[TDS] Failed to refresh reviewed draft:', err?.message);
    }
  };

  const handleLaunchSPQ = async () => {
    setSpqLaunchError(null);
    setIsLaunchingSPQ(true);
    try {
      const token = await getFirebaseToken();
      const propId = transaction?.propertyId || interestedPropertyId;
      const res = await apiClient.post(
        API_ENDPOINTS.spqWizardDrafts(transaction.id),
        { interestedPropertyId: propId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const raw = res.data?.data || res.data;
      setSpqDraft(raw);
      setShowSPQWizard(true);
    } catch (err) {
      setSpqLaunchError('Failed to launch SPQ Wizard. Please try again.');
    } finally {
      setIsLaunchingSPQ(false);
    }
  };

  const handleResumeSPQ = async () => {
    if (!spqDraft?.id && !spqDraft?.wizardId) return;
    setSpqLaunchError(null);
    setIsResumingSPQ(true);
    try {
      const token = await getFirebaseToken();
      const isSubmitted = isWizardSubmittedForReview(spqDraft.status);
      let draft;
      if (isSubmitted) {
        const propertyId = spqDraft.interestedPropertyId || transaction?.propertyId || interestedPropertyId;
        const reopenResp = await apiClient.post(
          API_ENDPOINTS.spqWizardReopen(transaction.id, propertyId),
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        draft = reopenResp.data?.data || reopenResp.data;
      } else {
        const draftId = spqDraft.id || spqDraft.wizardId;
        const response = await apiClient.get(
          API_ENDPOINTS.spqWizardDraftById(transaction.id, draftId),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        draft = response.data?.data || response.data;
      }
      setSpqDraft(draft);
      setShowSPQWizard(true);
    } catch (err) {
      const serverMsg = err?.response?.data?.message || err?.response?.data?.error || '';
      setSpqLaunchError(serverMsg || 'Failed to resume SPQ Wizard. Please try again.');
    } finally {
      setIsResumingSPQ(false);
    }
  };

  const handleReviewSPQ = async () => {
    if (!spqDraft) return;
    setSpqLaunchError(null);
    setIsResumingSPQ(true);
    try {
      const token = await getFirebaseToken();
      const draftId = spqDraft.id || spqDraft.wizardId;
      const response = await apiClient.get(
        API_ENDPOINTS.spqWizardDraftById(transaction.id, draftId),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const draft = response.data?.data || response.data;
      setSpqDraft(draft);
      setShowSPQWizard(true);
    } catch (err) {
      setSpqLaunchError('Failed to load SPQ for review. Please try again.');
    } finally {
      setIsResumingSPQ(false);
    }
  };

  const handleCompleteSPQReview = async () => {
    setShowSPQWizard(false);
    setSpqDraft(prev => prev ? { ...prev, lockedByAgent: true, status: 'COMPLETED' } : prev);
    try {
      const token = await getFirebaseToken();
      const draftId = spqDraft?.id || spqDraft?.wizardId;
      if (draftId) {
        const response = await apiClient.get(
          API_ENDPOINTS.spqWizardDraftById(transaction.id, draftId),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const draft = response.data?.data || response.data;
        setSpqDraft(draft);
      }
    } catch (err) {
      console.warn('[SPQ] Failed to refresh reviewed draft:', err?.message);
    }
  };

  // AVID handler
  const handleAvidMarkComplete = async (stageId) => {
    setIsMarkingAvidComplete(true);
    setAvidCompleteError(null);
    try {
      onProspectStageComplete && await onProspectStageComplete(stageId);
    } catch (err) {
      setAvidCompleteError(err?.message || 'Failed to mark complete. Please try again.');
    } finally {
      setIsMarkingAvidComplete(false);
    }
  };

  // NHD handlers (mirrors mobile TimelineTab)

  // Fetch NHD documents from GCS with folder=nhd filter (mirrors mobile fetchNhdDocuments)
  const fetchNhdDocuments = async () => {
    if (!transaction?.id) return;
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.get(API_ENDPOINTS.transactionFiles(transaction.id), {
        headers: { Authorization: `Bearer ${token}` },
        params: { folder: 'nhd' },
      });
      const files = res.data?.data || res.data?.content || res.data || [];
      setNhdDocuments(Array.isArray(files) ? files : []);
    } catch (err) {
      console.error('[NHD] fetchNhdDocuments error:', err);
    }
  };

  // Upload NHD report directly after file selection (mirrors mobile: upload does NOT
  // auto-complete the stage — the agent must separately tap "Mark as" to complete it)
  const handleNhdUpload = async (file) => {
    if (!file || !transaction?.id) return;
    setIsUploadingNhd(true);
    setNhdUploadError(null);
    try {
      const token = await getFirebaseToken();
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('name', file.name || 'NHD Report');
      formData.append('folder', 'nhd');
      await axios.post(
        API_ENDPOINTS.uploadTransactionFile(transaction.id),
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchNhdDocuments();
    } catch (err) {
      setNhdUploadError(
        err?.response?.data?.message || err?.message || 'Upload failed. Please try again.'
      );
    } finally {
      setIsUploadingNhd(false);
    }
  };

  // File selected → upload immediately (mirrors mobile: pick → upload directly)
  const handleNhdFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // allow re-selecting the same file
    handleNhdUpload(file);
  };

  // View an NHD document in a new tab — same "open tab synchronously, then
  // navigate to the fetched blob URL" pattern as CmaPricingCard's handleViewReport,
  // just pointed at the GCS file download endpoint instead of the CMA report endpoint.
  const handleViewNhdDoc = async (doc) => {
    const docId = doc.documentId || doc.id;
    if (!docId || !transaction?.id) return;
    const newTab = window.open('', '_blank');
    if (newTab) {
      newTab.document.write(
        '<!DOCTYPE html><html><head><title>Opening document…</title></head>' +
        '<body style="font-family:sans-serif;color:#2D1B69;display:flex;align-items:center;justify-content:center;height:100vh;margin:0">' +
        '<p>Opening document…</p></body></html>'
      );
      newTab.document.close();
    }
    setViewingNhdDocId(docId);
    try {
      const token = await getFirebaseToken();
      const response = await apiClient.get(
        API_ENDPOINTS.downloadTransactionFile(transaction.id, docId),
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      if (newTab) newTab.location.href = blobUrl;
      else window.open(blobUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('[NHD] handleViewNhdDoc error:', err);
      if (newTab) newTab.close();
      alert('Failed to open document. Please try again.');
    } finally {
      setViewingNhdDocId(null);
    }
  };

  // Delete an NHD document (mirrors mobile handleDeleteNhdDoc)
  const handleDeleteNhdDoc = async (doc) => {
    const docId = doc.documentId || doc.id;
    if (!docId || !transaction?.id) return;
    if (!window.confirm(`Remove "${doc.fileName || doc.name}"?`)) return;
    setIsDeletingNhdDoc(docId);
    setNhdUploadError(null);
    try {
      const token = await getFirebaseToken();
      await apiClient.delete(
        API_ENDPOINTS.deleteTransactionFile(transaction.id, docId),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNhdDocuments(prev => prev.filter(d => (d.documentId || d.id) !== docId));
    } catch (err) {
      console.error('[NHD] deleteNhdDoc error:', err);
      setNhdUploadError(err?.response?.data?.message || 'Failed to remove file.');
    } finally {
      setIsDeletingNhdDoc(null);
    }
  };

  // Purchase Agreement handlers (Active Listing checklist, upload_purchase_agreement
  // stage) — mirrors mobile TimelineTab's handleUploadPurchaseAgreement /
  // fetchPurchaseAgreementDocuments / handleDeletePurchaseAgreementDoc exactly,
  // same request/response shape as the NHD handlers above.

  // Fetch purchase_agreement folder documents (mirrors mobile fetchPurchaseAgreementDocuments)
  const fetchPurchaseAgreementDocuments = async () => {
    if (!transaction?.id) return;
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.get(API_ENDPOINTS.transactionFiles(transaction.id), {
        headers: { Authorization: `Bearer ${token}` },
        params: { folder: 'purchase_agreement' },
      });
      const files = res.data?.data || res.data?.content || res.data || [];
      setPurchaseAgreementDocuments(Array.isArray(files) ? files : []);
    } catch (err) {
      console.error('[PurchaseAgreement] fetchPurchaseAgreementDocuments error:', err);
    }
  };

  // Upload directly after file selection — tagged type=PURCHASE_AGREEMENT/category=PURCHASE
  // so it's tracked under the standard Purchase Agreement bucket, matching mobile.
  const handlePurchaseAgreementUpload = async (file) => {
    if (!file || !transaction?.id) return;
    setIsUploadingPurchaseAgreement(true);
    setPurchaseAgreementUploadError(null);
    try {
      const token = await getFirebaseToken();
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('name', file.name || 'Purchase Agreement');
      formData.append('folder', 'purchase_agreement');
      formData.append('type', 'PURCHASE_AGREEMENT');
      formData.append('category', 'PURCHASE');
      await axios.post(
        API_ENDPOINTS.uploadTransactionFile(transaction.id),
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchPurchaseAgreementDocuments();
    } catch (err) {
      setPurchaseAgreementUploadError(
        err?.response?.data?.message || err?.message || 'Upload failed. Please try again.'
      );
    } finally {
      setIsUploadingPurchaseAgreement(false);
    }
  };

  // File selected → upload immediately (mirrors mobile: pick → upload directly)
  const handlePurchaseAgreementFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // allow re-selecting the same file
    handlePurchaseAgreementUpload(file);
  };

  /**
   * Listing-agreement upload — routes through parse-documents with the
   * LISTING_AGREEMENT label and stashes the parsed facts on the parent
   * (via onListingAgreementParsed). Rides on the next convertToActive
   * so applyListingAgreementOverrides fires at the same commit. Fail-soft:
   * a parse miss surfaces an alert but doesn't block the Upload PA stage.
   */
  const handleListingAgreementFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (isUploadingListingAgreement) return;
    setIsUploadingListingAgreement(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const parsed = await parseTransactionDocuments(
        'LISTING',
        [{ blob: file, name: file.name, label: 'LISTING_AGREEMENT' }],
        { firebaseToken },
      );
      if (parsed?.listingAgreement) {
        setParsedListingAgreement(parsed.listingAgreement);
        if (typeof onListingAgreementParsed === 'function') {
          onListingAgreementParsed(parsed.listingAgreement);
        }
      } else {
        alert('Listing agreement uploaded, but the parser could not extract listing details. You can continue — fields can be edited manually.');
      }
    } catch (err) {
      console.error('[ListingAgreement] upload error:', err);
      alert(`Failed to upload listing agreement: ${err?.message || 'Unknown error'}`);
    } finally {
      setIsUploadingListingAgreement(false);
    }
  };

  // Delete a purchase agreement document (mirrors mobile handleDeletePurchaseAgreementDoc)
  const handleDeletePurchaseAgreementDoc = async (doc) => {
    const docId = doc.documentId || doc.id;
    if (!docId || !transaction?.id) return;
    if (!window.confirm(`Remove "${doc.fileName || doc.name}"?`)) return;
    setIsDeletingPurchaseAgreementDoc(docId);
    setPurchaseAgreementUploadError(null);
    try {
      const token = await getFirebaseToken();
      await apiClient.delete(
        API_ENDPOINTS.deleteTransactionFile(transaction.id, docId),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPurchaseAgreementDocuments(prev => prev.filter(d => (d.documentId || d.id) !== docId));
    } catch (err) {
      console.error('[PurchaseAgreement] deletePurchaseAgreementDoc error:', err);
      setPurchaseAgreementUploadError(err?.response?.data?.message || 'Failed to remove file.');
    } finally {
      setIsDeletingPurchaseAgreementDoc(null);
    }
  };

  // Active role from localStorage — respects currentRole (role switcher) and all role field variants
  const activeRole = getActiveRoleFromStorage();

  // Check if current user is an agent or TC
  const isAgent = ['AGENT', 'TRANSACTION_COORDINATOR', 'BUYER_AGENT', 'LISTING_AGENT'].includes(activeRole);


  // True when the current user is the Seller in this specific transaction.
  // Falls back to activeRole so participant-matching failures don't hide seller UI (mirrors mobile).
  const isSeller = accessControl?.userRole === 'SELLER' || activeRole === 'SELLER';

  // True when the current user's active role is BUYER
  const isBuyer = activeRole === 'BUYER';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (markAsRef.current && !markAsRef.current.contains(e.target)) {
        setOpenMarkAsId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load NHD documents on mount (mirrors mobile useEffect for nhdDocuments)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!transaction?.id) return;
      try {
        const token = await getFirebaseToken();
        const res = await apiClient.get(API_ENDPOINTS.transactionFiles(transaction.id), {
          headers: { Authorization: `Bearer ${token}` },
          params: { folder: 'nhd' },
        });
        const files = res.data?.data || res.data?.content || res.data || [];
        if (!cancelled) setNhdDocuments(Array.isArray(files) ? files : []);
      } catch { /* non-critical, NHD section may not exist for all transactions */ }
    };
    load();
    return () => { cancelled = true; };
  }, [transaction?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load purchase agreement documents on mount (mirrors mobile useEffect for purchaseAgreementDocuments)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!transaction?.id) return;
      try {
        const token = await getFirebaseToken();
        const res = await apiClient.get(API_ENDPOINTS.transactionFiles(transaction.id), {
          headers: { Authorization: `Bearer ${token}` },
          params: { folder: 'purchase_agreement' },
        });
        const files = res.data?.data || res.data?.content || res.data || [];
        const folderDocs = Array.isArray(files) ? files : [];
        // Also surface the agent's saved "Add a Document" selection — forms / existing
        // docs that aren't in the purchase_agreement folder won't appear in folderDocs
        // otherwise, so the stage would look empty right after "Add selected".
        const saved = Array.isArray(transaction?.metadata?.purchaseAgreementSelection)
          ? transaction.metadata.purchaseAgreementSelection : [];
        const seen = new Set(folderDocs.map(d => String(d.documentId ?? d.id)));
        const savedExtra = saved
          .filter(s => s.documentId && !seen.has(String(s.documentId)))
          .map(s => ({ documentId: s.documentId, id: s.documentId, fileName: s.name, name: s.name, _paLabel: s.label }));
        if (!cancelled) setPurchaseAgreementDocuments([...folderDocs, ...savedExtra]);
      } catch { /* non-critical — purchase agreement folder may not exist for all transactions */ }
    };
    load();
    return () => { cancelled = true; };
  }, [transaction?.id, purchaseAgreementRefreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pretty-print a byte size — matches the "237.3 KB" display shown on
  // the Upload Purchase Agreement file rows.
  const formatFileSize = (bytes) => {
    const n = Number(bytes);
    if (!Number.isFinite(n) || n <= 0) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Infer the initial label for a newly-added file from its filename.
  // Agent can override via the per-row dropdown.
  const inferDocLabel = (fileName) => {
    const n = String(fileName || '').toLowerCase();
    if (n.includes('listing') && n.includes('agreement')) return 'LISTING_AGREEMENT';
    if (n.includes('counter')) return 'COUNTER_OFFER';
    if (n.includes('rpa') || n.includes('purchase')) return 'RPA';
    return 'OTHER';
  };
  const getDocLabel = (doc) => {
    const docId = doc.documentId || doc.id;
    return purchaseAgreementDocLabels[docId] || inferDocLabel(doc.fileName || doc.name);
  };
  const setDocLabel = (docId, label) => {
    setPurchaseAgreementDocLabels(prev => ({ ...prev, [docId]: label }));
  };

  // Helper function to format date
  const formatDate = formatPlainDate;

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const [hours, minutes] = timeString.split(':');
      const h = parseInt(hours, 10);
      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      return `${h12}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  // Viewer's own profile timezone, used to render calendar entries that
  // carry a timeZone marker (see formatEventForViewer). Entries with no
  // marker — everything created before this feature existed — keep
  // rendering via the plain formatDate/formatTime above, unchanged.
  //
  // Starts from the generic localStorage-based fallback (getViewerTimezone),
  // then upgrades to the SP profile's State (GET /api/v1/users/me/sp-profile)
  // when available — same source and same "starts generic, upgrades on
  // fetch" pattern already used on JobsPage.js/JobDetailPage.js, so a
  // service-provider viewer sees the exact same timezone here as on their
  // own Jobs pages. Silently keeps the fallback for any viewer without an
  // SP profile (agents/brokers/etc.) — this call already 403s harmlessly
  // for non-SP roles elsewhere in this app, so failure here is expected and
  // non-disruptive, not an error condition.
  const [viewerTimezone, setViewerTimezone] = useState(() => getViewerTimezone());
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getFirebaseToken();
        const resp = await apiClient.get(API_ENDPOINTS.spProfile, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && typeof resp.data?.state === 'string' && resp.data.state.trim()) {
          setViewerTimezone(resolveTimezoneFromState(resp.data.state));
        }
      } catch {
        // Non-SP viewer or transient failure — keep the localStorage-based fallback.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Get transaction type display
  const getTransactionType = () => {
    if (!transaction) return '';
    return transaction.side;
  };

  // Check if transaction is in prospect/PENDING phase
  const isPending = transaction?.status?.toUpperCase() === 'PENDING';

  // Fell-Through (CANCELLED) transactions are read-only — no milestone actions allowed
  const isCancelledTransaction = (transaction?.status || '').toUpperCase() === 'CANCELLED';
  // CLOSED transactions are also read-only on the Timeline tab — no marking milestones/
  // stages/calendar entries, no date edits. Other statuses (PENDING, ACTIVE, etc.) are unaffected.
  const isClosedTransaction = (transaction?.status || '').toUpperCase() === 'CLOSED';
  // Matches mobile canAddCalendar: agent/TC + buyer + seller may add calendar
  // entries; brokers, cancelled, and closed transactions remain read-only.
  const canAddCalendar = (isAgent || isBuyer || isSeller) && !isCancelledTransaction && !isClosedTransaction;

  // SP-uploaded report attachments — size/icon-color helpers mirroring mobile's
  // attachment.service.ts formatFileSize/getFileColor (chat attachments), adapted
  // to a colored-badge-with-extension-label style since this app has no icon font.
  const formatAttachmentSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  const getAttachmentColor = (mimeType, fileName) => {
    const t = (mimeType || '').toLowerCase();
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    if (t.includes('pdf') || ext === 'pdf') return '#E53935';
    if (t.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return '#5B47D6';
    if (t.includes('word') || ['doc', 'docx'].includes(ext)) return '#1976D2';
    if (t.includes('sheet') || t.includes('excel') || ['xls', 'xlsx'].includes(ext)) return '#43A047';
    if (t.includes('presentation') || ['ppt', 'pptx'].includes(ext)) return '#FB8C00';
    return '#6B7280';
  };

  // Per-render 1-to-1 claim set for tier-3 correlation. When multiple same-
  // serviceType SRs exist on the same date without a tier-1 back-link (an
  // extremely common scenario after the Due-By-Time removal — all new SRs
  // have null requestedTime, so strict time-match is a permanent miss), the
  // pickTier3SrForEntry closure below removes the picked SR from the pool
  // as it maps. Without this, a single "closest createdAt" SR would end
  // up mislabelling every ambiguous entry with the same status pill and
  // stealing the "View Details" back-link. Rebuilt each render from an
  // empty state so no stale claims survive a refetch.
  const tier3ClaimedSrIds = new Set();

  const renderCalendarEntryNode = (entry, hasFollowingNode = true, isCurrentItem = true) => {
    // Hoisted above the date/time labels below so a service entry's linked SR
    // (when resolvable) can supply the due date/time instead of the calendar
    // event's own fields — same tier-1/tier-3 correlation used for
    // linkedSrStatus/showAssignSp further down (tier-2/email is skipped here,
    // same as there, since it's a documented no-op — see that comment below).
    const eventServiceType = (entry.entryType || '').toLowerCase();
    const isServiceEvent = entry.category === 'service' || !!entry.providerType;
    // Tier-3 disambiguation when a transaction has multiple SRs of the same
    // service type. Compares on LOCAL date/time — sr.requestedDate/Time
    // are UTC ISO strings, entry.scheduledDate/Time are local strings from
    // the calendar event, so parse the ISOs and re-format to local HH:mm
    // before comparing (raw ISO-slice was UTC and never matched local).
    // Returns null when there are multiple candidates but no schedule
    // match — a wrong pick is worse than no pick (row will just show
    // whatever state was already known, or nothing until refresh).
    const entryDate = entry.scheduledDate ? String(entry.scheduledDate).slice(0, 10) : null;
    const entryTime = entry.scheduledTime || null;
    const pickTier3SrForEntry = () => {
      // Exclude terminal SRs from tier-3. A calendar event without a
      // tier-1 back-link is almost always a freshly-created row whose
      // back-link PUT hasn't landed yet (or an event that never got one
      // wired) — matching it to a CANCELLED/DECLINED/EXPIRED/COMPLETED
      // SR mislabels the row. Fingerprint fixed 2026-09-08: cancel SR1,
      // create SR2 of the same serviceType — the fresh row briefly showed
      // SR1's "Cancelled" pill during the ~1s window before SR2's
      // back-link and optimistic-insert both landed.
      const all = (eventServiceType && srsByServiceType[eventServiceType]) || [];
      // Filter out claimed-by-earlier-rows SRs so each same-service-type,
      // same-date SR can only pair with ONE calendar event (see the top-of-
      // component tier3ClaimedSrIds comment). Without this, the closest-
      // createdAt disambiguation below returns the SAME SR for every
      // ambiguous entry, mislabelling all rows past the first.
      const candidates = all.filter(sr => (
        sr.status !== 'CANCELLED'
        && sr.status !== 'DECLINED'
        && sr.status !== 'EXPIRED'
        && sr.status !== 'COMPLETED'
        && !tier3ClaimedSrIds.has(sr.id)
      ));
      if (candidates.length === 0) return null;
      if (candidates.length === 1) return candidates[0];
      const toLocalDate = (s) => {
        if (!s) return '';
        const d = new Date(s);
        if (isNaN(d.getTime())) return '';
        const y = d.getFullYear();
        const mo = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${mo}-${day}`;
      };
      const toLocalTime = (s) => {
        if (!s) return '';
        const d = new Date(s);
        if (isNaN(d.getTime())) return '';
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      };
      if (entryDate && entryTime) {
        const exact = candidates.find(sr =>
          toLocalDate(sr.requestedDate) === entryDate
          && toLocalTime(sr.requestedTime) === entryTime);
        if (exact) return exact;
        // 2026-09-11: SR writes stopped sending requestedTime (Due-By-Time
        // input was removed), so most new SRs have requestedTime=null and
        // the strict time compare above always misses on them. Fall back
        // to a date-only match against SRs with null requestedTime; when
        // multiple such candidates hit the same date, disambiguate by the
        // SR createdAt closest to the calendar event createdAt (both are
        // written back-to-back in the same handler, so their timestamps
        // are the tightest pairing signal available).
        const nullTimeDateHits = candidates.filter(sr =>
          !sr.requestedTime && toLocalDate(sr.requestedDate) === entryDate);
        if (nullTimeDateHits.length === 1) return nullTimeDateHits[0];
        if (nullTimeDateHits.length > 1 && entry.createdAt) {
          const entryCreatedMs = new Date(entry.createdAt).getTime();
          if (!Number.isNaN(entryCreatedMs)) {
            let best = null;
            let bestDelta = Infinity;
            for (const sr of nullTimeDateHits) {
              if (!sr.createdAt) continue;
              const delta = Math.abs(new Date(sr.createdAt).getTime() - entryCreatedMs);
              if (delta < bestDelta) { best = sr; bestDelta = delta; }
            }
            if (best) return best;
          }
        }
        // Event has a specific time and no SR matches it — legacy-strict
        // case where the SR at a different time on this date is NOT the
        // right SR. Return null and let the row show as unlinked. The
        // old date-only fallback below was the bug that made 3 events
        // on the same day all resolve to the ONE SR that happened to
        // be on that date — invoice / status / attachment contamination
        // all rolled up from here. Do NOT reintroduce the date-only
        // match for SRs that DO carry a real requestedTime.
        return null;
      }
      if (entryDate) {
        const dateHits = candidates.filter(sr => toLocalDate(sr.requestedDate) === entryDate);
        if (dateHits.length === 1) return dateHits[0];
      }
      // Multiple candidates and no unambiguous schedule match: return null
      // rather than confidently pick the newest and mislabel the row.
      return null;
    };
    const tier3Sr = pickTier3SrForEntry();
    // Register the tier-3 pick as claimed so subsequent same-day, same-
    // serviceType entries can't inherit the same SR (see comment on
    // tier3ClaimedSrIds at the top of the component).
    if (tier3Sr?.id) tier3ClaimedSrIds.add(tier3Sr.id);
    // Tier-1 (entry.serviceRequestId) is authoritative — when a back-link
    // exists, trust it EXCLUSIVELY. When no back-link exists, use only the
    // strict schedule-aware tier-3 pick (or providerEmail tier-2 when the
    // calendar event carries an invitee) — DO NOT fall through to
    // srByServiceType[type] which returns the newest SR for the type and
    // therefore returns the SAME SR for every ambiguous row of that type.
    // That fallback was the root cause of two same-serviceType SR rows on
    // one transaction (both scheduled the same time) showing the same
    // "Accepted" pill the moment ONE of them got accepted — the other row
    // pulled the newest-SR fallback and inherited its status. Fixed
    // 2026-09-03; mobile TimelineTab has always followed the strict rule.
    const hasBackLink = !!entry.serviceRequestId;
    // Web parity with mobile TimelineTab (2026-09-15): 3-tier resolution.
    // Tier-1 = event.serviceRequestId back-link. Tier-2 = SR indexed by
    // provider email — matches rows where the SR was linked via its
    // provider before the back-link was stamped. Tier-3 = schedule-based
    // fallback. Web previously skipped tier-2 and stopped after tier-1,
    // which is why View Details hid on rows mobile had already resolved.
    //
    // emailKey extraction is defensive across the shapes a timeline entry
    // can carry the provider identifier in: some rows have
    // `entry.providerEmail` (mirror of mobile's `event.providerEmail`);
    // some rows carry it under the linked calendar event; others only
    // expose it via the invitees array. Take the first non-empty match.
    const rawEmail =
      entry.providerEmail
      || entry.provider?.email
      || entry._calendarEvent?.providerEmail
      || entry._calendarEvent?.provider?.email
      || entry.invitees?.[0]?.email
      || '';
    const emailKey = rawEmail.toLowerCase();
    // Composite `${email}::${serviceType}` — see srByProviderEmail comment in
    // TransactionDetailPage.js. A single Company provider can be routed
    // multiple different serviceTypes on one transaction (Appraisal +
    // Mortgage Broker both to Hvantage); email-only lookup returns the
    // wrong-type SR and mislabels the row (and opens the wrong SR in
    // View Details modal).
    const emailTypeKey = emailKey && eventServiceType
      ? `${emailKey}::${eventServiceType}`
      : '';
    const linkedSr = hasBackLink
      ? (srById[entry.serviceRequestId] || null)
      : (
          (emailTypeKey && srByProviderEmail[emailTypeKey])
          || tier3Sr
          || null
        );
    // Tier-1 back-linked SRs also register in the claim set — no other
    // entry's tier-3 fallback should be allowed to steal a SR that already
    // owns a real back-link.
    if (hasBackLink && entry.serviceRequestId) tier3ClaimedSrIds.add(entry.serviceRequestId);
    const linkedSrStatus = hasBackLink
      ? srStatusById[entry.serviceRequestId]
      : (
          (emailTypeKey && srStatusByProviderEmail[emailTypeKey])
          || linkedSr?.status
          || undefined
        );
    // Requester's Assign/Reassign gate — STRICT per spec (re-confirmed
    // 2026-08-31 EOD, superseding earlier loose iterations):
    //   1. No SR yet (`undefined`)                             → "Assign SP" (first create)
    //   2. Open SR placeholder (AWAITING_ASSIGNMENT, no providerId,
    //      no routedByCompanyId) — requester created the SR without picking
    //      a provider up-front and is finishing the assignment now.
    //      Introduced with the open-SR create flow 2026-09-08.
    //   3. Company explicitly declined the SR entirely (Row 7):
    //      status === 'DECLINED' && linkedSr.routedByCompanyId → "Reassign SP"
    // Everything else HIDES the button:
    //   · AWAITING_ASSIGNMENT (with Company — Company's turn)
    //   · PENDING             (with Inspector — Inspector's turn)
    //   · Row-5 DECLINED      (individual-SP direct decline — spec says
    //                          Company-decline is the only unlock trigger)
    //   · CANCELLED / EXPIRED (spec silent → strict-hide)
    //   · ACCEPTED / IN_PROGRESS / COMPLETED (SP owns; also hidden by
    //                                         isSrAcceptedOrBeyond)
    const isSrAcceptedOrBeyond = isServiceEvent && (
      linkedSrStatus === 'ACCEPTED'
      || linkedSrStatus === 'IN_PROGRESS'
      || linkedSrStatus === 'COMPLETED'
    );
    const isCompanyDeclined = linkedSrStatus === 'DECLINED'
      && !!linkedSr?.routedByCompanyId;
    const isOpenSrPlaceholder = linkedSrStatus === 'AWAITING_ASSIGNMENT'
      && !linkedSr?.providerId
      && !linkedSr?.routedByCompanyId;

    // Service entries: prefer the linked SR's requestedDate/requestedTime —
    // always a real UTC instant, so always convertible to the viewer's
    // timezone — over the calendar event's own scheduledDate/scheduledTime,
    // which can lack a timeZone marker (naive/unconverted) or have drifted
    // from the SR (the back-link/sync between the two records is documented
    // below as unreliable). Falls back to the calendar event's own fields —
    // exactly today's behavior — for custom entries and for service entries
    // where no SR could be resolved, so nothing regresses.
    // Timeline pill shows date only — the "Due By Time" input was removed
    // form-side, so appending "11:59 PM PDT" here would just be clutter.
    const { dateLabel: entryDateLabel } =
      isServiceEvent && linkedSr?.requestedDate
        ? { dateLabel: formatInstantForViewer(linkedSr.requestedDate, viewerTimezone).dateLabel }
        : formatEventForViewer(entry, viewerTimezone);
    const subtitle = [entry.entryType, entryDateLabel]
      .filter(Boolean)
      .join(' · ');
    const isEventCompleted = entry.status === 'COMPLETED';
    // Set by the cross-icon delete flow when the linked SR had already been
    // sent to a provider (see TransactionDetailPage.handleDeleteCalendarEntry)
    // — the entry stays in the timeline instead of disappearing, as an
    // audit trail, but with no further actions available.
    const isEventCancelled = entry.status === 'CANCELLED';

    const showAssignSp = isServiceEvent && (
      linkedSrStatus === undefined
      || isOpenSrPlaceholder
      || isCompanyDeclined
    );
    // Open-SR placeholder reads "Assign SP" (never had a provider); only the
    // company-declined case is a genuine RE-assign.
    const assignSpLabel = (linkedSrStatus === undefined || isOpenSrPlaceholder)
      ? 'Assign SP'
      : 'Reassign SP';

    // Report attachments the SP uploaded — pulled from the SAME strict
    // linkedSr the status pill uses (tier-1 back-link, else schedule-aware
    // tier-3, else null). The old code fell through to
    // srByServiceType[type]?.reportAttachments as a last resort, which
    // returned the newest SR-of-that-type's attachments for EVERY
    // ambiguous row — so an invoice uploaded to one Mortgage Broker SR
    // showed up on every other Mortgage Broker row on the same
    // transaction. Fixed 2026-09-03; matches the status-pill fix in the
    // linkedSr computation above.
    const rawAttachments = entry.reportAttachments
      || (entry.serviceRequestId && srAttachmentsById[entry.serviceRequestId])
      || linkedSr?.reportAttachments
      || [];
    // Only surface provider-uploaded files here (mirrors mobile's spAttachments
    // filter) — agent-uploaded files would be confusing in this read-only spot.
    // Defensive: keep items where uploadedByRole is missing rather than hide
    // them, since not every response shape is guaranteed to include it.
    const attachments = rawAttachments.filter(a => !a.uploadedByRole || a.uploadedByRole === 'PROVIDER');

    return (
      <div key={`calendar-${entry.id}`} className="txn-timeline-item">
        <div className="txn-timeline-icon completed" aria-label="Calendar event">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </div>
        {hasFollowingNode && <div className="txn-timeline-line txn-timeline-line-completed"></div>}
        <div className="txn-timeline-content-box">
          <div className="txn-timeline-header">
            <div className="txn-timeline-header-left">
              <div className="txn-timeline-title">{entry.title || entry.entryType}</div>
              {subtitle && <div className="txn-timeline-subtitle">{subtitle}</div>}
              {/* Per-row loading indicator — /sent-service-requests takes
                  20-30s on this endpoint (per-row PII decryption), so the
                  pill would otherwise flicker between "no status" and the
                  real state as the correlation list lands. Mirrors mobile's
                  correlationPending pattern. */}
              {isServiceEvent && !sentSrsLoaded && (
                <span className="txn-sr-status-pill txn-sr-status-pill-loading">
                  <span className="txn-sr-status-pill-spinner" aria-hidden="true" />
                  Syncing…
                </span>
              )}
              {isServiceEvent && sentSrsLoaded && linkedSrStatus && (() => {
                // Extra modifier class on the pill so the "Provider Not
                // Assigned" state (open SR, requester needs to act) can be
                // styled distinctly from "Awaiting Company Assignment" (they
                // wait on the company) — both share the AWAITING_ASSIGNMENT
                // backend status, only the discriminator differs.
                const displayLabel = resolveSrStatusLabel(linkedSrStatus, linkedSr);
                const modifierClass = isOpenSrPlaceholder
                  ? ' txn-sr-status-pill-open'
                  : '';
                return (
                  <span
                    className={`txn-sr-status-pill txn-sr-status-pill-${linkedSrStatus.toLowerCase()}${modifierClass}`}
                    title={`SP status: ${displayLabel}`}
                  >
                    {displayLabel}
                  </span>
                );
              })()}
            </div>
            <div className={`txn-timeline-date${canAddCalendar && onDeleteCalendarEntry ? ' txn-timeline-date-has-delete' : ''}`}>
              {entry.scheduledDate && (
                !isEventCompleted && !isEventCancelled && canAddCalendar && entry._calendarEvent && onEditCalendarEventDate ? (
                  /* Same date+pencil control as active-milestone dates */
                  <button
                    className="txn-date-edit-btn"
                    onClick={() => {
                      // For service events, prefer the linked SR's
                      // requestedDate — that's what the row above displays
                      // via formatInstantForViewer(linkedSr.requestedDate,
                      // viewerTimezone). Using entry.scheduledDate.slice(0,
                      // 10) here would return the UTC date portion of an
                      // ISO like "2026-09-19T18:30:00Z" (Sep-20 IST midnight)
                      // and drift off-by-one against the row.
                      let seedDate = '';
                      let seedTime = entry.scheduledTime || '';
                      if (isServiceEvent && linkedSr?.requestedDate) {
                        const { localDate, localTime } =
                          instantToWallClockInZone(linkedSr.requestedDate, viewerTimezone);
                        if (localDate) seedDate = localDate;
                        if (seedTime && localTime) seedTime = localTime;
                      } else {
                        const rawDate = String(entry.scheduledDate || '').slice(0, 10);
                        const rawTime = entry.scheduledTime || '';
                        seedDate = rawDate;
                        if (entry.timeZone === 'UTC' && rawDate) {
                          const iso = `${rawDate}T${rawTime || '00:00'}:00Z`;
                          const { localDate, localTime } = instantToWallClockInZone(iso, viewerTimezone);
                          if (localDate) seedDate = localDate;
                          if (rawTime && localTime) seedTime = localTime;
                        } else if (String(entry.scheduledDate || '').includes('T')) {
                          // ISO string with no timeZone='UTC' marker — parse
                          // as an instant and read local Y-M-D so the seed
                          // matches formatEventForViewer's display.
                          const parsed = new Date(entry.scheduledDate);
                          if (!isNaN(parsed.getTime())) {
                            const yyyy = parsed.getFullYear();
                            const mmm = String(parsed.getMonth() + 1).padStart(2, '0');
                            const ddd = String(parsed.getDate()).padStart(2, '0');
                            seedDate = `${yyyy}-${mmm}-${ddd}`;
                          }
                        }
                      }
                      setEditingDateEntry({
                        kind: 'event',
                        id: entry.id,
                        name: entry.title || entry.entryType || 'Calendar event',
                        date: seedDate,
                        time: seedTime,
                      });
                    }}
                  >
                    <span>{entryDateLabel}</span>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                ) : (
                  <span>{entryDateLabel}</span>
                )
              )}
              {isEventCompleted && (
                <span className="txn-calendar-entry-completed">✓ Completed</span>
              )}
              {isEventCancelled && (
                <span className="txn-calendar-entry-cancelled">✕ Cancelled</span>
              )}
              {/* Hide the × on service events once the SP has accepted —
                  the SP owns the row from that point on, and a
                  unilateral requester-side cancel would be disruptive.
                  Pre-acceptance (undefined / AWAITING_ASSIGNMENT /
                  PENDING) and terminal-fail (DECLINED / CANCELLED /
                  EXPIRED) states keep the × so the requester can still
                  clean up entries the SP hasn't committed to. Custom
                  entries (non-service) keep the × regardless. */}
              {canAddCalendar && onDeleteCalendarEntry && !isEventCancelled
                && !(isServiceEvent && isSrAcceptedOrBeyond) && (
                <button
                  className="txn-calendar-entry-delete"
                  onClick={() => onDeleteCalendarEntry(entry.id, entry._calendarEvent)}
                  title="Delete entry"
                  aria-label={`Delete ${entry.title || 'calendar entry'}`}
                >×</button>
              )}
            </div>
          </div>
          {/* Stage-style action row. Mark as hides post-accept; View
              Details stays visible for the full SR lifecycle so requesters
              can inspect schedule / amount / notes at any point. The
              Assign / Reassign pair moved into the details modal per
              product ask — clicking View Details reveals them there. */}
          {!isEventCompleted && !isEventCancelled && canAddCalendar && entry._calendarEvent && (
            <div className="txn-timeline-actions">
              {/* Right-aligned, matching the real-milestone action row (txn-milestone-right-actions
                  gets margin-left: auto) instead of sitting flush left under the title. */}
              <div className="txn-milestone-right-actions">
                {!isSrAcceptedOrBeyond && onCompleteCalendarEvent && (
                  <button
                    className="txn-timeline-button txn-primary-btn"
                    onClick={() => onCompleteCalendarEvent(entry.id)}
                    title="Mark as complete"
                  >
                    Mark as
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 4 }}><polyline points="20 6 9 17 4 12"/></svg>
                  </button>
                )}
                {isServiceEvent && linkedSr && (
                  <button
                    className="txn-timeline-button txn-view-details-btn"
                    onClick={() => openSrDetails(linkedSr, entry)}
                    title="View service request details"
                    type="button"
                  >
                    View Details
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4 }}>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                )}
                {/* Checklist feature — available for any service type once the SP has
                    actually uploaded a report to parse (eligibility is validated
                    dynamically server-side against the live service-type catalog, not
                    a hardcoded list here). Targets the first provider-uploaded
                    attachment (one report per job is the expected case). */}
                {isServiceEvent && linkedSr && attachments.length > 0 && (() => {
                    // Only READY means a checklist was successfully generated —
                    // PENDING (stuck mid-generation) and FAILED must still show
                    // "Generate Checklist" so the requester can (re)try, rather
                    // than routing into view mode where there's nothing to see.
                    const isGenerated = attachments[0].checklistStatus === 'READY';
                    return (
                      <button
                        className="txn-timeline-button"
                        onClick={() => setChecklistTarget({
                          srId: linkedSr.id,
                          attachmentId: attachments[0].id,
                          mode: isGenerated ? 'view' : 'generate',
                          reportFileUrl: attachments[0].fileUrl,
                          reportFileName: attachments[0].fileName,
                          sr: linkedSr,
                        })}
                        title={isGenerated ? 'View generated checklist' : 'Generate a findings checklist from this report'}
                        type="button"
                      >
                        {isGenerated ? 'View Generated' : 'Generate Checklist'}
                      </button>
                    );
                  })()}
                {/* Notes — private to the viewer, scoped to THIS specific timeline
                    entry (entry.id), not the whole transaction, so two tasks under
                    the same transaction never share one note list. Same underlying
                    record shown in My Notes — not a copy. The trigger (icon+badge)
                    stays inline here with the other action buttons; the "▶ N notes"
                    toggle + stack portals to the full-width placeholder below,
                    matching the reference wireframe's Timeline layout exactly
                    (rather than squeezing the expanded list into this button row). */}
                <NotesPanel
                  linkType="TRANSACTION"
                  linkId={entry.id}
                  linkLabel={entry.title || entry.entryType || 'Timeline task'}
                  transactionId={transaction?.id}
                  transactionName={transaction?.transactionName || transaction?.propertyAddress}
                  portalTargetId={`txn-notes-target-${entry.id}`}
                />
              </div>
            </div>
          )}
          {/* Portal target for this entry's Notes toggle+stack — see the
              NotesPanel usage above. Empty by default; NotesPanel fills it via
              a React portal only when this entry has notes or the composer is
              open, so it takes no space otherwise. */}
          <div id={`txn-notes-target-${entry.id}`} className="txn-notes-block" />
          {/* SP-uploaded report attachments — spec §4 collapsible "Documents (N)".
              Collapsed by default. Expanded rows show a Download button plus a
              "Mark as Transaction Document" button (or a green Filed pill when
              already filed) so the Requester can promote a shared file into
              the transaction's Docs tab under the auto-created Service Request
              folder (spec §5). */}
          {entry._calendarEvent && attachments.length > 0 && (() => {
            const isExpanded = expandedSrDocs.has(entry.id);
            // Same strict rule as attachments above — never let the
            // "newest SR of this serviceType" fallback pick the id
            // (Mark-as-Transaction-Document would file the wrong SR's
            // attachment). Use tier-1 back-link or the strict linkedSr
            // from the schedule-aware tier-3 pick.
            const linkedSrIdForFiling = entry.serviceRequestId
              || linkedSr?.id
              || null;
            return (
              <div className="txn-report-attachments">
                <button
                  type="button"
                  className="txn-sr-docs-toggle"
                  onClick={() => toggleSrDocsExpanded(entry.id)}
                  aria-expanded={isExpanded}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                  <span className="txn-sr-docs-toggle-text">Documents</span>
                  <span className="txn-sr-docs-count">{attachments.length}</span>
                  <span className="txn-sr-docs-toggle-spacer" />
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {isExpanded
                      ? <polyline points="18 15 12 9 6 15" />
                      : <polyline points="6 9 12 15 18 9" />}
                  </svg>
                </button>
                {isExpanded && attachments.map((att, i) => {
                  const fileName = att.fileName || att.name || 'File';
                  const sizeLabel = formatAttachmentSize(att.fileSize ?? att.size);
                  const ext = (fileName.split('.').pop() || 'FILE').toUpperCase().slice(0, 4);
                  const fileUrl = att.fileUrl || att.url || att.downloadUrl;
                  const filed = !!att.filedAsTransactionDocumentId || justFiledAttachments.has(att.id);
                  const filing = filingAttachments.has(att.id);
                  return (
                    <div key={att.id || `${entry.id}-att-${i}`} className="txn-report-attachment-row">
                      <div
                        className="txn-report-attachment-icon"
                        style={{ background: getAttachmentColor(att.mimeType || att.contentType, fileName) }}
                      >
                        {ext}
                      </div>
                      <div className="txn-report-attachment-info">
                        <div className="txn-report-attachment-name" title={fileName}>{fileName}</div>
                        {sizeLabel && <div className="txn-report-attachment-size">{sizeLabel}</div>}
                      </div>
                      {filed ? (
                        <span className="txn-sr-filed-pill">✓ Filed</span>
                      ) : (
                        <button
                          type="button"
                          className="txn-sr-mark-btn"
                          onClick={() => linkedSrIdForFiling && handleMarkAsTransactionDocument(linkedSrIdForFiling, att)}
                          disabled={filing || !linkedSrIdForFiling}
                          title="Copy this file into the transaction Documents tab (Service Request folder)"
                        >
                          {filing ? 'Filing…' : 'Mark as Transaction Document'}
                        </button>
                      )}
                      <button
                        type="button"
                        className="txn-report-attachment-download"
                        title={fileUrl ? 'Download' : 'File unavailable — try refreshing the page'}
                        aria-label={fileUrl ? `Download ${fileName}` : `${fileName} — file unavailable, try refreshing the page`}
                        disabled={!fileUrl}
                        onClick={() => fileUrl && window.open(fileUrl, '_blank', 'noopener,noreferrer')}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  // Determine which stage definitions to use — fetched from API via cache (mirrors mobile getCachedStages)
  const transactionSide = transaction?.side?.toUpperCase() || transaction?.type?.toUpperCase() || '';
  const isBuying = transactionSide === 'BUYING' || transactionSide === 'PURCHASE';
  const cacheSide = isBuying ? 'BUYING' : 'LISTING';

  // Map active role → roleAccess key used in milestone metadata (mirrors mobile getRoleAccessKey)
  // 'AGENT'/'TC'/'BUYER_AGENT'/'LISTING_AGENT' all map to the side-appropriate agent key
  const milestoneRoleKey = (() => {
    if (activeRole === 'BUYER') return 'BUYER';
    if (activeRole === 'SELLER') return 'SELLER';
    if (activeRole === 'AGENT' || activeRole === 'TRANSACTION_COORDINATOR'
        || activeRole === 'BUYER_AGENT' || activeRole === 'LISTING_AGENT') {
      return isBuying ? 'BUYER_AGENT' : 'LISTING_AGENT';
    }
    return null; // BROKER etc — always read-only
  })();
  const stageDefinitions = getCachedStages(cacheSide, txnState);

  // Get prospect stages from transaction metadata
  const prospectStages = transaction?.metadata?.prospectTimeline?.stages || [];

  // Merge stage definitions with actual stage data from metadata
  const mergedProspectStages = stageDefinitions.map((def, index) => {
    const stageData = prospectStages.find(s => s.stageId === def.id);
    // Lead Created (index 0) falls back to transaction.createdAt when completedAt is missing
    const fallbackCompletedAt = index === 0 ? (transaction?.createdAt || null) : null;
    return {
      ...def,
      status: stageData?.status || (index === 0 ? 'COMPLETED' : 'NOT_STARTED'),
      completedAt: stageData?.completedAt || fallbackCompletedAt,
      // §2.3 persisted suggested date (stamped at creation).
      suggestedDate: stageData?.suggestedDate,
    };
  }).sort((a, b) => {
    // Sort-only anchor: deliberately EXCLUDES completedAt so marking a stage
    // complete doesn't reorder the timeline — mirrors mobile TimelineTab.tsx's
    // stageSortDate exactly ("skips completedAt so marking a stage complete
    // doesn't reorder the timeline"). Only an agent-edited suggestedDate (or the
    // createdAt+offset fallback) repositions a stage; completing it in place
    // does not.
    const sortAnchorMs = (s) => {
      if (s.suggestedDate) return new Date(s.suggestedDate).getTime();
      if (transaction?.createdAt && s.suggestedOffsetDays != null) {
        const d = new Date(transaction.createdAt);
        d.setDate(d.getDate() + s.suggestedOffsetDays);
        return d.getTime();
      }
      return null;
    };
    const ma = sortAnchorMs(a);
    const mb = sortAnchorMs(b);
    if (ma != null && mb != null) return ma - mb;
    if (ma != null) return -1;
    if (mb != null) return 1;
    return 0;
  });

  // Calendar entries are first-class timeline nodes. Their legacy milestoneId is
  // intentionally ignored for display; placement is determined solely by date.
  // Calendar-service events for this transaction, normalized to the timeline-node
  // shape (eventId -> id, eventType -> entryType) and tagged so delete routes to
  // the calendar service.
  const normalizedCalendarEvents = (calendarEvents || []).map(ev => {
    // Calendar events carry the SP under `attendees` with role !== 'ORGANIZER'
    // (the organizer is the requesting agent). Mobile's TimelineTab derives
    // `providerEmail` the same way — web previously left this off, which
    // blocked the tier-2 email-based SR lookup below and hid View Details
    // on any row whose SR was linked-by-provider before the back-link was
    // stamped.
    const invitee = Array.isArray(ev.attendees)
      ? ev.attendees.find(a => a && a.role !== 'ORGANIZER')
      : null;
    return {
      id: ev.eventId,
      title: ev.title,
      entryType: ev.eventType,
      category: ev.category,
      scheduledDate: ev.scheduledDate,
      scheduledTime: ev.scheduledTime,
      timeZone: ev.timeZone,
      duration: ev.duration,
      status: ev.status,
      providerType: ev.providerType,
      serviceType: ev.eventType,
      providerName: invitee?.name,
      providerEmail: invitee?.email,
      invitees: Array.isArray(ev.attendees)
        ? ev.attendees.filter(a => a && a.role !== 'ORGANIZER')
        : (ev.invitees || []),
      serviceRequestId: ev.serviceRequestId,
      reportAttachments: ev.reportAttachments || ev.attachments || null,
      _calendarEvent: true,
    };
  });
  // Merge the transaction's service entries (calendar-entries subcollection) with
  // its calendar-service events (Custom entries + Calendar-page events). Legacy
  // 'custom' subcollection rows are dropped to avoid duplicating anything now
  // owned by the calendar service.
  //
  // Hide rows whose linked SR is CANCELLED — the SR-cancel path only mutates
  // ServiceRequest.status (user-service applyRequestScope's CANCELLED branch);
  // it does NOT reach into calendar-service to also flip the linked event to
  // CANCELLED. calendar-service's own repo filter is on event.status, not
  // SR.status, so events keep coming through with an ambient "Cancelled" pill
  // instead of disappearing — the "previously cancelled request reappears"
  // symptom (2026-09-29).
  //
  // Deterministic linkage only: filter needs event.serviceRequestId → SR id
  // → status. No serviceType fallback (same wrong-SR risk as PR #325 — the
  // fallback can match the wrong SR when two same-type SRs exist on one
  // transaction, and a corrupted fallback would silently hide a live row).
  //
  // Scope limited to CANCELLED: DECLINED + EXPIRED keep visible so the
  // Reassign / Retry affordances stay reachable; COMPLETED keeps visible for
  // the "successful job" audit trail.
  const isCancelledSrEvent = (e) => {
    const srId = e?.serviceRequestId;
    if (!srId) return false;
    return srStatusById[srId] === 'CANCELLED';
  };
  const standaloneCalendarEntries = [
    ...calendarEntries.filter(e => e.category !== 'custom' && !isCancelledSrEvent(e)),
    ...normalizedCalendarEvents.filter(e => !isCancelledSrEvent(e)),
  ];

  const parseTimelineDate = (value) => {
    if (!value) return new Date(0);
    // Date-only API values must be interpreted locally, not as UTC.
    return new Date(value.length === 10 ? `${value}T00:00:00` : value);
  };
  const calendarEntryDate = (entry) => parseTimelineDate(entry?.scheduledDate);

  // Placement/insertion sort key — YYYY-MM-DD string form. Used by the
  // event-into-stage/milestone insertion loops instead of parsed timestamps
  // so a UTC ISO `scheduledDate` ("2026-09-19T23:30:00Z") doesn't sort AFTER
  // a local-midnight date-only stage `suggestedDate` ("2026-09-20") in
  // browsers with a positive TZ offset (IST etc.) — the parsed .getTime()
  // values land on either side of a TZ-shifted day boundary while the
  // displayed date strings do not. Matches the display key produced by
  // .slice(0, 10) at the entry render site so placement follows what the
  // user actually sees. Never returns a Date object.
  const dateKeyOf = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value.slice(0, 10);
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString().slice(0, 10);
    }
    return null;
  };

  // Convert a UTC ISO instant (e.g. SR.requestedDate = "2026-09-19T07:00:00Z")
  // to the viewer's local YYYY-MM-DD — what the timeline row's date label
  // shows (via formatInstantForViewer). Returns null on unparseable input.
  const instantToLocalDateKey = (iso) => {
    if (!iso) return null;
    const instant = new Date(iso);
    if (Number.isNaN(instant.getTime())) return null;
    // en-CA gives YYYY-MM-DD, matching the placement-key shape.
    return instant.toLocaleDateString('en-CA', { timeZone: viewerTimezone });
  };

  // Resolve the effective placement date key for a calendar entry. Prefers
  // the linked SR's requestedDate (which is what the row's date label
  // renders — see the isServiceEvent branch in renderCalendarEntryNode) so
  // placement matches display. Falls through to the entry's own
  // scheduledDate when no SR is resolvable.
  //
  // Ownership order mirrors the linkedSr resolution at line ~2753:
  //   tier-1 back-link via entry.serviceRequestId
  //   tier-3 by serviceType — cheap same-type match; the schedule-aware
  //     tier-3 pick used at render time isn't available here without
  //     duplicating the whole pickTier3SrForEntry helper, and this simple
  //     fallback handles the common single-SR-per-type case (multiple SRs
  //     of the same type quietly fall through to scheduledDate).
  //
  // Motivating case (2026-09-16, transaction f56e679d-...): the
  // calendar_event.scheduledDate on some create paths lands a day off from
  // the SR.requestedDate (calendar event stored "2026-09-20" while the SR
  // stored "2026-09-19T07:00:00Z", both meant to represent the same picked
  // day). Display trusted the SR; placement trusted the calendar event and
  // rendered the row out of chronological order. This helper reconciles.
  const entryScheduleKey = (entry) => {
    if (!entry) return null;
    let sr = null;
    if (entry.serviceRequestId && srById && srById[entry.serviceRequestId]) {
      sr = srById[entry.serviceRequestId];
    } else {
      // srByServiceType is keyed by lowercase serviceType (see
      // TransactionDetailPage.js:431) so match the case here — passing the
      // canonical mixed-case entryType would silently miss every entry.
      const eventServiceType = (entry.entryType || entry.eventType || '').toLowerCase();
      if (eventServiceType && srByServiceType && srByServiceType[eventServiceType]) {
        sr = srByServiceType[eventServiceType];
      }
    }
    if (sr?.requestedDate) {
      const k = instantToLocalDateKey(sr.requestedDate);
      if (k) return k;
    }
    // Place the entry on the SAME day its date label shows (formatEventForViewer),
    // i.e. the viewer's own local day — NOT the raw UTC scheduledDate. A UTC-encoded
    // event can fall on a different calendar day than its raw digits (e.g. an evening
    // event for a viewer west of UTC gets a next-day UTC scheduledDate), which
    // previously placed the row one day off from the day the user picked/sees. Mirrors
    // mobile's entryScheduleDate (eventLocalDateKey). Falls back to the raw key for
    // legacy/date-only events with no marker.
    return eventLocalDateKey(entry, viewerTimezone) || dateKeyOf(entry.scheduledDate);
  };

  // Deterministic ordering for events on the same date: date -> time -> title
  // (Gap #10) so same-day events render in a stable, time-ordered sequence.
  const sortCalendarEntries = (a, b) => {
    const d = calendarEntryDate(a) - calendarEntryDate(b);
    if (d !== 0) return d;
    const t = (a.scheduledTime || '').localeCompare(b.scheduledTime || '');
    if (t !== 0) return t;
    return (a.title || '').localeCompare(b.title || '');
  };

  // ===== MILESTONE RENDERING (ACTIVE+ transactions) =====

  // Map API milestone status to display class
  const getMilestoneStatus = (milestone) => {
    if (!milestone) return 'pending';
    const status = milestone.status?.toUpperCase();
    if (status === 'COMPLETED' || status === 'DONE') return 'completed';
    if (status === 'IN_PROGRESS' || status === 'ACTIVE') return 'in-progress';
    if (status === 'SKIPPED' || status === 'WAIVED') return 'skipped';
    if (status === 'OVERDUE' || milestone.overdue) return 'overdue';
    return 'pending';
  };

  // Human-readable status text (matches mobile getMilestoneStatusText)
  const getMilestoneStatusText = (milestone) => {
    const status = milestone.status?.toUpperCase();
    const isOverdue = status === 'OVERDUE' || milestone.overdue;
    if (isOverdue && status !== 'COMPLETED' && status !== 'SKIPPED' && status !== 'WAIVED') {
      return milestone.daysOverdue > 0 ? `Overdue by ${milestone.daysOverdue} days` : 'Overdue';
    }
    switch (status) {
      case 'COMPLETED': case 'DONE':   return milestone.completionNotes || 'Completed';
      case 'WAIVED':                   return 'Waived';
      case 'SKIPPED':                  return 'Not applicable';
      case 'IN_PROGRESS': case 'ACTIVE': return 'In progress';
      default:                         return 'Not started';
    }
  };

  // Shared status icon glyph — used by both the real milestone rows and the
  // Active Listing stage rows so the two sections render identical icons
  // instead of maintaining separate (and potentially divergent) ternaries.
  const renderMilestoneIcon = (status) => {
    switch (status) {
      case 'completed': return '✓';
      case 'in-progress': return '●';
      case 'skipped': return '—';
      case 'overdue': return '!';
      default: return '○';
    }
  };

  // Format assigned role for display
  const formatAssignedRole = (role) => {
    const map = {
      BUYER_AGENT: "Buyer's Agent", BUYERS_AGENT: "Buyer's Agent",
      LISTING_AGENT: 'Listing Agent', BUYER: 'Buyer', SELLER: 'Seller',
      BROKER: 'Broker', TRANSACTION_COORDINATOR: 'Transaction Coordinator',
      ESCROW_OFFICER: 'Escrow', TITLE_OFFICER: 'Title', LENDER: 'Lender',
      LOAN_OFFICER: 'Loan Officer', APPRAISER: 'Appraiser', INSPECTOR: 'Inspector',
    };
    if (!role) return null;
    return map[role] || role.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
  };

  // Owner role for an ACTIVE-timeline milestone. Parsed/template milestones don't
  // set assignedRole; they carry the owner in metadata.roleAccess (the ACTIONABLE
  // role). Pick the primary owner (agents before principals) so every milestone
  // shows an owner label, matching the prospect timeline's stage.roleLabel.
  const ownerRoleFromRoleAccess = (roleAccess) => {
    if (!roleAccess || typeof roleAccess !== 'object') return null;
    // Only own-side roles are valid owners — the opposite side is always read-only.
    // BUYING -> Buyer's Agent / Buyer; LISTING -> Listing Agent / Seller.
    const sideUp = (transaction?.side || transaction?.type || '').toUpperCase();
    const buying = sideUp === 'BUYING' || sideUp === 'PURCHASE';
    const priority = buying
      ? ['BUYER_AGENT', 'BUYER']
      : ['LISTING_AGENT', 'SELLER_AGENT', 'SELLER'];
    return priority.find((r) => String(roleAccess[r]).toUpperCase() === 'ACTIONABLE') || null;
  };

  const getPreviousIncompleteName = (milestoneId) => {
    const sorted = [...milestones].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    for (const m of sorted) {
      if (m.id === milestoneId) return null;
      const s = m.status?.toUpperCase();
      if (s !== 'COMPLETED' && s !== 'SKIPPED' && s !== 'WAIVED' && s !== 'DONE') return m.name || m.type;
    }
    return null;
  };

  // Default timeline was previously a hardcoded 5-row placeholder (Transaction
  // Created / Initial Review / Documentation / Under Contract / Closing). It
  // leaked into the UI any time milestones.length === 0 — e.g. a LISTING+ACTIVE
  // transaction mid-checklist (activeListingTimeline present, real milestones
  // not yet generated by "Process and generate timelines"). The names look
  // like template rows but aren't backed by any real data, confusing agents.
  // Keep it empty — the empty state is handled below (SRs / calendar entries
  // / Convert-to-Closed card still render through their own paths).
  const defaultTimeline = [];

  // Format milestone name — mirrors mobile formatMilestoneName():
  // if the name contains underscores (e.g. CONTRACT_SIGNED) convert to Title Case;
  // otherwise use the backend string as-is.
  const formatMilestoneName = (milestone) => {
    const raw = milestone.name || milestone.title || '';
    if (!raw) return 'Milestone';
    if (!raw.includes('_')) return raw;
    return raw.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  };

  // Use API milestones if available, otherwise use default timeline
  const timelineData = milestones.length > 0
    ? milestones.map((milestone, index) => ({
        id: milestone.id || index + 1,
        title: formatMilestoneName(milestone),
        subtitle: milestone.description || milestone.subtitle || '',
        date: milestone.dueDate ? formatDate(milestone.dueDate) : '',
        type: milestone.milestoneType || getTransactionType(),
        status: getMilestoneStatus(milestone),
        meta: milestone.notes || milestone.meta || '',
        originalData: milestone
      }))
    : defaultTimeline;

  // ===== RENDER =====

  // Change Date modal for prospect stages + calendar events — same UX as the
  // active-milestone modal; saves via the pages' handlers. Defined once and
  // mounted in BOTH the prospect and the active render trees (the component
  // returns early for PENDING transactions).
  const changeDateModal = editingDateEntry && (
    <div className="txn-date-modal-overlay" onClick={() => setEditingDateEntry(null)}>
      <div className="txn-date-modal" onClick={e => e.stopPropagation()}>
        <div className="txn-date-modal-header">
          <span className="txn-date-modal-title">
            {editingDateEntry.kind === 'event' ? 'Change Due By Date' : 'Change Date'}
          </span>
          <button
            className="txn-date-modal-close"
            onClick={() => setEditingDateEntry(null)}
          >✕</button>
        </div>
        <div className="txn-date-modal-milestone-name">{editingDateEntry.name}</div>
        <input
          type="date"
          className="txn-date-modal-input"
          value={editingDateEntry.date}
          onChange={e => setEditingDateEntry({ ...editingDateEntry, date: e.target.value })}
        />
        {/* Time input removed for events — the Due-By-Time input was dropped
             platform-wide (deadline is end-of-day only). Passing null through
             onEditCalendarEventDate below keeps back-compat with the legacy
             signature. */}
        <div className="txn-date-modal-actions">
          <button
            className="txn-date-modal-cancel"
            onClick={() => setEditingDateEntry(null)}
          >Cancel</button>
          <button
            className="txn-date-modal-save"
            disabled={!editingDateEntry.date}
            onClick={() => {
              if (editingDateEntry.kind === 'stage') {
                onEditStageDate && onEditStageDate(editingDateEntry.id, editingDateEntry.date);
              } else {
                onEditCalendarEventDate && onEditCalendarEventDate(editingDateEntry.id, editingDateEntry.date, editingDateEntry.time || null);
              }
              setEditingDateEntry(null);
            }}
          >Save</button>
        </div>
      </div>
    </div>
  );

  const assignSpModal = (
    <AssignServiceProviderModal
      visible={!!assignSpEntry}
      entry={assignSpEntry}
      onClose={() => setAssignSpEntry(null)}
      onSearch={onSearchServiceProviders}
      onAssign={async (provider, date, time) => {
        await onAssignServiceProvider(assignSpEntry, provider, date, time);
        setAssignSpEntry(null);
      }}
    />
  );

  // Read-only SR details overlay + inline edit. srDetailsTarget holds
  // { sr, entry }: sr feeds the display, entry lets the Assign picker
  // reuse the timeline context. onSave PATCHes the SR through the
  // requester-scope endpoint and optimistically merges the response back
  // into the local sr snapshot so the reopened modal shows fresh values
  // without waiting for the next /sent-service-requests refetch.
  const srDetailsModal = (
    <ServiceRequestDetailsModal
      sr={srDetailsTarget?.sr || null}
      onClose={() => setSrDetailsTarget(null)}
      onAssignClick={
        onAssignServiceProvider && srDetailsTarget?.entry
          ? () => {
              const baseEntry = srDetailsTarget.entry;
              const sr = srDetailsTarget.sr;
              // Prefer the SR's requestedDate as the "Due By Date" seed —
              // that's the same source the timeline row and the SR-details
              // header render via formatInstantForViewer/formatDisplayDate.
              // Using baseEntry.scheduledDate here would fall back to the
              // calendar-event's own date, which can drift from the SR
              // (bare "YYYY-MM-DD" on the calendar event vs. a UTC ISO on
              // the SR whose local Y-M-D differs) and cause an off-by-one
              // versus the row the user just clicked.
              let nextEntry = baseEntry;
              if (sr?.requestedDate) {
                const { localDate } =
                  instantToWallClockInZone(sr.requestedDate, viewerTimezone);
                if (localDate) {
                  nextEntry = {
                    ...baseEntry,
                    scheduledDate: localDate,
                    scheduledTime: '',
                    timeZone: undefined,
                  };
                }
              }
              setSrDetailsTarget(null);
              // Carry the SR the requester is actually looking at, so
              // handleAssignServiceProvider reassigns/resends exactly that SR
              // instead of re-guessing via the serviceType fallback (which
              // can resolve to a different — e.g. older DECLINED — SR of the
              // same type when this row has no serviceRequestId back-link).
              setAssignSpEntry(sr?.id
                ? { ...nextEntry, targetServiceRequestId: sr.id, targetServiceRequestStatus: sr.status }
                : nextEntry);
            }
          : null
      }
      onSave={
        srDetailsTarget?.sr?.id
          ? async (patch) => {
              const firebaseToken = await getFirebaseToken();
              const headers = {
                Authorization: `Bearer ${firebaseToken}`,
                'Content-Type': 'application/json',
              };
              const res = await apiClient.patch(
                API_ENDPOINTS.patchSentServiceRequest(srDetailsTarget.sr.id),
                patch,
                { headers },
              );
              const updated = res.data?.data || res.data || null;
              if (updated) {
                // Defensive merge — drop null/undefined keys from the response
                // so a Jackson-dropped BigDecimal (backend read bug on legacy
                // docs) doesn't wipe an intact value on the local sr. Real
                // clears from the requester still go through — those arrive as
                // explicit non-null zero, not null.
                const cleaned = Object.fromEntries(
                  Object.entries(updated).filter(([, v]) => v !== null && v !== undefined),
                );
                setSrDetailsTarget((prev) => (prev ? { ...prev, sr: { ...prev.sr, ...cleaned } } : prev));
                if (typeof onSrUpdated === 'function') onSrUpdated(cleaned);
              }
            }
          : null
      }
    />
  );

  // Checklist popup — same component for "Generate Checklist" (mode:
  // 'generate') and "View Generated" (mode: 'view'); see ChecklistModal's
  // own header comment. "Raise Service Request" hands the item off to the
  // standard Create-Service-Request page via route state — this component
  // doesn't know anything about that page's internals, it just prefills it.
  const checklistModal = checklistTarget && (
    <ChecklistModal
      serviceRequestId={checklistTarget.srId}
      attachmentId={checklistTarget.attachmentId}
      mode={checklistTarget.mode}
      reportFileUrl={checklistTarget.reportFileUrl}
      reportFileName={checklistTarget.reportFileName}
      onClose={() => setChecklistTarget(null)}
      onRaiseServiceRequest={(item, checklist) => {
        setChecklistTarget(null);
        navigate('/create-service-request', {
          state: {
            fromChecklist: {
              areaOrItemName: item.areaOrItemName,
              suggestedServiceType: item.suggestedServiceType,
              notes: item.summary,
              propertyAddress: checklistTarget.sr?.propertyAddress || '',
              transactionId: checklist?.transactionId || checklistTarget.sr?.transactionId || null,
              sourceChecklistItemId: item.id,
            },
          },
        });
      }}
    />
  );

  // While stage definitions are still loading for PENDING transactions, show a spinner
  if (isPending && !stagesReady && mergedProspectStages.length === 0) {
    return (
      <div className="txn-content-section">
        <div className="txn-content-header">
          <h2 className="txn-content-title">Prospect Timeline</h2>
        </div>
        <div className="txn-timeline-setup">
          <div className="txn-setup-spinner"></div>
          <p className="txn-setup-text">Loading prospect stages...</p>
        </div>
      </div>
    );
  }

  // Render prospect stages for PENDING transactions
  // Use mergedProspectStages (stage definitions merged with metadata) so timeline shows
  // even when metadata has no stages yet — matches mobile behavior where stages come from
  // getCachedStages() regardless of metadata initialization state.
  if (isPending && mergedProspectStages.length > 0) {
    // Effective anchor date for a prospect stage: actual completion date if
    // completed, else its suggested date (createdAt + suggestedOffsetDays, §2.3).
    // Returns YYYY-MM-DD string (see dateKeyOf) so comparisons match the
    // display key and don't drift across timezone boundaries.
    const txnCreatedAt = transaction?.createdAt ? parseTimelineDate(transaction.createdAt) : null;
    const stageAnchorKey = (stage) => {
      if (stage.completedAt) return dateKeyOf(stage.completedAt);
      // Persisted suggested date (§2.3, stamped at creation).
      if (stage.suggestedDate) return dateKeyOf(stage.suggestedDate);
      // Legacy fallback (transactions created before suggested dates were stamped).
      if (txnCreatedAt && stage.suggestedOffsetDays != null) {
        const d = new Date(txnCreatedAt);
        d.setDate(d.getDate() + stage.suggestedOffsetDays);
        return dateKeyOf(d);
      }
      return null;
    };
    // Viewer's current calendar day, for overdue comparisons below — matches
    // instantToLocalDateKey's timezone handling elsewhere in this file rather
    // than a raw UTC "now" (which would drift the boundary in positive-offset
    // zones). Prospect stages have no backend-computed overdue field (unlike
    // real Milestones, which get one from transaction-service on every
    // fetch) — this is purely a client-side, per-viewer comparison.
    const todayKey = new Date().toLocaleDateString('en-CA', { timeZone: viewerTimezone });

    // Place events by date using a running MAX of stage anchor date keys so
    // an event never lands above a later stage that already has an earlier
    // date (Listing's parallel stages are non-monotonic). Comparison is on
    // YYYY-MM-DD strings — lex order matches chronological order and stays
    // consistent with the .slice(0, 10) key the entry row itself renders.
    const eventsAboveAllStages = [];
    const eventsAfterStageIndex = {};
    [...standaloneCalendarEntries]
      .sort(sortCalendarEntries)
      .forEach(entry => {
        let insertAfter = -1;
        let runningMaxKey = null;
        const entryKey = entryScheduleKey(entry);
        for (let index = 0; index < mergedProspectStages.length; index++) {
          const k = stageAnchorKey(mergedProspectStages[index]);
          if (k) runningMaxKey = runningMaxKey && runningMaxKey > k ? runningMaxKey : k;
          if (runningMaxKey === null) continue;
          if (entryKey && runningMaxKey <= entryKey) insertAfter = index;
          else break;
        }
        if (insertAfter === -1) eventsAboveAllStages.push(entry);
        else (eventsAfterStageIndex[insertAfter] ||= []).push(entry);
      });

    // Option B — flatten the display order (events above → stage → events after → …)
    // and find the first incomplete item: the "current" item that may be acted on.
    // An incomplete event before a stage blocks that stage. Legacy non-calendar
    // entries can't be completed, so they never block (treated as done).
    //
    // Disclosure form stages (TDS/SPQ on CA, SPD on CO — three sub-stages each) are
    // non-blocking: they don't gate the next stage from unlocking, but each is still
    // independently actionable while incomplete. See NON_BLOCKING_STAGE_IDS above.
    const entryDone = (e) => e.status === 'COMPLETED' || !e._calendarEvent;
    const prospectStageDone = (s) => NON_BLOCKING_STAGE_IDS.has(s.id) || s.status === 'COMPLETED' || s.status === 'SKIPPED';
    const flatItems = [];
    eventsAboveAllStages.forEach(e => flatItems.push({ key: `event-${e.id}`, done: entryDone(e) }));
    mergedProspectStages.forEach((s, i) => {
      flatItems.push({ key: `stage-${s.id}`, done: prospectStageDone(s) });
      (eventsAfterStageIndex[i] || []).forEach(e => flatItems.push({ key: `event-${e.id}`, done: entryDone(e) }));
    });
    const currentItemKey = (flatItems.find(it => !it.done) || {}).key || null;

    return (
      <>
      <div className="txn-content-section">
        <div className="txn-content-header">
          <h2 className="txn-content-title">Prospect Timeline</h2>
          {isLoading && <span className="txn-loading-indicator">Loading...</span>}
        </div>

        <div className="txn-timeline-list">
          {canAddCalendar && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
              <button
                className="txn-timeline-button txn-primary-btn txn-add-to-calendar-btn"
                onClick={() => onShowCalendarModal && onShowCalendarModal(null)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                Add to Calendar
              </button>
            </div>
          )}
          {eventsAboveAllStages.map(entry => renderCalendarEntryNode(entry, true, currentItemKey === `event-${entry.id}`))}
          {(() => {
            return mergedProspectStages.map((stage, index) => {
            const isCompleted = stage.status === 'COMPLETED';
            const isSkipped = stage.status === 'SKIPPED';
            const isNotStarted = stage.status === 'NOT_STARTED';
            // isConversionTrigger comes from the API stage definition (matches mobile stage.isConversionTrigger)
            const isConversionTrigger = stage.isConversionTrigger === true;
            // Overdue: an incomplete stage whose suggested date has passed. Mirrors
            // the real-Milestone overdue rule (not completed/skipped + date < today),
            // computed client-side since prospect stages carry no backend overdue
            // field. Reuses stageAnchorKey so this always agrees with the date the
            // row itself displays below (suggestedDate, falling back to
            // createdAt+offset for legacy stages) — never a second source of truth.
            const stageDueKey = !isCompleted && !isSkipped ? stageAnchorKey(stage) : null;
            const isOverdue = !!stageDueKey && stageDueKey < todayKey;
            const daysOverdue = isOverdue
              ? Math.round((new Date(`${todayKey}T00:00:00Z`) - new Date(`${stageDueKey}T00:00:00Z`)) / 86400000)
              : 0;
            // Mark as / N/A is enabled on every prospect stage regardless of order, for both
            // Buying and Listing — no turn-based gating. A stage still shows no action buttons
            // once it's completed or skipped (handled by isCompleted/isSkipped at each call site).
            const isLocked = false;
            // CMA & Pricing milestone — backend-driven stage list has no fixed id we can rely
            // on across states/templates, so match on the visible name as well as the most
            // likely id slugs.
            const isCmaStage = stage.id === 'cma_pricing' || stage.id === 'cma_and_pricing'
              || /cma/i.test(stage.name || '');
            // Determine actionability from API-provided roleAccess — mirrors mobile getRoleAccessKey().
            // milestoneRoleKey already maps AGENT→BUYER_AGENT/LISTING_AGENT based on transaction side.
            const stageRoleAccess = milestoneRoleKey
              ? (stage?.metadata?.roleAccess?.[milestoneRoleKey] ?? stage?.roleAccess?.[milestoneRoleKey])
              : null;
            // Whether the current user can act on this stage (API says ACTIONABLE, or role-based fallback).
            // Covers buyers on buyer_* stages, sellers on non-wizard seller_* stages, and agents on all.
            const SELLER_WIZARD_STAGES = ['seller_tds_form', 'seller_spq_form', 'seller_nhd', 'seller_nhd_form', 'seller_nhd_report', 'seller_avid', 'seller_avid_form', 'seller_spd_form'];
            const canActOnThisStage = stageRoleAccess === 'ACTIONABLE'
              || (!stageRoleAccess && isBuyer
                  && stage.id.startsWith('buyer_')
                  && !isConversionTrigger
                  && !['buyer_offer_preparation', 'buyer_offer_submission', 'buyer_confirm_property'].includes(stage.id))
              || (!stageRoleAccess && isSeller
                  && stage.id.startsWith('seller_')
                  && !SELLER_WIZARD_STAGES.includes(stage.id));
            const trailingEvents = eventsAfterStageIndex[index] || [];

            return (
              <React.Fragment key={stage.id}>
              <div className="txn-timeline-item">
                {/* Status icon */}
                <div className={`txn-timeline-icon ${isConversionTrigger ? 'conversion-trigger' : isCompleted ? 'completed' : isSkipped ? 'skipped' : isOverdue ? 'overdue' : 'pending'}`}>
                  {isConversionTrigger ? '★' : isCompleted ? '✓' : isSkipped ? 'N/A' : isOverdue ? '!' : '○'}
                </div>
                {/* Connecting line */}
                {index !== mergedProspectStages.length - 1 && (
                  <div className={`txn-timeline-line ${isCompleted ? 'txn-timeline-line-completed' : isOverdue ? 'txn-timeline-line-overdue' : ''}`}></div>
                )}

                <div className={isConversionTrigger ? 'txn-conversion-card' : 'txn-timeline-content-box'}>
                  {/* Conversion badge shown above title for conversion trigger stage */}
                  {isConversionTrigger && (
                    <div className="txn-conversion-badge">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                      Completes → Converts to Active
                    </div>
                  )}
                  <div className="txn-timeline-header">
                    <div>
                      <div className="txn-timeline-title">{stage.name}</div>
                      <div className="txn-timeline-subtitle">{stage.description}</div>
                      {isOverdue && (
                        <div className="txn-milestone-status-text overdue">
                          {daysOverdue > 0 ? `Overdue by ${daysOverdue} days` : 'Overdue'}
                        </div>
                      )}
                    </div>
                    <div className="txn-timeline-date">
                      {isCompleted && stage.completedAt && <>{formatUtcNaiveInstantForViewer(stage.completedAt).dateLabel}<br /></>}
                      {isSkipped && <span className="txn-skipped-label">Skipped</span>}
                      {!isCompleted && !isSkipped && stage.suggestedDate && (
                        canAddCalendar && onEditStageDate && !isConversionTrigger ? (
                          /* Same date+pencil control as active-milestone dates.
                             Deliberately NOT gated by turn order (isLocked):
                             future stages' dates are editable. */
                          <>
                            <button
                              className={`txn-date-edit-btn${isOverdue ? ' txn-date-overdue' : ''}`}
                              onClick={() => setEditingDateEntry({
                                kind: 'stage',
                                id: stage.id,
                                name: stage.name,
                                date: String(stage.suggestedDate).slice(0, 10),
                              })}
                            >
                              <span>{formatDate(stage.suggestedDate)}</span>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                            <br />
                          </>
                        ) : isOverdue ? (
                          <><span className="txn-date-overdue">{formatDate(stage.suggestedDate)}</span><br /></>
                        ) : (
                          <>{formatDate(stage.suggestedDate)}<br /></>
                        )
                      )}
                      <span className="txn-role-label">{stage.roleLabel}</span>
                    </div>
                  </div>

                  {/* CMA & Pricing — mirrors mobile CmaMilestoneBlock: summary card + Generated
                      Reports list once reports exist (visible to everyone), Generate/Regenerate
                      restricted to agents, brokers, and TCs (not BUYER_AGENT/LISTING_AGENT) and
                      disabled on cancelled transactions or once this stage is skipped. Replaces
                      the generic Mark as dropdown for this stage (auto-completes the milestone
                      on the first successful generate). */}
                  {isCmaStage && (
                    <CmaPricingCard
                      transaction={transaction}
                      isAgent={(activeRole === 'AGENT' || activeRole === 'TRANSACTION_COORDINATOR' || ['BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN'].includes(activeRole?.toUpperCase())) && !isCancelledTransaction}
                      locked={isLocked || isSkipped}
                      onGenerated={() => onProspectStageComplete && onProspectStageComplete(stage.id)}
                    />
                  )}

                  {/* Confirm Property — property selector, agents only */}
                  {stage.id === 'buyer_confirm_property' && isAgent && (() => {
                    // Mark as / N/A enabled regardless of order — no turn-based gating.
                    const prevStagesDone = true;

                    // Helper mirrors TransactionProperties
                    const getPropAddress = (p) =>
                      p.address || p.propertyAddress || p.streetAddress || p.fullAddress || p.name || '';
                    const getPropMls = (p) =>
                      p.mlsId || p.mlsNumber || p.listingId || '';

                    return (
                      <div className={`txn-confirm-prop-section${!prevStagesDone ? ' txn-confirm-prop-section-disabled' : ''}`}>
                        {!prevStagesDone && (
                          <div className="txn-rpa-locked-notice">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                            Complete or mark N/A all previous stages to unlock Confirm Property.
                          </div>
                        )}

                        <div className="txn-confirm-prop-label">Select property for offer preparation</div>

                        {confirmPropsLoading ? (
                          <div className="txn-confirm-prop-loading">
                            <div className="txn-confirm-prop-spinner"></div>
                            Loading properties…
                          </div>
                        ) : (
                          <>
                            {/* Property dropdown with "Others" option — mirrors mobile ProspectTimeline */}
                            <div className="txn-confirm-prop-select-wrap">
                              <select
                                className="txn-confirm-prop-select"
                                value={smartSearchOpen ? '__others__' : confirmPropertyId}
                                onChange={async e => {
                                  const val = e.target.value;
                                  if (val === '__others__') {
                                    setSmartSearchOpen(true);
                                    setConfirmPropertyId('');
                                    setSmartSearchQuery('');
                                    setSmartSearchResults([]);
                                    setSmartSearchError('');
                                    setPlacePredictions([]);
                                  } else {
                                    setSmartSearchOpen(false);
                                    setSmartSearchQuery('');
                                    setSmartSearchResults([]);
                                    setSmartSearchError('');
                                    setPlacePredictions([]);
                                    handlePropertyChange(val);
                                    if (val) {
                                      // Persist the selection on the backend (selectedForTransaction,
                                      // prospect-timeline interestedPropertyId, Lone Wolf transaction sync)
                                      // — mirrors mobile's dropdown and handleSmartSearchSelect. Awaited
                                      // before completing the stage: both write transaction metadata.
                                      try {
                                        const token = await getFirebaseToken();
                                        await apiClient.post(
                                          API_ENDPOINTS.selectInterestedProperty(transaction.id, val),
                                          {},
                                          { headers: { Authorization: `Bearer ${token}` } }
                                        );
                                      } catch (err) {
                                        console.warn('[ConfirmProperty] Failed to persist property selection:', err?.message);
                                      }
                                    }
                                    if (val && !isCompleted) {
                                      onProspectStageComplete && onProspectStageComplete(stage.id);
                                    }
                                  }
                                }}
                                disabled={!prevStagesDone || isFetchingWizard}
                              >
                                <option value="">— Select a property —</option>
                                {confirmProperties.map(p => {
                                  const pid = p.id || p.interestedPropertyId;
                                  const addr = getPropAddress(p);
                                  return (
                                    <option key={pid} value={pid}>{addr || pid}</option>
                                  );
                                })}
                                <option value="__others__">Other</option>
                              </select>
                              <svg className="txn-confirm-prop-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                            </div>

                            {/* Smart Search panel — shown when "Others" is selected */}
                            {smartSearchOpen && prevStagesDone && (
                              <div className="txn-smart-search-panel">
                                {/* Search input */}
                                <div className="txn-smart-search-input-wrap" style={{ position: 'relative' }}>
                                  <span className="txn-smart-search-input-icon">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                    </svg>
                                  </span>
                                  <input
                                    type="text"
                                    className="txn-smart-search-input"
                                    placeholder="Start typing an address..."
                                    value={smartSearchQuery}
                                    onChange={e => handleSmartSearchInput(e.target.value)}
                                    autoFocus
                                    autoComplete="off"
                                  />
                                  {(placesLoading || attomLoading) && <div className="txn-smart-search-spinner" />}

                                  {/* Google Places predictions dropdown */}
                                  {placePredictions.length > 0 && (
                                    <div className="txn-smart-places-dropdown">
                                      {placePredictions.map((pred, pi) => (
                                        <button
                                          key={pred.place_id}
                                          className={`txn-smart-places-item${pi < placePredictions.length - 1 ? ' txn-smart-places-item--border' : ''}`}
                                          onMouseDown={e => e.preventDefault()}
                                          onClick={() => handlePlaceSelect(pred, stage.id, isCompleted)}
                                        >
                                          <svg className="txn-smart-places-pin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                                          </svg>
                                          <div className="txn-smart-places-text">
                                            <span className="txn-smart-places-main">
                                              {pred.structured_formatting?.main_text || pred.description}
                                            </span>
                                            {pred.structured_formatting?.secondary_text && (
                                              <span className="txn-smart-places-secondary">
                                                {pred.structured_formatting.secondary_text}
                                              </span>
                                            )}
                                          </div>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                

                                {/* ATTOM loading skeleton — shown while fetching property after address selection */}
                                {attomLoading && (
                                  <div className="txn-smart-search-skeleton">
                                    <div className="txn-smart-search-skeleton-icon" />
                                    <div className="txn-smart-search-skeleton-lines">
                                      <div className="txn-smart-search-skeleton-line txn-smart-search-skeleton-line--wide" />
                                      <div className="txn-smart-search-skeleton-line txn-smart-search-skeleton-line--narrow" />
                                    </div>
                                  </div>
                                )}

                                {/* ATTOM Property Result Card — matches mobile selectedPropertySummary design */}
                                {!attomLoading && smartSearchResults.length > 0 && (
                                  <div className="txn-smart-prop-results">
                                    {smartSearchResults.map((r, i) => {
                                      const addrLine = r.address || '';
                                      const cityLine = [r.city, r.state, r.zip].filter(Boolean).join(', ');
                                      return (
                                        <div key={i} className="txn-smart-prop-card">
                                          {/* Top row: thumbnail + address info */}
                                          <div className="txn-smart-prop-card-top">
                                            <div className="txn-smart-prop-card-thumb">
                                              {r.primaryPhoto
                                                ? <img src={r.primaryPhoto} alt="" className="txn-smart-prop-card-img" />
                                                : (
                                                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
                                                    <polyline points="9 21 9 12 15 12 15 21"/>
                                                  </svg>
                                                )
                                              }
                                            </div>
                                            <div className="txn-smart-prop-card-info">
                                              <span className="txn-smart-prop-card-addr">{addrLine || smartSearchQuery}</span>
                                              {cityLine && <span className="txn-smart-prop-card-city">{cityLine}</span>}
                                            </div>
                                          </div>

                                          {/* Property detail chips */}
                                          {(r.bedrooms || r.bathrooms || r.squareFeet || r.propertyType) && (
                                            <div className="txn-smart-prop-card-chips">
                                              {r.bedrooms && (
                                                <span className="txn-smart-prop-card-chip">
                                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8"/><path d="M4 10V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4"/><line x1="12" y1="10" x2="12" y2="20"/></svg>
                                                  {r.bedrooms} bd
                                                </span>
                                              )}
                                              {r.bathrooms && (
                                                <span className="txn-smart-prop-card-chip">
                                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12h16M4 12a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2M20 12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M6 5v7M18 5v7"/><rect x="2" y="12" width="20" height="6" rx="1"/></svg>
                                                  {r.bathrooms} ba
                                                </span>
                                              )}
                                              {r.squareFeet && (
                                                <span className="txn-smart-prop-card-chip">
                                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
                                                  {Number(r.squareFeet).toLocaleString()} sqft
                                                </span>
                                              )}
                                              {r.propertyType && (
                                                <span className="txn-smart-prop-card-chip">{r.propertyType}</span>
                                              )}
                                            </div>
                                          )}

                                          {/* Select button */}
                                          <button
                                            className={`txn-smart-prop-card-btn${addingSmartProperty ? ' txn-smart-prop-card-btn--adding' : ''}`}
                                            onClick={() => handleSmartSearchSelect(r, stage.id, isCompleted)}
                                            disabled={addingSmartProperty}
                                          >
                                            {addingSmartProperty ? (
                                              <>
                                                <div className="txn-smart-search-spinner txn-smart-search-spinner--sm txn-smart-search-spinner--white" />
                                                Adding Property…
                                              </>
                                            ) : (
                                              <>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                                Select This Property
                                              </>
                                            )}
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}

                                {/* Error */}
                                {!smartSearchLoading && smartSearchError && (
                                  <div className="txn-smart-search-error">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                    {smartSearchError}
                                  </div>
                                )}

                                {/* No results — shown after a place was selected but ATTOM returned nothing */}
                                {!attomLoading && !placesLoading && !smartSearchError && smartSearchQuery.trim().length >= 3 && placePredictions.length === 0 && smartSearchResults.length === 0 && (
                                  <div className="txn-smart-search-empty">
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                                    <span>No address suggestions found.</span>
                                    <span className="txn-smart-search-empty-sub">Try a different address, e.g. <em>123 Main St, Los Angeles, CA</em></span>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Empty state — no properties and smart search closed */}
                            {confirmProperties.length === 0 && !smartSearchOpen && (
                              <div className="txn-confirm-prop-empty">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                No interested properties yet. Select "Others" above to search or add in the Properties tab.
                              </div>
                            )}
                          </>
                        )}

                        {/* Wizard lookup indicator */}
                        {isFetchingWizard && (
                          <div className="txn-confirm-prop-loading">
                            <div className="txn-confirm-prop-spinner"></div>
                            Checking for existing wizard…
                          </div>
                        )}

                        {/* Selected property detail chip */}
                        {confirmPropertyId && (() => {
                          const sel = confirmProperties.find(
                            p => (p.id || p.interestedPropertyId) === confirmPropertyId
                          );
                          if (!sel) return null;
                          const addr = getPropAddress(sel);
                          const beds = sel.bedrooms || sel.beds;
                          const baths = sel.bathrooms || sel.baths;
                          return (
                            <div className="txn-confirm-prop-chip">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/><polyline points="9 21 9 12 15 12 15 21"/></svg>
                              <div className="txn-confirm-prop-chip-info">
                                {addr && <span className="txn-confirm-prop-chip-addr">{addr}</span>}
                                {(beds || baths) && (
                                  <span className="txn-confirm-prop-chip-meta">
                                    {beds ? <span>{beds} bd</span> : null}
                                    {baths ? <span>{baths} ba</span> : null}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}

                  {/* Confirm Property — read-only summary for Buyer role (matches mobile selectedPropertySummary) */}
                  {stage.id === 'buyer_confirm_property' && !isAgent && (() => {
                    // Resolve selected property: stage progress interestedPropertyId → selected flag → prop fallback
                    const confirmStageData = prospectStages.find(s => s.stageId === 'buyer_confirm_property');
                    const selectedId =
                      confirmStageData?.interestedPropertyId ||
                      confirmProperties.find(p => p.selected || p.isSelected || p.selectedAt)?.id ||
                      interestedPropertyId ||
                      '';
                    const selectedProp = selectedId
                      ? confirmProperties.find(p => (p.id || p.interestedPropertyId) === selectedId)
                      : null;

                    if (!selectedProp) {
                      return (
                        <div className="txn-rpa-section">
                          <div className="txn-buyer-prop-empty">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/><polyline points="9 21 9 12 15 12 15 21"/></svg>
                            No property selected yet
                          </div>
                        </div>
                      );
                    }

                    const addr = selectedProp.address || selectedProp.propertyAddress || selectedProp.streetAddress || selectedProp.fullAddress || '';
                    const beds = selectedProp.bedrooms || selectedProp.beds;
                    const baths = selectedProp.bathrooms || selectedProp.baths;

                    return (
                      <div className="txn-rpa-section">
                        <div className="txn-confirm-prop-chip">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/><polyline points="9 21 9 12 15 12 15 21"/></svg>
                          <div className="txn-confirm-prop-chip-info">
                            {addr && <span className="txn-confirm-prop-chip-addr">{addr}</span>}
                            {(beds || baths) && (
                              <span className="txn-confirm-prop-chip-meta">
                                {beds ? <span>{beds} bd</span> : null}
                                {baths ? <span>{baths} ba</span> : null}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* RPA Wizard inline section — only for Offer Preparation stage, agents only */}
                  {stage.id === 'buyer_offer_preparation' && isAgent && (() => {
                    // Mark as / N/A enabled regardless of order — no turn-based gating.
                    const prevStagesDone = true;
                    return (
                      <div className={`txn-rpa-section${!prevStagesDone ? ' txn-rpa-section-disabled' : ''}`}>
                        {!prevStagesDone && (
                          <div className="txn-rpa-locked-notice">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                            Complete or mark N/A all previous stages to unlock Offer Preparation.
                          </div>
                        )}
                        <div className="txn-rpa-section-label">OFFER WIZARD</div>
                        <div className="txn-rpa-info-box">
                          {txnState === 'CO'
                            ? <><strong>11-step guided wizard</strong> — Colorado CBS (Contract to Buy &amp; Sell). Covers CO-specific deadlines, water rights, title, and disclosures.</>
                            : <><strong>10-step guided wizard</strong> — fill offer terms, financing, contingencies &amp; all key dates. Completed RPA auto-populates Offer Submission package.</>
                          }
                        </div>
                        <div className="txn-rpa-card-footer">
                          {rpaDraft ? (() => {
                            const rpaStatus = (rpaDraft.status || '').toUpperCase();
                            const completedSteps = Array.isArray(rpaDraft.completedSteps) ? rpaDraft.completedSteps : [];
                            const isRpaComplete = rpaStatus === 'SUBMITTED' || rpaStatus === 'COMPLETED' || completedSteps.length >= rpaWizardTotalSteps;
                            if (isRpaComplete) {
                              return (
                                <div className="txn-rpa-footer-draft-row">
                                  <div className="txn-rpa-complete-badge">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                    {txnState === 'CO' ? 'CBS Wizard completed · All 11 steps' : 'RPA Wizard completed · All 10 steps'}
                                  </div>
                                  <button
                                    className="txn-rpa-resume-btn"
                                    disabled={isResumingRPA}
                                    onClick={txnState === 'CO' ? handleResumeCBS : handleResumeRPA}
                                  >
                                    {isResumingRPA ? (
                                      <>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                        Loading…
                                      </>
                                    ) : (
                                      <>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                        {txnState === 'CO' ? 'Edit CBS Offer' : 'Edit Offer'}
                                      </>
                                    )}
                                  </button>
                                </div>
                              );
                            }
                            return (
                              <div className="txn-rpa-footer-draft-row">
                                <div className="txn-rpa-draft-badge">
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                  Draft saved · Step {rpaDraft.currentStep || 1} of {rpaWizardTotalSteps}
                                </div>
                                <button
                                  className="txn-rpa-resume-btn"
                                  disabled={isResumingRPA}
                                  onClick={txnState === 'CO' ? handleResumeCBS : handleResumeRPA}
                                >
                                  {isResumingRPA ? (
                                    <>
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                      Loading…
                                    </>
                                  ) : (
                                    <>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                                      {txnState === 'CO' ? 'Resume Offer Creation' : 'Resume Offer Creation'}
                                    </>
                                  )}
                                </button>
                              </div>
                            );
                          })() : (
                            <>
                              <button
                                className="txn-rpa-launch-btn"
                                disabled={!prevStagesDone || isLaunchingRPA || !confirmPropertyId}
                                onClick={txnState === 'CO' ? handleLaunchCBS : handleLaunchRPA}
                              >
                                {isLaunchingRPA ? (
                                  <>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                    Starting…
                                  </>
                                ) : (
                                  <>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M15 4V2"/><path d="M15 16v-2"/><path d="M8 9h2"/><path d="M20 9h2"/><path d="M17.8 11.8L19 13"/><path d="M15 9h.01"/><path d="M17.8 6.2L19 5"/><path d="M10.2 6.2L9 5"/><path d="M10.2 11.8L9 13"/><path d="M12 22l4-8H8l4 8z"/>
                                    </svg>
                                     Launch Offer Creation
                                    {/* {txnState === 'CO' ? 'Launch CBS Offer Wizard' : 'Launch Offer Creation'} */}
                                  </>
                                )}
                              </button>
                              <span className="txn-rpa-meta-text">{txnState === 'CO' ? 'Step-by-step · ~20 min · Auto-saves draft' : 'Step-by-step · ~14 min · Auto-saves draft'}</span>
                            </>
                          )}
                        </div>
                        {prevStagesDone && !confirmPropertyId && (
                          <div className="txn-rpa-no-property-error">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                            Select a property in the Confirm Property step above to enable the RPA Wizard.
                          </div>
                        )}
                        {rpaNoPropertyError && (
                          <div className="txn-rpa-no-property-error">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                            Add at least 1 property in this transaction before launching the RPA Wizard.
                          </div>
                        )}
                        {rpaLaunchError && (
                          <div className="txn-rpa-no-property-error">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                            {rpaLaunchError}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Offer Preparation — read-only RPA status for Buyer role (matches mobile renderRpaWizardCard readOnly branches) */}
                  {stage.id === 'buyer_offer_preparation' && !isAgent && (() => {
                    const draft = offerSubmissionDraft;
                    const draftSteps = draft?.steps || {};
                    // Use backend's completedSteps array directly (steps use stepStatus string, not a completed boolean)
                    const completedSteps = Array.isArray(draft?.completedSteps) ? draft.completedSteps : [];
                    const resumeAtStep = draft?.currentStep || (completedSteps.length + 1);
                    const rpaIsComplete = draft && (
                      draft.status === 'SUBMITTED' ||
                      draft.status === 'COMPLETED' ||
                      completedSteps.length >= rpaWizardTotalSteps
                    );
                    const rpaIsInProgress = draft && completedSteps.length > 0 && !rpaIsComplete;

                    if (rpaIsComplete) {
                      // Completed — badge + single summary text with · separators (matches mobile completed branch)
                      const s3 = draftSteps['3']?.data || {};
                      const s4 = draftSteps['4']?.data || {};
                      const s6 = draftSteps['6']?.data || {};
                      const offerPrice = parseOfferAmount(s3.offerPrice);
                      const emd = parseOfferAmount(s3.emdAmount || s3.initialDeposit);
                      const financing = s4.financingType || '';
                      const coe = s6.coeDaysAfterAcceptance || s6.coeDays || '';
                      const emdLabel = emd > 0
                        ? (emd >= 1000 ? `$${Math.round(emd / 1000)}K EMD` : `$${emd.toLocaleString()} EMD`)
                        : null;
                      const chips = [
                        offerPrice ? `$${offerPrice.toLocaleString()}` : null,
                        financing || null,
                        emdLabel,
                        coe ? `${coe}-day COE` : null,
                      ].filter(Boolean);
                      return (
                        <div className="txn-rpa-section">
                          <div className="txn-buyer-rpa-complete-badge">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            {txnState === 'CO' ? 'CBS Wizard completed · All 11 steps' : 'RPA Wizard completed · All 10 steps'}
                          </div>
                          {chips.length > 0 && (
                            <div className="txn-buyer-rpa-chips">
                              <span className="txn-buyer-rpa-chip">{chips.join('  ·  ')}</span>
                            </div>
                          )}
                        </div>
                      );
                    }

                    if (rpaIsInProgress) {
                      // In-progress — progress bar + step pills + draft summary (matches mobile in-progress branch)
                      const pct = Math.round((completedSteps.length / rpaWizardTotalSteps) * 100);
                      const stepLabels = txnState === 'CO'
                        ? ['Property', 'Parties', 'Price & EMD', 'Financing', 'Deadlines', 'Inclusions', 'Water Rights', 'HOA & Title', 'Disclosures', 'Addenda', 'Review']
                        : ['Property', 'Parties', 'Price', 'Financing', 'Contingencies', 'COE', 'Inclusions', 'Costs', 'Addenda', 'Review'];

                      // Draft summary rows — only shown when relevant steps are completed
                      const s1 = draftSteps['1']?.data || {};
                      const s2 = draftSteps['2']?.data || {};
                      const s3 = draftSteps['3']?.data || {};
                      const s4 = draftSteps['4']?.data || {};
                      const summaryRows = [];
                      if (completedSteps.includes(3) && s3.offerPrice) {
                        const price = parseOfferAmount(s3.offerPrice);
                        const listPrice = parseOfferAmount(s1.listPrice);
                        const diff = listPrice > 0 ? Math.round(((price - listPrice) / listPrice) * 100) : 0;
                        summaryRows.push({ label: 'Offer Price', value: `$${price.toLocaleString()}`, highlight: listPrice > 0 ? `${diff >= 0 ? '+' : ''}${diff}% ask` : null });
                      }
                      if (completedSteps.includes(3) && s3.emdAmount) {
                        const emd = parseOfferAmount(s3.emdAmount);
                        const price = parseOfferAmount(s3.offerPrice);
                        const pctEmd = price > 0 ? ((emd / price) * 100).toFixed(1) : null;
                        summaryRows.push({ label: 'EMD', value: `$${emd.toLocaleString()}${pctEmd ? ` (${pctEmd}%)` : ''}` });
                      }
                      if (completedSteps.includes(4) && s4.financingType) {
                        const loan = s4.firstLoanAmount ? `$${parseOfferAmount(s4.firstLoanAmount).toLocaleString()}` : '';
                        summaryRows.push({ label: 'Financing', value: `${s4.financingType}${loan ? ` · ${loan}` : ''}` });
                      }
                      if (completedSteps.includes(2) && Array.isArray(s2.buyers) && s2.buyers.filter(Boolean).length > 0) {
                        summaryRows.push({ label: 'Buyers', value: s2.buyers.filter(Boolean).join(' & ') });
                      }

                      return (
                        <div className="txn-rpa-section">
                          <div className="txn-rpa-section-label">Offer Creation</div>
                          <div className="txn-buyer-rpa-progress-row">
                            <span><strong>{completedSteps.length} of 10</strong> steps completed</span>
                            <span className="txn-buyer-rpa-pct">{pct}%</span>
                          </div>
                          <div className="txn-buyer-rpa-progress-bar">
                            <div className="txn-buyer-rpa-progress-fill" style={{ width: `${pct}%` }} />
                          </div>
                          <div className="txn-buyer-rpa-step-pills">
                            {stepLabels.map((label, i) => {
                              const stepNum = i + 1;
                              const done = completedSteps.includes(stepNum);
                              const current = stepNum === resumeAtStep;
                              return (
                                <span key={stepNum} className={`txn-buyer-rpa-pill${done ? ' done' : ''}${current ? ' current' : ''}`}>
                                  {done ? '✓ ' : ''}{current && !done ? <span className="txn-rpa-pill-dot" /> : null}{label}
                                </span>
                              );
                            })}
                          </div>
                          {summaryRows.length > 0 && (
                            <div className="txn-buyer-rpa-draft-summary">
                              <div className="txn-buyer-rpa-draft-title">DRAFT SUMMARY</div>
                              <div className="txn-buyer-rpa-draft-divider" />
                              {summaryRows.map((row) => (
                                <div key={row.label} className="txn-buyer-rpa-draft-row">
                                  <span className="txn-buyer-rpa-draft-label">{row.label}</span>
                                  <span className="txn-buyer-rpa-draft-value-row">
                                    <span className="txn-buyer-rpa-draft-value">{row.value}</span>
                                    {row.highlight && <span className="txn-buyer-rpa-draft-highlight">{row.highlight}</span>}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }

                    // Not started — info box + document list (matches mobile notStarted branch)
                    return (
                      <div className="txn-rpa-section">
                        <div className="txn-rpa-section-label">Offer Creation</div>
                        <div className="txn-rpa-info-box">
                          <strong>10-step guided wizard</strong> — fill offer terms, financing, contingencies &amp; all key dates. Completed RPA auto-populates the Offer Submission package.
                        </div>
                        <div className="txn-buyer-rpa-doc-list">
                          {['Purchase Agreement', 'Agency Disclosure', 'Wire Fraud Advisory'].map((doc, i) => (
                            <div key={i} className="txn-buyer-rpa-doc-row">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                              <span className="txn-buyer-rpa-doc-name">{doc}</span>
                              <span className="txn-buyer-rpa-doc-status">Not started</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Offer Submission inline section — only for offer submission stage, agents only */}
                  {stage.id === 'buyer_offer_submission' && isAgent && (() => {
                    // Mark as / N/A enabled regardless of order — no turn-based gating.
                    const prevStagesDone = true;

                    // Derive checklist state from RPA draft (mirrors mobile OfferSubmissionCard)
                    const draftSteps = offerSubmissionDraft?.steps || {};
                    const s2data = draftSteps['2']?.data || {};
                    const s3data = draftSteps['3']?.data || {};
                    const emdAmt = parseOfferAmount(s3data.emdAmount || s3data.initialDeposit);
                    const emdConfirmed = emdAmt > 0;
                    const preApprovalUploaded = offerPreApprovalFile !== null;

                    // Names match across all docs (mirrors mobile nameChecks useMemo)
                    const normName = (s) => (s || '').toLowerCase().trim().replace(/\s+/g, ' ');
                    // 1. Buyer names: RPA step 2 vs BUYER participants
                    const rpaBuyerNames = [];
                    if (s2data.buyer1Name) {
                      rpaBuyerNames.push(normName(s2data.buyer1Name));
                      if (s2data.buyer2Name) rpaBuyerNames.push(normName(s2data.buyer2Name));
                    } else if (Array.isArray(s2data.buyers)) {
                      s2data.buyers.forEach(b => {
                        const n = typeof b === 'string' ? b : [b.firstName, b.lastName].filter(Boolean).join(' ');
                        if (n) rpaBuyerNames.push(normName(n));
                      });
                    }
                    // Normalize role the same way accessControl.js does so that aliases
                    // like 'CLIENT' and 'PURCHASER' are treated as 'BUYER'.
                    const normParticipantRole = (r) => {
                      const up = (r || '').toUpperCase().trim().replace(/[^A-Z_]/g, '_');
                      return { CLIENT: 'BUYER', PURCHASER: 'BUYER', BUYERS_AGENT: 'BUYER_AGENT', BUYING_AGENT: 'BUYER_AGENT' }[up] || up;
                    };
                    const txnBuyerNames = participants
                      .filter(p => normParticipantRole(p.role || p.participantRole || p.userRole || '') === 'BUYER')
                      .map(p => normName(p.name || p.fullName || [p.firstName, p.lastName].filter(Boolean).join(' ')));
                    const buyerMatch = rpaBuyerNames.length === 0 || rpaBuyerNames.every(rb => txnBuyerNames.some(tb => tb === rb));
                    // 2. Agent name: RPA step 2 vs user profile
                    let agentProfile = {};
                    try { agentProfile = JSON.parse(localStorage.getItem('user') || '{}'); } catch {}
                    const profileFullName = normName(agentProfile.fullName || [agentProfile.firstName, agentProfile.lastName].filter(Boolean).join(' '));
                    const rpaAgentName = normName(s2data.agentName || s2data.buyerAgentName || '');
                    const agentNameMatch = !rpaAgentName || !profileFullName || rpaAgentName === profileFullName;
                    // 3. Agent DRE license: RPA step 2 vs user profile
                    const profileLicense = (agentProfile.licenseNumber || agentProfile.dreLicenseNumber || '').trim();
                    const rpaLicense = (s2data.agentDRE || s2data.buyerAgentLicense || '').trim();
                    const licenseMatch = !rpaLicense || !profileLicense || rpaLicense === profileLicense;
                    // 4. Seller name: RPA step 2 vs interested property sellerName
                    const selPropId = confirmPropertyId || interestedPropertyId;
                    const selProp = confirmProperties.find(p => (p.id || p.interestedPropertyId) === selPropId);
                    const propSellerName = normName(selProp?.sellerName || '');
                    const rpaSellerNames = [s2data.seller1, s2data.seller2, s2data.sellerName].filter(Boolean).map(normName);
                    const sellerMatch = rpaSellerNames.length === 0 || !propSellerName
                      || rpaSellerNames.every(rs => propSellerName.includes(rs) || rs.includes(propSellerName));
                    const namesMatch = buyerMatch && agentNameMatch && licenseMatch && sellerMatch;

                    const buyerCount = rpaBuyerNames.length;
                    const rpaSignedLabel = `${txnState === 'CO' ? 'CBS' : 'RPA'} signed by ${buyerCount > 1 ? 'both buyers' : 'buyer'}`;
                    const emdLabel = emdConfirmed
                      ? `EMD confirmed — ${fmtOfferCurrency(emdAmt)} wire`
                      : 'EMD confirmed (complete RPA step 3)';

                    if (offerSent) {
                      return (
                        <div className="txn-offer-section">
                          <div className="txn-offer-success-banner">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                            Offer sent successfully
                          </div>
                          <button className="txn-offer-resend-link" onClick={() => setOfferSent(false)}>
                            Resend ›
                          </button>
                        </div>
                      );
                    }

                    return (
                      <div className={`txn-offer-section${!prevStagesDone ? ' txn-rpa-section-disabled' : ''}`}>
                        {!prevStagesDone && (
                          <div className="txn-rpa-locked-notice">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                            Complete or mark N/A all previous stages to unlock Offer Submission.
                          </div>
                        )}

                        {/* PACKAGE */}
                        <div className="txn-offer-section-label">PACKAGE</div>
                        <div className="txn-offer-sectionbox">
                          {[
                            { label: 'Pre-Approval Letter', ready: preApprovalUploaded, isUpload: true },
                          ].map((doc, di, arr) => (
                            <React.Fragment key={doc.label}>
                              <div className={`txn-offer-doc-row${doc.isUpload && offerFlashError && !doc.ready ? ' txn-offer-doc-row-flash' : ''}`}>
                                <span className={`txn-offer-doc-dot${doc.ready ? ' ready' : ''}`}></span>
                                <span className="txn-offer-doc-label">{doc.label}</span>
                                {doc.isUpload && !doc.ready ? (
                                  <button
                                    className={`txn-offer-tag txn-offer-tag-upload${offerFlashError ? ' txn-offer-tag-required' : ''}`}
                                    onClick={() => preApprovalInputRef.current?.click()}
                                    disabled={isUploadingPreApproval}
                                  >
                                    {isUploadingPreApproval ? '…' : offerFlashError ? '! Required' : 'Click to Upload'}
                                  </button>
                                ) : (
                                  <span className="txn-offer-tag txn-offer-tag-ready">Ready</span>
                                )}
                              </div>
                              {di < arr.length - 1 && <div className="txn-offer-divider" />}
                            </React.Fragment>
                          ))}
                        </div>

                        {/* Upload / change pre-approval */}
                        <div
                          className={`txn-upload-drop-target${isPreApprovalDragging ? ' drag-over' : ''}`}
                          style={{ display: 'block' }}
                          onDragOver={(e) => { e.preventDefault(); if (!isUploadingPreApproval) setIsPreApprovalDragging(true); }}
                          onDragLeave={() => setIsPreApprovalDragging(false)}
                          onDrop={handlePreApprovalDrop}
                        >
                          <button
                            className="txn-offer-upload-row"
                            onClick={() => preApprovalInputRef.current?.click()}
                            disabled={isUploadingPreApproval}
                          >
                            {isUploadingPreApproval ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                            ) : preApprovalUploaded ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><polyline points="9 12 12 15 16 9"/></svg>
                            ) : (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                            )}
                            <div className="txn-offer-upload-text">
                              {preApprovalUploaded ? (
                                <span
                                  className="txn-offer-upload-title"
                                  onClick={(e) => { e.stopPropagation(); handlePreviewPreApproval(); }}
                                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                >
                                  {offerPreApprovalFile.name}
                                </span>
                              ) : (
                                <>
                                  <span className="txn-offer-upload-title">Upload Pre-Approval Letter</span>
                                  <span className="txn-offer-upload-sub">PDF · Max 50 MB</span>
                                </>
                              )}
                            </div>
                            {preApprovalUploaded && <span className="txn-offer-upload-change">Change</span>}
                          </button>
                        </div>

                        {/* CHECKLIST (hidden — matches mobile OfferSubmissionCard; keep code for future re-enable) */}
                        {false && (
                          <>
                            <div className="txn-offer-section-label" style={{ marginTop: 16 }}>CHECKLIST</div>
                            <div className="txn-offer-sectionbox">
                              {[
                                { label: rpaSignedLabel, done: offerRpaSigned },
                                { label: emdLabel, done: emdConfirmed },
                                { label: 'Pre-approval letter uploaded', done: preApprovalUploaded },
                                { label: 'Names match across all docs', done: namesMatch },
                              ].map((item, ii, arr) => (
                                <React.Fragment key={item.label}>
                                  <div className="txn-offer-check-row">
                                    <span className={`txn-offer-checkbox${item.done ? ' done' : ''}`}>
                                      {item.done && (
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                                      )}
                                    </span>
                                    <span className="txn-offer-check-label">{item.label}</span>
                                  </div>
                                  {ii < arr.length - 1 && <div className="txn-offer-divider" />}
                                </React.Fragment>
                              ))}
                            </div>
                            {/* Name mismatch details — mirrors mobile OfferSubmissionCard mismatchBox */}
                            {!namesMatch && (
                              <div className="txn-offer-mismatch-box">
                                {!buyerMatch && <div className="txn-offer-mismatch-text">• Buyer name mismatch</div>}
                                {!agentNameMatch && <div className="txn-offer-mismatch-text">• Agent name mismatch</div>}
                                {!licenseMatch && <div className="txn-offer-mismatch-text">• Agent license mismatch</div>}
                                {!sellerMatch && <div className="txn-offer-mismatch-text">• Seller name mismatch</div>}
                              </div>
                            )}
                          </>
                        )}

                        {/* EMAIL TO LISTING AGENT */}
                        <div className="txn-offer-section-label" style={{ marginTop: 16 }}>EMAIL TO LISTING AGENT</div>
                        <div className="txn-offer-email-card">
                          <div className="txn-offer-email-row">
                            <span className="txn-offer-email-field-label">TO</span>
                            <input
                              className="txn-offer-email-input"
                              type="email"
                              multiple
                              value={offerToEmail}
                              onChange={e => setOfferToEmail(e.target.value)}
                              placeholder="listingagent@example.com, another@example.com"
                            />
                          </div>
                          <div className="txn-offer-divider" />
                          <div className="txn-offer-email-row">
                            <span className="txn-offer-email-field-label">SUBJ</span>
                            <input
                              className="txn-offer-email-input"
                              type="text"
                              value={offerEmailSubject}
                              onChange={e => setOfferEmailSubject(e.target.value)}
                              placeholder="Email subject"
                            />
                          </div>
                          <div className="txn-offer-email-body-divider" />
                          <pre className="txn-offer-email-body">{offerEmailBody}</pre>
                          <button className="txn-offer-edit-email-btn" onClick={handleOpenOfferEmailEdit}>
                            Edit Email ›
                          </button>
                        </div>

                        {/* ATTACHMENTS — mirrors mobile OfferSubmissionCard attachments section */}
                        <div className="txn-offer-section-label" style={{ marginTop: 16 }}>ATTACHMENTS</div>
                        {(() => {
                          const visibleAuto = offerAutoAttachments.filter(a => !offerExcludedAutoNames.includes(a.name));
                          const preApprovalVisible = preApprovalUploaded && offerPreApprovalFile
                            && !offerExcludedAutoNames.includes(offerPreApprovalFile.name);
                          const isEmpty = visibleAuto.length === 0 && !preApprovalVisible && offerExtraAttachments.length === 0;
                          return (
                            <div className="txn-offer-sectionbox">
                              {isEmpty ? (
                                <div className="txn-offer-attachment-empty">No files attached</div>
                              ) : (
                                <>
                                  {visibleAuto.map((file, idx) => (
                                    <React.Fragment key={`auto-${idx}-${file.name}`}>
                                      {idx > 0 && <div className="txn-offer-divider" />}
                                      <div className="txn-offer-attachment-row">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                        <div className="txn-offer-attachment-info">
                                          <span className="txn-offer-attachment-name">{file.name}</span>
                                          <span className="txn-offer-attachment-size">Auto-attached</span>
                                        </div>
                                        <button className="txn-offer-attachment-remove" onClick={() => handleRemoveAutoAttachment(file.name)}>Remove</button>
                                      </div>
                                    </React.Fragment>
                                  ))}
                                  {preApprovalVisible && offerPreApprovalFile && (
                                    <React.Fragment>
                                      {visibleAuto.length > 0 && <div className="txn-offer-divider" />}
                                      <div className="txn-offer-attachment-row">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                        <div className="txn-offer-attachment-info">
                                          <span className="txn-offer-attachment-name">{offerPreApprovalFile.name}</span>
                                          <span className="txn-offer-attachment-size">Auto-attached</span>
                                        </div>
                                        <button className="txn-offer-attachment-remove" onClick={handleClearPreApproval} disabled={isClearingPreApproval}>
                                          {isClearingPreApproval ? 'Removing…' : 'Remove'}
                                        </button>
                                      </div>
                                    </React.Fragment>
                                  )}
                                  {offerExtraAttachments.map((file, idx) => (
                                    <React.Fragment key={`extra-${idx}-${file.name}`}>
                                      {(visibleAuto.length > 0 || preApprovalVisible || idx > 0) && <div className="txn-offer-divider" />}
                                      <div className="txn-offer-attachment-row">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                                        <div className="txn-offer-attachment-info">
                                          <span className="txn-offer-attachment-name">{file.name}</span>
                                          {file.size && <span className="txn-offer-attachment-size">{formatOfferFileSize(file.size)}</span>}
                                        </div>
                                        <button className="txn-offer-attachment-remove" onClick={() => handleRemoveExtraAttachment(idx)}>Remove</button>
                                      </div>
                                    </React.Fragment>
                                  ))}
                                </>
                              )}
                            </div>
                          );
                        })()}
                        <button
                          className="txn-offer-add-attachment-btn"
                          onClick={() => offerExtraFileInputRef.current?.click()}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                          Add file
                        </button>

                        {/* SEND BUTTON — checklist gating removed, opens confirm-send popup first
                            (mirrors mobile OfferSubmissionCard's handleConfirmSend) */}
                        <button
                          className="txn-offer-send-btn"
                          onClick={handleConfirmSendOffer}
                          disabled={isSendingOffer}
                        >
                          {isSendingOffer ? (
                            <>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                              Sending…
                            </>
                          ) : (
                            'Send Offer Package'
                          )}
                        </button>

                        {offerSendError && (
                          <div className="txn-rpa-no-property-error">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                            {offerSendError}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* TDS Wizard inline section — Seller fills, Agent views status */}
                  {stage.id === 'seller_tds_form' && isSeller && (() => {
                    // Mark as / N/A enabled regardless of order — no turn-based gating.
                    const prevStagesDone = true;
                    return (
                      <div className={`txn-rpa-section${!prevStagesDone ? ' txn-rpa-section-disabled' : ''}`}>
                        {!prevStagesDone && (
                          <div className="txn-rpa-locked-notice">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                            Complete or mark N/A all previous stages to unlock TDS Form.
                          </div>
                        )}
                        <div className="txn-rpa-section-label">TDS WIZARD</div>
                        {/* <div className="txn-rpa-info-box">
                          <strong>6-step guided wizard</strong> — California Transfer Disclosure Statement (Civil Code §1102). Covers property systems, structural defects, seller disclosures &amp; agent inspection.
                        </div> */}
                        <div className="txn-rpa-card-footer">
                          {(() => {
                            const TDS_STEP_LABELS = ['Property', 'Systems', 'Defects', 'Disclosures', 'Agent Insp.', 'Review'];
                            const completedNums = tdsProgress?.completedSteps ?? [];
                            const resumeAt = tdsProgress?.resumeAtStep ?? (tdsDraft?.currentStep || 1);
                            const tdsIsComplete = completedNums.length >= 6 ||
                              (tdsDraft && isWizardSubmittedForReview(tdsDraft.status));
                            const tdsIsInProgress = tdsDraft && completedNums.length > 0 && !tdsIsComplete;

                            if (tdsIsComplete) {
                              return (
                                <div className="txn-rpa-footer-draft-row">
                                  <div className="txn-buyer-rpa-complete-badge">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                    TDS Wizard completed · All 6 steps
                                  </div>
                                  <button
                                    className="txn-rpa-resume-btn"
                                    disabled={isResumingTDS}
                                    onClick={handleResumeTDS}
                                  >
                                    {isResumingTDS ? (
                                      <>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                        Loading…
                                      </>
                                    ) : (
                                      <>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                        Edit TDS
                                      </>
                                    )}
                                  </button>
                                </div>
                              );
                            }

                            if (tdsIsInProgress) {
                              const pct = Math.round((completedNums.length / 6) * 100);
                              return (
                                <div style={{ width: '100%' }}>
                                  <div className="txn-buyer-rpa-draft-title">WIZARD PROGRESS</div>
                                  <div className="txn-buyer-rpa-progress-row">
                                    <span><strong>{completedNums.length} of 6</strong> steps completed</span>
                                    <span className="txn-buyer-rpa-pct">{pct}%</span>
                                  </div>
                                  <div className="txn-buyer-rpa-progress-bar">
                                    <div className="txn-buyer-rpa-progress-fill" style={{ width: `${pct}%` }} />
                                  </div>
                                  <div className="txn-buyer-rpa-step-pills">
                                    {TDS_STEP_LABELS.map((label, i) => {
                                      const stepNum = i + 1;
                                      const done = completedNums.includes(stepNum);
                                      const current = stepNum === resumeAt;
                                      return (
                                        <span key={stepNum} className={`txn-buyer-rpa-pill${done ? ' done' : ''}${current ? ' current' : ''}`}>
                                          {done ? '✓ ' : ''}{current && !done ? <span className="txn-rpa-pill-dot" /> : null}{label}
                                        </span>
                                      );
                                    })}
                                  </div>
                                  <div className="txn-rpa-footer-draft-row" style={{ borderTop: '1px solid #EDE9FE', padding: '12px 14px', marginTop: 4 }}>
                                    <div className="txn-rpa-draft-badge">
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                      Draft saved · Step {resumeAt} of 6
                                    </div>
                                    <button
                                      className="txn-rpa-resume-btn"
                                      disabled={isResumingTDS}
                                      onClick={handleResumeTDS}
                                    >
                                      {isResumingTDS ? (
                                        <>
                                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                          Loading…
                                        </>
                                      ) : (
                                        <>
                                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                                          Resume TDS Creation — Step {resumeAt}
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              );
                            }

                            if (tdsDraft) {
                              return (
                                <div className="txn-rpa-footer-draft-row">
                                  <div className="txn-rpa-draft-badge">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                    Draft saved · Step {tdsDraft.currentStep || 1} of {tdsDraft.totalSteps || 6}
                                  </div>
                                  <button
                                    className="txn-rpa-resume-btn"
                                    disabled={isResumingTDS}
                                    onClick={handleResumeTDS}
                                  >
                                    {isResumingTDS ? (
                                      <>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                        Loading…
                                      </>
                                    ) : (
                                      <>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                                        Resume TDS Form
                                      </>
                                    )}
                                  </button>
                                </div>
                              );
                            }

                            return (
                              <>
                                <button
                                  className="txn-rpa-launch-btn"
                                  disabled={!prevStagesDone || isLaunchingTDS}
                                  onClick={handleLaunchTDS}
                                >
                                  {isLaunchingTDS ? (
                                    <>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                      Starting…
                                    </>
                                  ) : (
                                    <>
                                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                                      Launch TDS Form
                                    </>
                                  )}
                                </button>
                                <span className="txn-rpa-meta-text">Step-by-step · ~12 min · Auto-saves draft</span>
                              </>
                            );
                          })()}
                        </div>
                        {tdsLaunchError && (
                          <div className="txn-rpa-no-property-error">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                            {tdsLaunchError}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* TDS review panel — Agent view only */}
                  {stage.id === 'seller_tds_form' && isAgent && !isSeller && (
                    <div className="txn-rpa-section">
                      <div className="txn-rpa-wizard-tag">TDS REVIEW</div>
                      {!tdsDraft ? (
                        <div className="txn-rpa-info-box" style={{ marginBottom: 14 }}>
                          <strong>Awaiting seller submission</strong>
                          {' — the seller has not yet started their Transfer Disclosure Statement. You will be able to review it and complete the Agent Inspection (Step 5) once submitted.'}
                        </div>
                      ) : (() => {
                        const TDS_STEP_LABELS = ['Property', 'Systems', 'Defects', 'Disclosures', 'Agent Insp.', 'Review'];
                        const sellerSteps = tdsProgress?.completedSteps ?? [];
                        const sellerPct = Math.round((sellerSteps.length / 6) * 100);
                        return (
                          <>
                            <div className="txn-buyer-rpa-draft-title">SELLER'S TDS PROGRESS</div>
                            <div className="txn-buyer-rpa-progress-row">
                              <span><strong>{sellerSteps.length} of 6</strong> steps completed</span>
                              <span className="txn-buyer-rpa-pct">{sellerPct}%</span>
                            </div>
                            <div className="txn-buyer-rpa-progress-bar">
                              <div className="txn-buyer-rpa-progress-fill" style={{ width: `${sellerPct}%` }} />
                            </div>
                            <div className="txn-buyer-rpa-step-pills">
                              {TDS_STEP_LABELS.map((label, i) => {
                                const stepNum = i + 1;
                                const done = sellerSteps.includes(stepNum);
                                return (
                                  <span key={stepNum} className={`txn-buyer-rpa-pill${done ? ' done' : ''}`}>
                                    {done ? '✓ ' : ''}{label}
                                  </span>
                                );
                              })}
                            </div>
                            <button
                              className="txn-rpa-review-btn"
                              disabled={isResumingTDS}
                              onClick={handleReviewTDS}
                            >
                              {isResumingTDS ? (
                                <>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                  Loading…
                                </>
                              ) : (
                                <>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                                  Review TDS &amp; Complete Agent Inspection
                                </>
                              )}
                            </button>
                            <div className="txn-rpa-launch-subtitle">Review seller disclosures · Complete Step 5 — Agent Visual Inspection</div>
                          </>
                        );
                      })()}
                      {tdsLaunchError && (
                        <div className="txn-rpa-no-property-error" style={{ padding: '0 14px 10px' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                          {tdsLaunchError}
                        </div>
                      )}
                    </div>
                  )}

                  {/* SPQ Wizard inline section — Seller fills, Agent views status */}
                  {stage.id === 'seller_spq_form' && isSeller && (() => {
                    // Mark as / N/A enabled regardless of order — no turn-based gating.
                    const prevStagesDone = true;
                    return (
                      <div className={`txn-rpa-section${!prevStagesDone ? ' txn-rpa-section-disabled' : ''}`}>
                        {!prevStagesDone && (
                          <div className="txn-rpa-locked-notice">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                            Complete or mark N/A all previous stages to unlock SPQ Form.
                          </div>
                        )}
                        <div className="txn-rpa-section-label">SPQ WIZARD</div>
                        {/* <div className="txn-rpa-info-box">
                          <strong>9-step guided wizard</strong> — California Seller's Property Questionnaire (C.A.R. Form SPQ). Covers §5–§19 disclosures including repairs, systems, water, boundaries, HOA, title, and governmental issues.
                        </div> */}
                        <div className="txn-rpa-card-footer">
                          {(() => {
                            const SPQ_STEP_LABELS = ['Property', 'Docs & Legal', 'Repairs', 'Systems', 'Water & Pests', 'Boundaries', 'Title', 'Gov & Other', 'Review'];
                            const completedNums = spqProgress?.completedSteps ?? [];
                            const resumeAt = spqProgress?.resumeAtStep ?? (spqDraft?.currentStep || 1);
                            const spqIsComplete = completedNums.length >= 9 ||
                              (spqDraft && isWizardSubmittedForReview(spqDraft.status));
                            const spqIsInProgress = spqDraft && completedNums.length > 0 && !spqIsComplete;

                            if (spqIsComplete) {
                              return (
                                <div className="txn-rpa-footer-draft-row">
                                  <div className="txn-buyer-rpa-complete-badge">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                    SPQ Wizard completed · All 9 steps
                                  </div>
                                  <button
                                    className="txn-rpa-resume-btn"
                                    disabled={isResumingSPQ}
                                    onClick={handleResumeSPQ}
                                  >
                                    {isResumingSPQ ? (
                                      <>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                        Loading…
                                      </>
                                    ) : (
                                      <>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                        Edit SPQ
                                      </>
                                    )}
                                  </button>
                                </div>
                              );
                            }

                            if (spqIsInProgress) {
                              const pct = Math.round((completedNums.length / 9) * 100);
                              return (
                                <div style={{ width: '100%' }}>
                                  <div className="txn-buyer-rpa-draft-title">WIZARD PROGRESS</div>
                                  <div className="txn-buyer-rpa-progress-row">
                                    <span><strong>{completedNums.length} of 9</strong> steps completed</span>
                                    <span className="txn-buyer-rpa-pct">{pct}%</span>
                                  </div>
                                  <div className="txn-buyer-rpa-progress-bar">
                                    <div className="txn-buyer-rpa-progress-fill" style={{ width: `${pct}%` }} />
                                  </div>
                                  <div className="txn-buyer-rpa-step-pills">
                                    {SPQ_STEP_LABELS.map((label, i) => {
                                      const stepNum = i + 1;
                                      const done = completedNums.includes(stepNum);
                                      const current = stepNum === resumeAt;
                                      return (
                                        <span key={stepNum} className={`txn-buyer-rpa-pill${done ? ' done' : ''}${current ? ' current' : ''}`}>
                                          {done ? '✓ ' : ''}{current && !done ? <span className="txn-rpa-pill-dot" /> : null}{label}
                                        </span>
                                      );
                                    })}
                                  </div>
                                  <div className="txn-rpa-footer-draft-row" style={{ borderTop: '1px solid #EDE9FE', padding: '12px 14px', marginTop: 4 }}>
                                    <div className="txn-rpa-draft-badge">
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                      Draft saved · Step {resumeAt} of 9
                                    </div>
                                    <button
                                      className="txn-rpa-resume-btn"
                                      disabled={isResumingSPQ}
                                      onClick={handleResumeSPQ}
                                    >
                                      {isResumingSPQ ? (
                                        <>
                                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                          Loading…
                                        </>
                                      ) : (
                                        <>
                                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                                          Resume SPQ Creation — Step {resumeAt}
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              );
                            }

                            if (spqDraft) {
                              return (
                                <div className="txn-rpa-footer-draft-row">
                                  <div className="txn-rpa-draft-badge">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                    Draft saved · Step {spqDraft.currentStep || 1} of {spqDraft.totalSteps || 9}
                                  </div>
                                  <button
                                    className="txn-rpa-resume-btn"
                                    disabled={isResumingSPQ}
                                    onClick={handleResumeSPQ}
                                  >
                                    {isResumingSPQ ? (
                                      <>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                        Loading…
                                      </>
                                    ) : (
                                      <>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                                        Resume SPQ Form
                                      </>
                                    )}
                                  </button>
                                </div>
                              );
                            }

                            return (
                              <>
                                <button
                                  className="txn-rpa-launch-btn"
                                  disabled={!prevStagesDone || isLaunchingSPQ}
                                  onClick={handleLaunchSPQ}
                                >
                                  {isLaunchingSPQ ? (
                                    <>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                      Starting…
                                    </>
                                  ) : (
                                    <>
                                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                                      Launch SPQ Form
                                    </>
                                  )}
                                </button>
                                <span className="txn-rpa-meta-text">Step-by-step · ~18 min · Auto-saves draft</span>
                              </>
                            );
                          })()}
                        </div>
                        {spqLaunchError && (
                          <div className="txn-rpa-no-property-error">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                            {spqLaunchError}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* SPQ review panel — Agent view only */}
                  {stage.id === 'seller_spq_form' && isAgent && !isSeller && (
                    <div className="txn-rpa-section">
                      <div className="txn-rpa-wizard-tag">SPQ REVIEW</div>
                      {!spqDraft ? (
                        <div className="txn-rpa-info-box" style={{ marginBottom: 14 }}>
                          <strong>Awaiting seller submission</strong>
                          {' — the seller has not yet started their Seller Property Questionnaire. You will be able to review it once submitted.'}
                        </div>
                      ) : (() => {
                        const SPQ_STEP_LABELS = ['Property', 'Docs & Legal', 'Repairs', 'Systems', 'Water & Pests', 'Boundaries', 'Title', 'Gov & Other', 'Review'];
                        const sellerSteps = spqProgress?.completedSteps ?? [];
                        const sellerPct = Math.round((sellerSteps.length / 9) * 100);
                        return (
                          <>
                            <div className="txn-buyer-rpa-draft-title">SELLER'S SPQ PROGRESS</div>
                            <div className="txn-buyer-rpa-progress-row">
                              <span><strong>{sellerSteps.length} of 9</strong> steps completed</span>
                              <span className="txn-buyer-rpa-pct">{sellerPct}%</span>
                            </div>
                            <div className="txn-buyer-rpa-progress-bar">
                              <div className="txn-buyer-rpa-progress-fill" style={{ width: `${sellerPct}%` }} />
                            </div>
                            <div className="txn-buyer-rpa-step-pills">
                              {SPQ_STEP_LABELS.map((label, i) => {
                                const stepNum = i + 1;
                                const done = sellerSteps.includes(stepNum);
                                return (
                                  <span key={stepNum} className={`txn-buyer-rpa-pill${done ? ' done' : ''}`}>
                                    {done ? '✓ ' : ''}{label}
                                  </span>
                                );
                              })}
                            </div>
                            <button
                              className="txn-rpa-review-btn"
                              disabled={isResumingSPQ}
                              onClick={handleReviewSPQ}
                            >
                              {isResumingSPQ ? (
                                <>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                  Loading…
                                </>
                              ) : (
                                <>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                                  Review Seller Property Questionnaire
                                </>
                              )}
                            </button>
                            <div className="txn-rpa-launch-subtitle">View seller disclosures · Read-only</div>
                          </>
                        );
                      })()}
                      {spqLaunchError && (
                        <div className="txn-rpa-no-property-error" style={{ marginTop: 8 }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                          {spqLaunchError}
                        </div>
                      )}
                    </div>
                  )}

                  {/* TDS / SPQ Lone Wolf form — the same form the Forms tab shows, inside its
                      "Create / Upload Signed TDS/SPQ" stage. Agent/TC on Lone-Wolf-pinned transactions. */}
                  {(stage.id === 'seller_tds_form_creation' || stage.id === 'seller_spq_form_creation')
                    && isAgent && !isSeller && transaction?.esignatureProvider === 'LONEWOLF' && (
                    <LoneWolfStageFormCard
                      transaction={transaction}
                      formType={stage.id === 'seller_tds_form_creation' ? 'TDS' : 'SPQ'}
                      participants={participants}
                      userRole={accessControl?.userRole || ''}
                    />
                  )}

                  {/* SPD Wizard inline section — CO listing only: Seller role opens SPD19 wizard popup */}
                  {stage.id === 'seller_spd_form' && isSeller && (() => {
                    // Mark as / N/A enabled regardless of order — no turn-based gating.
                    const prevStagesDone = true;
                    const spdComplete = spdProgress && Array.isArray(spdProgress.completedSteps) && spdProgress.completedSteps.length >= 10;
                    const spdInProgress = spdDraft && spdProgress && spdProgress.completedSteps?.length > 0 && !spdComplete;
                    return (
                      <div className={`txn-rpa-section${!prevStagesDone ? ' txn-rpa-section-disabled' : ''}`}>
                        {!prevStagesDone && (
                          <div className="txn-rpa-locked-notice">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                            Complete or mark N/A all previous stages to unlock SPD Form.
                          </div>
                        )}
                        <div className="txn-rpa-section-label">SELLER'S PROPERTY DISCLOSURE (SPD19)</div>
                        <div className="txn-rpa-info-box">
                          <strong>10-step guided wizard</strong> — Colorado SPD19 (Seller's Property Disclosure). Covers building conditions, roof, electrical, HVAC, water, flooding, environmental (incl. radon), legal, and HOA disclosures.
                        </div>
                        <div className="txn-rpa-card-footer">
                          {spdComplete ? (
                            <div className="txn-rpa-footer-draft-row">
                              <div className="txn-rpa-complete-badge">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                SPD Wizard completed · All 10 steps
                              </div>
                              <button className="txn-rpa-resume-btn" onClick={handleResumeSPD} disabled={isResumingSPD}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                {isResumingSPD ? 'Opening…' : 'Edit SPD'}
                              </button>
                            </div>
                          ) : spdInProgress ? (
                            <div className="txn-rpa-footer-draft-row">
                              <div className="txn-rpa-draft-badge">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                Draft saved · Step {spdProgress.completedSteps.length + 1} of 10
                              </div>
                              <button className="txn-rpa-resume-btn" onClick={handleResumeSPD} disabled={isResumingSPD}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                                {isResumingSPD ? 'Opening…' : 'Resume SPD Wizard'}
                              </button>
                            </div>
                          ) : (
                            <>
                              <button
                                className="txn-rpa-launch-btn"
                                disabled={!prevStagesDone || isLaunchingSPD}
                                onClick={handleLaunchSPD}
                              >
                                {isLaunchingSPD
                                  ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>Starting…</>
                                  : <><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>Launch SPD Wizard</>
                                }
                              </button>
                              <span className="txn-rpa-meta-text">Step-by-step · ~20 min · Auto-saves draft</span>
                              {spdLaunchError && <div className="txn-rpa-error-text" style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{spdLaunchError}</div>}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* SPD review panel — Agent view only, CO listing */}
                  {stage.id === 'seller_spd_form' && isAgent && !isSeller && (
                    <div className="txn-rpa-section">
                      <div className="txn-rpa-wizard-tag">SPD REVIEW</div>
                      {!spdDraft ? (
                        <div className="txn-rpa-info-box" style={{ marginBottom: 14 }}>
                          <strong>Awaiting seller submission</strong>
                          {' — the seller has not yet started their Seller\'s Property Disclosure. You will be able to review it once submitted.'}
                        </div>
                      ) : (() => {
                        const SPD_STEP_LABELS = ['Property Info', 'Building', 'Roof & Appliances', 'Electrical', 'HVAC & Heat', 'Water & Sewer', 'Flooding & Env', 'Legal & Access', 'HOA & District', 'Review & Confirm'];
                        const sellerSteps = spdProgress?.completedSteps ?? [];
                        const sellerPct = Math.round((sellerSteps.length / 10) * 100);
                        return (
                          <>
                            <div className="txn-buyer-rpa-draft-title">SELLER'S SPD PROGRESS</div>
                            <div className="txn-buyer-rpa-progress-row">
                              <span><strong>{sellerSteps.length} of 10</strong> steps completed</span>
                              <span className="txn-buyer-rpa-pct">{sellerPct}%</span>
                            </div>
                            <div className="txn-buyer-rpa-progress-bar">
                              <div className="txn-buyer-rpa-progress-fill" style={{ width: `${sellerPct}%` }} />
                            </div>
                            <div className="txn-buyer-rpa-step-pills">
                              {SPD_STEP_LABELS.map((label, i) => {
                                const stepNum = i + 1;
                                const done = sellerSteps.includes(stepNum);
                                return (
                                  <span key={stepNum} className={`txn-buyer-rpa-pill${done ? ' done' : ''}`}>
                                    {done ? '✓ ' : ''}{label}
                                  </span>
                                );
                              })}
                            </div>
                            <button className="txn-rpa-review-btn" disabled={isResumingSPD} onClick={handleReviewSPD}>
                              {isResumingSPD ? <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>Loading…</> : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>Review SPD</>}
                            </button>
                            <div className="txn-rpa-launch-subtitle">Review seller disclosures across all 10 steps</div>
                          </>
                        );
                      })()}
                      {spdLaunchError && (
                        <div className="txn-rpa-no-property-error" style={{ padding: '0 14px 10px' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                          {spdLaunchError}
                        </div>
                      )}
                    </div>
                  )}

                  {/* AVID Mark Complete section — listing side, agents/TC only, not yet completed */}
                  {(stage.id === 'seller_avid' || stage.id === 'seller_avid_form') && isAgent && !isCompleted && !isSkipped && (() => {
                    // Mark as / N/A enabled regardless of order — no turn-based gating.
                    const prevStagesDone = true;
                    return (
                      <div className={`txn-rpa-section${!prevStagesDone ? ' txn-rpa-section-disabled' : ''}`}>
                        {!prevStagesDone && (
                          <div className="txn-rpa-locked-notice">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                            Complete or mark N/A all previous stages to unlock AVID.
                          </div>
                        )}
                        <div className="txn-rpa-section-label">AVID</div>
                        <div className="txn-rpa-info-box">
                          Complete a reasonably diligent <strong>visual inspection of accessible areas</strong> of the property and disclose all material facts. Once done, mark this stage as complete.
                        </div>
                        <div className="txn-rpa-card-footer">
                          <button
                            className="txn-rpa-launch-btn"
                            disabled={!prevStagesDone || isMarkingAvidComplete}
                            onClick={() => handleAvidMarkComplete(stage.id)}
                          >
                            {isMarkingAvidComplete ? (
                              <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                Marking Complete…
                              </>
                            ) : (
                              <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                Mark as Complete
                              </>
                            )}
                          </button>
                        </div>
                        {avidCompleteError && (
                          <div className="txn-rpa-no-property-error">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                            {avidCompleteError}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* NHD Upload section — listing side, agents/brokers only (matches mobile:
                      TC and BUYER_AGENT/LISTING_AGENT are not granted upload access), not yet completed */}
                  {(stage.id === 'seller_nhd' || stage.id === 'seller_nhd_form' || stage.id === 'seller_nhd_report') &&
                    (activeRole === 'AGENT' || ['BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN'].includes(activeRole?.toUpperCase())) &&
                    !isCompleted && !isSkipped && (() => {
                    // Mark as / N/A enabled regardless of order — no turn-based gating.
                    const prevStagesDone = true;
                    return (
                      <div className={`txn-rpa-section txn-rpa-section--nhd${!prevStagesDone ? ' txn-rpa-section-disabled' : ''}`}>
                        {!prevStagesDone && (
                          <div className="txn-rpa-locked-notice">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                            Complete or mark N/A all previous stages to unlock NHD Report.
                          </div>
                        )}
                        <div className="txn-rpa-section-label">NHD REPORT</div>
                        <div className="txn-rpa-info-box">
                          Upload the <strong>Natural Hazard Disclosure (NHD)</strong> report for this property.
                        </div>

                        {/* Hidden file input — upload fires immediately on selection, then auto-marks stage complete */}
                        <input
                          ref={nhdFileInputRef}
                          type="file"
                          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                          style={{ display: 'none' }}
                          onChange={e => handleNhdFileChange(e)}
                        />

                        {/* Upload Report + Mark as Complete / N/A — NHD is optional */}
                        <div className="txn-rpa-card-footer">
                          <button
                            className="txn-rpa-launch-btn"
                            disabled={!prevStagesDone || isUploadingNhd}
                            onClick={() => nhdFileInputRef.current?.click()}
                          >
                            {isUploadingNhd ? (
                              <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                Uploading…
                              </>
                            ) : (
                              <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                                Upload Report
                              </>
                            )}
                          </button>
                          <div className="txn-mark-as-wrapper" ref={openMarkAsId === stage.id ? markAsRef : null}>
                            <button
                              className="txn-timeline-button txn-primary-btn"
                              disabled={!prevStagesDone}
                              onClick={() => setOpenMarkAsId(openMarkAsId === stage.id ? null : stage.id)}
                            >
                              Mark as
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 4 }}>
                                <polyline points="6 9 12 15 18 9" />
                              </svg>
                            </button>
                            {openMarkAsId === stage.id && (
                              <div className="txn-mark-as-dropdown">
                                <button
                                  className="txn-mark-as-option"
                                  onClick={() => { setOpenMarkAsId(null); onProspectStageComplete && onProspectStageComplete(stage.id); }}
                                >
                                  <span className="txn-mark-as-icon txn-mark-as-complete">✓</span>
                                  Complete
                                </button>
                                <button
                                  className="txn-mark-as-option"
                                  onClick={() => { setOpenMarkAsId(null); onProspectStageSkip && onProspectStageSkip(stage.id); }}
                                >
                                  <span className="txn-mark-as-icon txn-mark-as-na">—</span>
                                  N/A
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Uploaded NHD documents list (mirrors mobile nhdDocuments.map) */}
                        {nhdDocuments.length > 0 && (
                          <div className="txn-nhd-docs-list">
                            {nhdDocuments.map(doc => {
                              const docId = doc.documentId || doc.id;
                              const isDeleting = isDeletingNhdDoc === docId;
                              const isViewing = viewingNhdDocId === docId;
                              return (
                                <div key={docId} className="txn-nhd-file-row">
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                  <span className="txn-nhd-file-name">{doc.fileName || doc.name || 'Document'}</span>
                                  <button
                                    className="txn-nhd-view-btn"
                                    disabled={isViewing}
                                    onClick={() => handleViewNhdDoc(doc)}
                                    title="View file"
                                  >
                                    {isViewing ? '…' : 'View'}
                                  </button>
                                  <button
                                    className="txn-nhd-delete-btn"
                                    disabled={isDeleting}
                                    onClick={() => handleDeleteNhdDoc(doc)}
                                    title="Remove file"
                                  >
                                    {isDeleting ? '…' : '✕'}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {nhdUploadError && (
                          <div className="txn-rpa-no-property-error">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                            {nhdUploadError}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <div className="txn-timeline-actions">
                    {/* Single right-aligned cluster for this row — previously the
                        conversion-trigger block, the normal-stage block, and the Notes
                        trigger each had their OWN .txn-milestone-right-actions div. That
                        class carries margin-left:auto, and flexbox splits the leftover
                        space EQUALLY across every sibling with an auto margin — with two
                        such divs present at once, "Mark as" landed centered instead of
                        flush right. One wrapper, one auto margin, fixes it. */}
                    <div className="txn-milestone-right-actions">
                    {/* Conversion trigger actions — matches mobile conversionCard quickActions */}
                    {/* Button visible even when locked (isLocked) — disabled until it's this stage's turn, mirrors mobile stageDisabled pattern */}
                    {!isCompleted && !isSkipped && isConversionTrigger && (
                      <>
                        {stage.id === 'buyer_rpa_signed' ? (
                          /* buyer_rpa_signed ("Create/Upload Signed Purchase Agreement") is
                             agent-only — the buyer never gets a Mark as/Complete option here,
                             only the agent does (matches mobile isAgentOrBrokerRole branch). */
                          isAgent && (
                            <>
                              <button
                                className="txn-timeline-button txn-primary-btn"
                                disabled={isLocked}
                                onClick={() => !isLocked && onProspectStageComplete && onProspectStageComplete(stage.id)}
                              >
                                Mark as
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 4 }}><polyline points="20 6 9 17 4 12"/></svg>
                              </button>
                              <button
                                className="txn-timeline-button txn-share-offer-btn"
                                onClick={handleShareOffer}
                                disabled={isLocked || isShareOfferLoading}
                              >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                                {isShareOfferLoading ? 'Sending…' : 'Share Offer'}
                              </button>
                            </>
                          )
                        ) : (
                          /* Everyone else on conversion triggers: Mark as ✓ + N/A (matches mobile else branch) */
                          <>
                            <button
                              className="txn-timeline-button txn-primary-btn"
                              disabled={isLocked}
                              onClick={() => !isLocked && onProspectStageComplete && onProspectStageComplete(stage.id)}
                            >
                              Mark as
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 4 }}><polyline points="20 6 9 17 4 12"/></svg>
                            </button>
                            <button
                              className="txn-timeline-button txn-na-btn"
                              disabled={isLocked}
                              onClick={() => !isLocked && onProspectStageSkip && onProspectStageSkip(stage.id)}
                            >
                              Not applicable
                            </button>
                          </>
                        )}
                      </>
                    )}

                    {/* Normal stages: Mark as dropdown with Complete + N/A — agents, buyers (buyer_*), sellers (non-wizard seller_*) */}
                    {/* Button visible even when locked — disabled until it's this stage's turn, mirrors mobile stageDisabled pattern */}
                    {(isAgent || canActOnThisStage) && !isCompleted && !isSkipped && !isConversionTrigger && !isCmaStage && !SELLER_WIZARD_STAGES.includes(stage.id) && stage.id !== 'buyer_offer_preparation' && stage.id !== 'buyer_offer_submission' && stage.id !== 'buyer_confirm_property' && (
                        <div className="txn-mark-as-wrapper" ref={openMarkAsId === stage.id ? markAsRef : null}>
                          <button
                            className="txn-timeline-button txn-primary-btn"
                            disabled={isLocked}
                            onClick={() => !isLocked && setOpenMarkAsId(openMarkAsId === stage.id ? null : stage.id)}
                          >
                            Mark as
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 4 }}>
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </button>
                          {!isLocked && openMarkAsId === stage.id && (
                            <div className="txn-mark-as-dropdown">
                              <button
                                className="txn-mark-as-option"
                                onClick={() => { setOpenMarkAsId(null); onProspectStageComplete && onProspectStageComplete(stage.id); }}
                              >
                                <span className="txn-mark-as-icon txn-mark-as-complete">✓</span>
                                Complete
                              </button>
                              <button
                                className="txn-mark-as-option"
                                onClick={() => { setOpenMarkAsId(null); onProspectStageSkip && onProspectStageSkip(stage.id); }}
                              >
                                <span className="txn-mark-as-icon txn-mark-as-na">—</span>
                                N/A
                              </button>
                            </div>
                          )}
                        </div>
                    )}

                    {/* View Only — shown when API says READ_ONLY (or role has no access and no special inline UI) */}
                    {/* {!isAgent && !isConversionTrigger && !buyerCanActOnThisStage
                      && stageRoleAccess !== 'ACTIONABLE'
                      && !(isBuyer && stage.id.startsWith('buyer_'))
                      && !(isSeller && (stage.id === 'seller_tds_form' || stage.id === 'seller_spq_form')) && (
                      <span className="txn-timeline-readonly-badge">View Only</span>
                    )} */}

                    {/* Notes — private to the viewer, scoped to this specific prospect
                        stage, same pattern as the calendar/service-request entries and
                        the active-milestone rows so every timeline section (not just
                        Services) gets the icon+badge trigger. */}
                    <NotesPanel
                      linkType="TRANSACTION"
                      linkId={stage.id}
                      linkLabel={stage.name || stage.id}
                      transactionId={transaction?.id}
                      transactionName={transaction?.transactionName || transaction?.propertyAddress}
                      portalTargetId={`txn-notes-target-stage-${stage.id}`}
                    />
                    </div>
                  </div>
                  {/* Portal target for this stage's Notes toggle+stack — see the
                      NotesPanel usage above. Placed inside the content box (like the
                      calendar-entry and active-milestone equivalents) so the notes
                      stack renders inside the same card, not floating below it. */}
                  <div id={`txn-notes-target-stage-${stage.id}`} className="txn-notes-block" />
                </div>
              </div>
              {trailingEvents.map((entry, eventIndex) => renderCalendarEntryNode(
                entry,
                index < mergedProspectStages.length - 1 || eventIndex < trailingEvents.length - 1,
                currentItemKey === `event-${entry.id}`
              ))}
              </React.Fragment>
            );
          });
          })()}
        </div>

        {/* Convert to Active — LISTING side only. Kicks off the 5-stage Active
            Listing checklist (rendered once the transaction leaves PENDING).
            Mirrors mobile's "Ready to go live?" card in TimelineTab.tsx. */}
        {transactionSide === 'LISTING' && isAgent && !isCancelledTransaction && stagesReady && mergedProspectStages.length > 0 && (
          <div className="txn-convert-active-card">
            <div className="txn-convert-active-header">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>
              <span className="txn-convert-active-title">Ready to go live?</span>
            </div>
            <p className="txn-convert-active-desc">
              You've captured everything needed on the prospect side. Convert this
              listing to Active to start the offer-review timeline — you'll add
              the MLS ID, log offer activity, and generate the full transaction
              timeline from there.
            </p>
            <button
              className="txn-timeline-button txn-primary-btn txn-convert-active-btn"
              onClick={onStartActiveListing}
              disabled={isConvertingToActiveListing}
            >
              {isConvertingToActiveListing ? 'Converting…' : (
                <>
                  Convert to Active
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6 }}><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </>
              )}
            </button>
          </div>
        )}
        {changeDateModal}
        {assignSpModal}
        {srDetailsModal}
      {checklistModal}
      </div>

      {/* Confirm Send Offer Package modal — mirrors mobile OfferSubmissionCard's
          "Confirm Documents Before Sending" popup, styled to match this page's
          existing Change Date modal (same overlay/card/button conventions).
          Rendered here (not in the outer return) since buyer_offer_submission —
          and its Send Offer Package button — only exist in the Prospect timeline tree. */}
      {showConfirmSendOfferModal && (
        <div className="txn-date-modal-overlay" onClick={handleConfirmSendOfferNo}>
          <div className="txn-confirm-send-modal" onClick={e => e.stopPropagation()}>
            <div className="txn-confirm-send-icon-wrap">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <polyline points="9 15 11 17 15 12.5"/>
              </svg>
            </div>
            <div className="txn-confirm-send-title">Confirm Documents Before Sending</div>
            <p className="txn-confirm-send-body">
              Please confirm all required documents are attached to this offer. Do you want to proceed?
            </p>
            <div className="txn-confirm-send-actions">
              <button className="txn-date-modal-cancel txn-confirm-send-btn" onClick={handleConfirmSendOfferNo}>
                No
              </button>
              <button className="txn-date-modal-save txn-confirm-send-btn" onClick={handleConfirmSendOfferYes}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pre-Approval Letter Preview — 3-tier fallback (local file / GCS download / legacy notice) */}
      {showPreApprovalPreview && createPortal(
        <div className="pdf-viewer-overlay" onClick={closePreApprovalPreview}>
          <div className="pdf-viewer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-viewer-header">
              <div className="pdf-viewer-title-section">
                <span className="pdf-viewer-icon">📄</span>
                <h3 className="pdf-viewer-title">{offerPreApprovalFile?.name || 'Pre-Approval Letter'}</h3>
              </div>
              <div className="pdf-viewer-actions">
                <button className="pdf-viewer-close-btn" onClick={closePreApprovalPreview} title="Close">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="pdf-viewer-body">
              {isLoadingPreApprovalPreview ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 8 }}>
                  <span className="btn-spinner"></span><span>Loading preview…</span>
                </div>
              ) : preApprovalPreviewTier === 'legacy' ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 8, textAlign: 'center', padding: '0 24px' }}>
                  <span>Preview unavailable — this file was uploaded before the GCS migration.</span>
                  <span>Re-upload it to enable previewing.</span>
                </div>
              ) : (
                <iframe
                  src={preApprovalPreviewUrl}
                  title={offerPreApprovalFile?.name || 'Pre-Approval Letter'}
                  className="pdf-viewer-iframe"
                />
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Offer Wizard Modal — CBS (11-step) for CO, RPA (10-step) for all other states */}
      {txnState === 'CO' ? (
        <CBSWizardModal
          isOpen={showRPAWizard}
          onClose={() => { setShowRPAWizard(false); }}
          onComplete={() => {
            setShowRPAWizard(false);
            setRpaDraft(prev => prev ? { ...prev, status: 'SUBMITTED' } : prev);
            onProspectStageComplete && onProspectStageComplete('buyer_offer_preparation');
          }}
          onProgressUpdate={(completedSteps, currentStep) => {
            setRpaDraft(prev => prev ? { ...prev, completedSteps, currentStep } : prev);
          }}
          transaction={transaction}
          draftData={rpaDraft}
          participants={participants}
        />
      ) : (
        <RPAWizardModal
          isOpen={showRPAWizard}
          onClose={() => { setShowRPAWizard(false); }}
          onComplete={() => {
            setShowRPAWizard(false);
            setRpaDraft(prev => prev ? { ...prev, status: 'SUBMITTED' } : prev);
            onProspectStageComplete && onProspectStageComplete('buyer_offer_preparation');
          }}
          onProgressUpdate={(completedSteps, resumeAtStep) => {
            setRpaDraft(prev => prev ? { ...prev, completedSteps, currentStep: resumeAtStep } : prev);
          }}
          transaction={transaction}
          draftData={rpaDraft}
          participants={participants}
          interestedProperty={rpaInterestedProperty}
          envelopeId={rpaDraft?.envelopeId || null}
        />
      )}
      {/* TDS Wizard Modal */}
      <TDSWizardModal
        isOpen={showTDSWizard}
        onClose={() => { setShowTDSWizard(false); }}
        readOnly={isAgent && !isSeller}
        onReviewComplete={(isAgent && !isSeller) ? handleCompleteTDSReview : undefined}
        onComplete={!(isAgent && !isSeller) ? () => {
          setShowTDSWizard(false);
          setTdsDraft(prev => prev ? { ...prev, status: 'SUBMITTED' } : prev);
          // CO maps TDS+SPQ into a single SPD form — complete seller_spd_form for CO (matches mobile)
          onProspectStageComplete && onProspectStageComplete(txnState === 'CO' ? 'seller_spd_form' : 'seller_tds_form');
        } : undefined}
        onProgressUpdate={(completedSteps, resumeAtStep) => {
          setTdsProgress({ completedSteps, resumeAtStep });
        }}
        transaction={enrichedSellerTransaction}
        draftData={tdsDraft}
      />
      {/* SPQ Wizard Modal */}
      <SPQWizardModal
        isOpen={showSPQWizard}
        onClose={() => { setShowSPQWizard(false); }}
        readOnly={isAgent && !isSeller}
        onReviewComplete={(isAgent && !isSeller) ? handleCompleteSPQReview : undefined}
        onComplete={!(isAgent && !isSeller) ? () => {
          setShowSPQWizard(false);
          setSpqDraft(prev => prev ? { ...prev, status: 'SUBMITTED' } : prev);
          // CO maps TDS+SPQ into a single SPD form — complete seller_spd_form for CO (matches mobile)
          onProspectStageComplete && onProspectStageComplete(txnState === 'CO' ? 'seller_spd_form' : 'seller_spq_form');
        } : undefined}
        onProgressUpdate={(completedSteps, resumeAtStep) => {
          setSpqProgress({ completedSteps, resumeAtStep });
        }}
        transaction={enrichedSellerTransaction}
        draftData={spqDraft}
      />

      {/* SPD Wizard Modal — CO listing: Seller edits, Agent reviews read-only */}
      {txnState === 'CO' && (isSeller || isAgent) && (
        <SPDWizardModal
          isOpen={showSPDWizard}
          onClose={() => setShowSPDWizard(false)}
          onComplete={() => {
            setShowSPDWizard(false);
            setSpdDraft(prev => prev ? { ...prev, status: 'SUBMITTED' } : prev);
            setSpdProgress(prev => prev ? { ...prev, completedSteps: [1,2,3,4,5,6,7,8,9,10] } : { completedSteps: [1,2,3,4,5,6,7,8,9,10] });
            onProspectStageComplete && onProspectStageComplete('seller_spd_form');
          }}
          onProgressUpdate={(completedSteps, currentStep) => {
            setSpdProgress({ completedSteps, currentStep });
          }}
          transaction={enrichedSellerTransaction}
          draftData={spdDraft}
          readOnly={isAgent && !isSeller}
          onReviewComplete={isAgent && !isSeller ? handleCompleteSPDReview : undefined}
          participants={participants}
        />
      )}

      {/* Offer Submission — hidden file input for pre-approval */}
      <input
        ref={preApprovalInputRef}
        type="file"
        accept=".pdf,application/pdf"
        style={{ display: 'none' }}
        onChange={handlePickPreApproval}
      />
      {/* Offer Submission — hidden file input for extra attachments */}
      <input
        ref={offerExtraFileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleAddExtraAttachment}
      />

      {/* Offer Submission — email edit modal */}
      {showOfferEmailModal && (
        <div className="txn-offer-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowOfferEmailModal(false); }}>
          <div className="txn-offer-modal-sheet">
            <div className="txn-offer-modal-header">
              <span className="txn-offer-modal-title">Edit Email</span>
              <button className="txn-offer-modal-close" onClick={() => setShowOfferEmailModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="txn-offer-modal-body">
              <textarea
                className="txn-offer-email-edit-input"
                value={offerEmailDraft}
                onChange={e => setOfferEmailDraft(e.target.value)}
                autoFocus
              />
            </div>
            <div className="txn-offer-modal-footer">
              <button className="txn-offer-modal-save-btn" onClick={handleSaveOfferEmailEdit}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
</>
    );
  }

  // Active Listing 5-stage checklist — rendered above the real milestone list
  // once metadata.activeListingTimeline exists. Append semantics (not replace):
  // stays visible as a summary/audit trail even after the terminal stage
  // converts the transaction and real milestones appear. Mirrors mobile
  // TimelineTab.tsx's activeListingStages.map render exactly: no client-side
  // sort — the array is rendered in the order the API returns it in.
  const activeListingTimelineData = transaction?.metadata?.activeListingTimeline;
  const activeListingStages = Array.isArray(activeListingTimelineData?.stages) ? activeListingTimelineData.stages : [];
  const hasActiveListingTimeline = activeListingStages.length > 0;

  // BUYING counterpart (post-2026-10-29): TX / DEFAULT buyer transactions carry
  // metadata.activeBuyingTimeline after convertToActive seeds it on the backend.
  // CA / CO buyer transactions never carry this key (they're milestone-driven).
  // First iteration renders stages read-only; Mark As / N/A actions follow in a
  // follow-up PR.
  const activeBuyingTimelineData = transaction?.metadata?.activeBuyingTimeline;
  const activeBuyingStages = Array.isArray(activeBuyingTimelineData?.stages) ? activeBuyingTimelineData.stages : [];
  const hasActiveBuyingTimeline = activeBuyingStages.length > 0;

  // "Add to Calendar" — lives above the ENTIRE timeline (Active Listing +
  // milestones), not just above the milestone list, so it's available as
  // soon as any timeline content exists.
  const renderAddCalendarButton = () => canAddCalendar && (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
      <button
        className="txn-timeline-button txn-primary-btn txn-add-to-calendar-btn"
        onClick={() => onShowCalendarModal && onShowCalendarModal(null)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        Add to Calendar
      </button>
    </div>
  );

  // `hasMilestonesAfter` tells the last Active Listing row whether real
  // milestones follow it, so the connecting line/divider only stop short
  // when this row is truly the last row of the ENTIRE timeline.
  // Read-only active-buying stage render. Simpler than renderActiveListingStages
  // — no MLS-ID input, no PA upload drop zone. Mark As / N/A actions are a
  // follow-up (prospect.service helpers already live in the mobile app; web
  // will mirror once the UI pattern is agreed).
  const renderActiveBuyingStages = (stages, hasMilestonesAfter) => stages.map((stage, index) => {
    const meta = activeBuyingStagesReady ? getActiveBuyingStageMeta(txnState, stage.stageId) : undefined;
    const isDone = stage.status === 'COMPLETED';
    const isSkipped = stage.status === 'SKIPPED';
    const iconStatus = isDone ? 'completed' : isSkipped ? 'skipped' : 'pending';
    const statusText = isDone ? 'Completed' : isSkipped ? 'Not applicable' : (meta?.description || '');
    const isLastOverall = index === stages.length - 1 && !hasMilestonesAfter;
    return (
      <div key={`active-buying-${stage.stageId}`} className="txn-timeline-item">
        <div className={`txn-timeline-icon ${iconStatus}`}>
          {renderMilestoneIcon(iconStatus)}
        </div>
        {!isLastOverall && (
          <div className={`txn-timeline-line ${isDone ? 'txn-timeline-line-completed' : isSkipped ? 'txn-timeline-line-skipped' : ''}`}></div>
        )}
        <div className="txn-timeline-content-box">
          <div className="txn-timeline-header">
            <div className="txn-timeline-header-left">
              <div className="txn-timeline-title">{meta?.name ?? stage.stageId}</div>
              {statusText && <div className={`txn-milestone-status-text ${iconStatus}`}>{statusText}</div>}
            </div>
            <div className="txn-timeline-date">
              {isDone && stage.completedAt ? formatUtcNaiveInstantForViewer(stage.completedAt).dateLabel : null}
              {meta?.roleLabel && <span className="txn-role-label">{meta.roleLabel}</span>}
            </div>
          </div>
        </div>
      </div>
    );
  });

  const renderActiveListingStages = (stages, hasMilestonesAfter) => stages.map((stage, index) => {
    const meta = activeListingStagesReady ? getActiveListingStageMeta(txnState, stage.stageId) : undefined;
    const isDone = stage.status === 'COMPLETED';
    const isSkipped = stage.status === 'SKIPPED';
    const showActions = !isDone && !isSkipped && isAgent && !isCancelledTransaction && !isClosedTransaction;
    // Same MilestoneStatus enum the real milestone rows use, so both sections
    // share identical icon glyphs/colors via renderMilestoneIcon().
    const iconStatus = isDone ? 'completed' : isSkipped ? 'skipped' : 'pending';
    const statusText = isDone ? 'Completed' : isSkipped ? 'Not applicable' : (meta?.description || '');
    const isLastOverall = index === stages.length - 1 && !hasMilestonesAfter;
    return (
      <div key={`active-listing-${stage.stageId}`} className="txn-timeline-item">
        <div className={`txn-timeline-icon ${iconStatus}`}>
          {renderMilestoneIcon(iconStatus)}
        </div>
        {!isLastOverall && (
          <div className={`txn-timeline-line ${isDone ? 'txn-timeline-line-completed' : isSkipped ? 'txn-timeline-line-skipped' : ''}`}></div>
        )}
        <div className="txn-timeline-content-box">
          <div className="txn-timeline-header">
            <div className="txn-timeline-header-left">
              <div className="txn-timeline-title">{stage.stageId === 'upload_purchase_agreement' ? 'Upload Purchase Agreement and Counter Offer' : (meta?.name ?? stage.stageId)}</div>
              {statusText && <div className={`txn-milestone-status-text ${iconStatus}`}>{statusText}</div>}
            </div>
            <div className="txn-timeline-date">
              {isDone && stage.completedAt ? formatUtcNaiveInstantForViewer(stage.completedAt).dateLabel : null}
              {/* Action role — same as mobile; "Listing Agent" until the stage metadata loads. */}
              <span className="txn-role-label">{meta?.roleLabel ?? 'Listing Agent'}</span>
            </div>
          </div>

          {stage.stageId === 'add_mls_id' && (
            <div className="txn-active-listing-mls-row">
              <input
                type="text"
                className="txn-active-listing-mls-input"
                placeholder="Enter MLS ID"
                value={activeListingMlsIdInput}
                onChange={(e) => onActiveListingMlsIdInputChange && onActiveListingMlsIdInputChange(e.target.value)}
                disabled={isDone || isSkipped || isSavingMlsId}
              />
            </div>
          )}

          {/* upload_purchase_agreement — dashed drop zone (click OR drag/drop
              to add a file) + list of uploaded files with inline label dropdown.
              The dashed zone IS the upload action — the right-aligned
              "Upload" button in the actions row below is suppressed for this
              stage via showUploadButton below (duplicate action). */}
          {stage.stageId === 'upload_purchase_agreement' && isAgent && (
            <div className="txn-active-listing-upload-wrap">
              <input
                ref={purchaseAgreementFileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                style={{ display: 'none' }}
                onChange={e => handlePurchaseAgreementFileChange(e)}
              />
              {/* Dropzone only until the first doc is added — once the agent has selected
                  the Purchase Agreement (+ counters), the selection is final: no re-upload. */}
              {!isDone && !isSkipped && purchaseAgreementDocuments.length === 0 && (
                <div
                  className={`txn-upload-pa-dropzone${isUploadingPurchaseAgreement ? ' txn-upload-pa-dropzone-disabled' : ''}`}
                  onClick={() => {
                    // Opens the "Add documents" picker (select/upload + tag) instead of the OS
                    // file browser. Selection is parsed later at "Process and generate timelines".
                    if (!isUploadingPurchaseAgreement) onOpenPurchaseAgreementPicker && onOpenPurchaseAgreementPicker();
                  }}
                >
                  <div className="txn-upload-pa-dropzone-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                  </div>
                  <div className="txn-upload-pa-dropzone-title">
                    {isUploadingPurchaseAgreement
                      ? 'Uploading…'
                      : purchaseAgreementDocuments.length > 0
                        ? 'Add another document'
                        : 'Add a document'}
                  </div>
                  <div className="txn-upload-pa-dropzone-sub">
                    <span className="txn-upload-pa-dropzone-sub-emph">Select or upload files</span> · PDF · Max 25 MB
                  </div>
                </div>
              )}
              {purchaseAgreementDocuments.length > 0 && (
                <div className="txn-nhd-docs-list">
                  {purchaseAgreementDocuments.map(doc => {
                    const docId = doc.documentId || doc.id;
                    const isDeleting = isDeletingPurchaseAgreementDoc === docId;
                    const label = getDocLabel(doc);
                    const sizeText = formatFileSize(doc.sizeBytes || doc.size);
                    return (
                      <div key={docId} className="txn-upload-pa-file-row">
                        <svg className="txn-upload-pa-file-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        <div className="txn-upload-pa-file-meta">
                          <div className="txn-upload-pa-file-name">{doc.fileName || doc.name || 'Document'}</div>
                          {!!sizeText && <div className="txn-upload-pa-file-size">{sizeText}</div>}
                        </div>
                        {/* Tag is set in the "Add a Document" picker (RPA / Counter chips)
                            now — show it read-only here instead of an editable dropdown. */}
                        {(() => {
                          const tag = doc._paLabel || label;
                          const tagText = tag === 'COUNTER_OFFER' ? 'Counter Offer'
                            : tag === 'LISTING_AGREEMENT' ? 'Listing Agreement'
                            : tag === 'OTHER' ? 'Other'
                            : 'Purchase Agreement';
                          return (
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#381F83', background: '#f0ecf9', padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap' }}>
                              {tagText}
                            </span>
                          );
                        })()}
                        {/* No remove (×) — the selection is final once docs are added. */}
                      </div>
                    );
                  })}
                </div>
              )}
              {purchaseAgreementUploadError && (
                <div className="txn-rpa-no-property-error">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {purchaseAgreementUploadError}
                </div>
              )}
              {/* The separate "Upload Listing Agreement" button that lived
                  here was removed — the stage's regular Upload button
                  (right-aligned actions row) already covers file uploads
                  for this stage. If the backend still needs the LA parsed
                  into transaction metadata, that happens via the earlier
                  Prospect-timeline "Listing Agreement Signed" stage, not
                  here. */}

            </div>
          )}

          <div className="txn-timeline-actions">
            {/* Single right-aligned cluster (see the equivalent comment in the
                Prospect-stage block above) — showActions content, the N/A badge,
                and Notes all share ONE .txn-milestone-right-actions wrapper so
                only one auto-margin is ever in play on this row. */}
            <div className="txn-milestone-right-actions">
            {showActions && (
              <>
                {/* The action-row Upload button for upload_purchase_agreement
                    was suppressed — the dashed drop zone rendered inside the
                    stage body above IS the upload action, so this would be a
                    duplicate action. "Mark as" + Notes still render below. */}
                {/* Same "Mark as" dropdown (Complete/N/A) used by Prospect stages and
                    real milestones — matches Buying's behavior instead of two flat buttons. */}
                <div className="txn-mark-as-wrapper" ref={openMarkAsId === stage.stageId ? markAsRef : null}>
                  <button
                    className="txn-timeline-button txn-primary-btn"
                    disabled={stage.stageId === 'add_mls_id' && isSavingMlsId}
                    onClick={() => setOpenMarkAsId(openMarkAsId === stage.stageId ? null : stage.stageId)}
                  >
                    {stage.stageId === 'add_mls_id' && isSavingMlsId ? 'Saving…' : (
                      <>
                        Mark as
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 4 }}>
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </>
                    )}
                  </button>
                  {openMarkAsId === stage.stageId && (
                    <div className={`txn-mark-as-dropdown${isLastOverall ? ' txn-mark-as-dropdown-flip-up' : ''}`}>
                      <button
                        className="txn-mark-as-option"
                        disabled={
                          (stage.stageId === 'add_mls_id' && !activeListingMlsIdInput.trim())
                          || (stage.stageId === 'upload_purchase_agreement' && purchaseAgreementDocuments.length === 0)
                        }
                        onClick={() => {
                          if (stage.stageId === 'add_mls_id' && !activeListingMlsIdInput.trim()) return;
                          if (stage.stageId === 'upload_purchase_agreement' && purchaseAgreementDocuments.length === 0) return;
                          setOpenMarkAsId(null);
                          onActiveListingStageComplete && onActiveListingStageComplete(stage.stageId);
                        }}
                      >
                        <span className="txn-mark-as-icon txn-mark-as-complete">✓</span>
                        Complete
                      </button>
                      {stage.stageId !== 'process_and_generate_timelines' && (
                        <button
                          className="txn-mark-as-option"
                          onClick={() => { setOpenMarkAsId(null); onActiveListingStageSkip && onActiveListingStageSkip(stage.stageId); }}
                        >
                          <span className="txn-mark-as-icon txn-mark-as-na">—</span>
                          N/A
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* N/A badge — shown when this Active Listing stage is skipped
                (mirrors the standalone txn-timeline-actions block it used to
                live in, now folded into the always-rendered actions row so
                Notes below is never orphaned without a wrapper). */}
            {isSkipped && (
              <span className="txn-timeline-readonly-badge">Not applicable</span>
            )}

            {/* Notes — private to the viewer, scoped to this specific Active
                Listing stage, same pattern as the calendar/service-request
                entries, prospect stages, and active-milestone rows so every
                timeline section (not just Services) gets the icon+badge
                trigger. */}
            <NotesPanel
              linkType="TRANSACTION"
              linkId={stage.stageId}
              linkLabel={meta?.name ?? stage.stageId}
              transactionId={transaction?.id}
              transactionName={transaction?.transactionName || transaction?.propertyAddress}
              portalTargetId={`txn-notes-target-activelisting-${stage.stageId}`}
            />
            </div>
          </div>
          {/* Portal target for this stage's Notes toggle+stack — see the
              NotesPanel usage above. */}
          <div id={`txn-notes-target-activelisting-${stage.stageId}`} className="txn-notes-block" />
        </div>

        {/* Divider between rows — hidden only on the very last row of the
            entire timeline (Active Listing + milestones combined) so both
            sections read as one continuous list. */}
        {!isLastOverall && <div className="txn-milestone-divider"></div>}
      </div>
    );
  });

  // ACTIVE transaction but no milestones yet.
  // Gated on standaloneCalendarEntries.length === 0 so an SR raised from
  // Dashboard → Select Property against a milestone-empty ACTIVE txn
  // (BUYING side + SALE type skips auto-apply per TransactionDetailPage)
  // still renders — the prospect branch never hid events and neither
  // should this one. When events exist, fall through to the main render
  // which handles ALT + events + zero-milestone deduplicatedTimelineData.
  if (!isPending && milestones.length === 0 && standaloneCalendarEntries.length === 0) {
    // Active Listing checklist in progress — show it even before any real
    // milestones exist (they only appear once the terminal stage converts).
    // Suppressed on CLOSED (spec Row 5: active timeline is locked history
    // on CLOSED; only POST_CLOSING items are visible, which would be in
    // deduplicatedTimelineData so this zero-milestone branch wouldn't fire
    // anyway — belt-and-braces gate).
    if ((hasActiveListingTimeline || hasActiveBuyingTimeline) && !isClosedTransaction) {
      return (
        <div className="txn-content-section">
          <div className="txn-content-header">
            <h2 className="txn-content-title">Timeline</h2>
          </div>
          <div className="txn-timeline-list">
            {renderAddCalendarButton()}
            {!!transaction?.metadata?.prospectTimeline
              && mergedProspectStages.length > 0 && (
                <>
                  <button
                    type="button"
                    className="txn-sr-docs-toggle txn-timeline-history-toggle"
                    onClick={() => setProspectCollapsed(prev => !prev)}
                    aria-expanded={!prospectCollapsed}
                  >
                    <span className="txn-sr-docs-toggle-text">Prospect history</span>
                    <span className="txn-sr-docs-count">{mergedProspectStages.length}</span>
                    <span className="txn-sr-docs-toggle-spacer" />
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {prospectCollapsed
                        ? <polyline points="6 9 12 15 18 9" />
                        : <polyline points="18 15 12 9 6 15" />}
                    </svg>
                  </button>
                  {!prospectCollapsed && mergedProspectStages.map((stage) => {
                    const isDone = stage.status === 'COMPLETED';
                    const isSkipped = stage.status === 'SKIPPED';
                    const iconStatus = isDone ? 'completed' : isSkipped ? 'skipped' : 'pending';
                    const statusText = isDone ? 'Completed' : isSkipped ? 'Not applicable' : (stage.description || '');
                    return (
                      <div key={`prospect-history-${stage.id}`} className="txn-timeline-item">
                        <div className={`txn-timeline-icon ${iconStatus}`}>
                          {renderMilestoneIcon(iconStatus)}
                        </div>
                        <div className={`txn-timeline-line ${isDone ? 'txn-timeline-line-completed' : isSkipped ? 'txn-timeline-line-skipped' : ''}`}></div>
                        <div className="txn-timeline-content-box">
                          <div className="txn-timeline-header">
                            <div className="txn-timeline-header-left">
                              <div className="txn-timeline-title">{stage.name || stage.id}</div>
                              {statusText && <div className={`txn-milestone-status-text ${iconStatus}`}>{statusText}</div>}
                            </div>
                            <div className="txn-timeline-date">
                              {isDone && stage.completedAt ? formatUtcNaiveInstantForViewer(stage.completedAt).dateLabel : null}
                              {stage.roleLabel && <span className="txn-role-label">{stage.roleLabel}</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            {renderActiveListingStages(activeListingStages, false)}
            {renderActiveBuyingStages(activeBuyingStages, false)}
          </div>
        </div>
      );
    }
    // Still loading or applying template — show spinner
    if (isLoading || isApplyingTemplate) {
      return (
        <div className="txn-content-section">
          <div className="txn-content-header">
            <h2 className="txn-content-title">Timeline</h2>
          </div>
          <div className="txn-timeline-setup">
            <div className="txn-setup-spinner"></div>
            <p className="txn-setup-text">Setting up timeline...</p>
          </div>
        </div>
      );
    }
    // Template apply finished (or failed) — show empty state so page doesn't get stuck
    return (
      <div className="txn-content-section">
        <div className="txn-content-header">
          <h2 className="txn-content-title">Timeline</h2>
        </div>
        <div className="txn-timeline-setup">
          <p className="txn-setup-text">No milestones found for this transaction.</p>
        </div>
      </div>
    );
  }

  // Render milestones for ACTIVE+ transactions.
  // Sort mirrors mobile sortMilestonesByDate: dueDate ascending first (ISO strings compare
  // correctly as plain strings), then sortOrder as the tiebreaker. AI-parsed milestones
  // often have no sortOrder, so date-first ensures chronological display for them.
  const sortedTimelineData = [...timelineData].sort((a, b) => {
    // Fall back to createdAt when dueDate is missing (mirrors mobile
    // sortMilestonesByDate: `a.dueDate || a.createdAt`). Without this, undated
    // milestones get '' and drop into the sortOrder branch, mixing date-sorted
    // and sortOrder-sorted items into a non-total order → stages render out of
    // ascending-date sequence.
    const dA = a.originalData?.dueDate || a.originalData?.createdAt || '';
    const dB = b.originalData?.dueDate || b.originalData?.createdAt || '';
    if (dA && dB && dA !== dB) return dA < dB ? -1 : 1;
    return (a.originalData?.sortOrder || 0) - (b.originalData?.sortOrder || 0);
  });

  // Deduplication — mirrors mobile deduplicatedMilestones logic:
  // hide any SKIPPED/WAIVED milestone when an active version (any other status)
  // with the same name already exists in the list. This prevents duplicate rows
  // that appear when a milestone is re-created after being skipped.
  const deduplicatedTimelineData = sortedTimelineData.filter(item => {
    const rawStatus = (item.originalData?.status || '').toUpperCase();
    if (rawStatus !== 'SKIPPED' && rawStatus !== 'WAIVED') return true;
    return !sortedTimelineData.some(
      other =>
        other.id !== item.id &&
        other.originalData?.name === item.originalData?.name &&
        (other.originalData?.status || '').toUpperCase() !== 'SKIPPED' &&
        (other.originalData?.status || '').toUpperCase() !== 'WAIVED'
    );
  });

  // Active rule: merge events into the due-date ordered milestone timeline.
  // Comparison uses YYYY-MM-DD string keys (see dateKeyOf) so the placement
  // survives timezone offsets — a UTC-ISO entry doesn't sort past a
  // local-midnight milestone dueDate on a positive-offset browser.
  const eventsAboveAllMilestones = [];
  const eventsAfterMilestoneIndex = {};
  [...standaloneCalendarEntries]
    .sort(sortCalendarEntries)
    .forEach(entry => {
      let insertAfter = -1;
      const entryKey = entryScheduleKey(entry);
      deduplicatedTimelineData.forEach((item, index) => {
        const milestoneDate = item.originalData?.dueDate || item.originalData?.createdAt;
        const milestoneKey = dateKeyOf(milestoneDate);
        if (milestoneKey && entryKey && milestoneKey <= entryKey) insertAfter = index;
      });
      if (insertAfter === -1) eventsAboveAllMilestones.push(entry);
      else (eventsAfterMilestoneIndex[insertAfter] ||= []).push(entry);
    });

  // "Ready to close?" card — always shown after the last entry of any ACTIVE timeline
  // (Purchase and Listing alike), regardless of what that final milestone is named or
  // typed (custom/AI-parsed timelines don't reliably carry a CLOSE_OF_ESCROW type/name).
  // Marking the last milestone complete only completes that milestone; the transaction's
  // status only flips to CLOSED via this explicit, separate action (mirrors mobile's
  // "Convert to Closed" card + confirmation).
  const showConvertToClosedCard = deduplicatedTimelineData.length > 0 && isAgent && !isCancelledTransaction && !isClosedTransaction;

  // Phase grouping is applicable only on CLOSED transactions that have BOTH pre-close
  // (ACTIVE / unset phase) rows and POST_CLOSING rows. Missing phase falls back to
  // ACTIVE to stay consistent with the backend read-path default in FirestoreConverter.
  const postClosingCount = deduplicatedTimelineData.filter(
    item => (item.originalData?.phase || 'ACTIVE') === 'POST_CLOSING'
  ).length;
  const historyItemCount = deduplicatedTimelineData.length - postClosingCount;
  const hasPhaseGrouping = isClosedTransaction && postClosingCount > 0 && historyItemCount > 0;

  // Acceptance-pending banner — shown when convertToActive persisted the
  // reviewed timeline but the parser couldn't resolve an acceptance anchor
  // (and the agent didn't supply one). Surfaces an inline date picker; on
  // submit the parent PATCHes /acceptance-date, which recomputes every
  // milestone carrying metadata.anchorPending and auto-completes ACCEPTANCE.
  const hasPendingAnchor = !transaction?.acceptanceDate
    && Array.isArray(milestones)
    && milestones.some((m) => m?.metadata?.anchorPending?.anchor === 'ACCEPTANCE');
  const showAcceptancePendingBanner = hasPendingAnchor && isAgent;

  return (
    <div className="txn-content-section">
      <div className="txn-content-header">
        <h2 className="txn-content-title">Timeline</h2>
        {isLoading && <span className="txn-loading-indicator">Loading...</span>}
      </div>

      {showAcceptancePendingBanner && (
        <div className="txn-acceptance-pending-banner" role="alert">
          <div className="txn-acceptance-pending-text">
            <strong>Enter the acceptance date</strong> to activate the pending deadlines on this timeline.
          </div>
          <form
            className="txn-acceptance-pending-form"
            onSubmit={(e) => {
              e.preventDefault();
              const value = e.target.elements['acceptanceDateInput']?.value;
              if (value && onSetAcceptanceDate) onSetAcceptanceDate(value);
            }}
          >
            <input
              type="date"
              name="acceptanceDateInput"
              max={new Date().toISOString().slice(0, 10)}
              required
              disabled={isSettingAcceptanceDate}
              className="txn-acceptance-pending-input"
            />
            <button
              type="submit"
              className="txn-acceptance-pending-save"
              disabled={isSettingAcceptanceDate}
            >
              {isSettingAcceptanceDate ? 'Saving…' : 'Save'}
            </button>
          </form>
        </div>
      )}

      <div className="txn-timeline-list">
        {(deduplicatedTimelineData.length > 0 || hasActiveListingTimeline || hasActiveBuyingTimeline) && renderAddCalendarButton()}
        {/* Converted-from-Prospect history — when the transaction carries
            metadata.prospectTimeline, render each prospect stage read-only
            at the TOP so the agent can see where the transaction came from
            (Register Buyer → Property Search → Offer Submission → RPA
            Signed → … → convertToActive). No Mark As, no actions — pure
            audit trail. User asked 2026-10-07 for this to be visible on
            the active timeline too. */}
        {!!transaction?.metadata?.prospectTimeline
          && mergedProspectStages.length > 0 && (
            <>
              <button
                type="button"
                className="txn-sr-docs-toggle txn-timeline-history-toggle"
                onClick={() => setProspectCollapsed(prev => !prev)}
                aria-expanded={!prospectCollapsed}
              >
                <span className="txn-sr-docs-toggle-text">Prospect history</span>
                <span className="txn-sr-docs-count">{mergedProspectStages.length}</span>
                <span className="txn-sr-docs-toggle-spacer" />
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {prospectCollapsed
                    ? <polyline points="6 9 12 15 18 9" />
                    : <polyline points="18 15 12 9 6 15" />}
                </svg>
              </button>
              {!prospectCollapsed && mergedProspectStages.map((stage, index) => {
                const isDone = stage.status === 'COMPLETED';
                const isSkipped = stage.status === 'SKIPPED';
                const iconStatus = isDone ? 'completed' : isSkipped ? 'skipped' : 'pending';
                const statusText = isDone ? 'Completed' : isSkipped ? 'Not applicable' : (stage.description || '');
                return (
                  <div key={`prospect-history-${stage.id}`} className="txn-timeline-item">
                    <div className={`txn-timeline-icon ${iconStatus}`}>
                      {renderMilestoneIcon(iconStatus)}
                    </div>
                    <div className={`txn-timeline-line ${isDone ? 'txn-timeline-line-completed' : isSkipped ? 'txn-timeline-line-skipped' : ''}`}></div>
                    <div className="txn-timeline-content-box">
                      <div className="txn-timeline-header">
                        <div className="txn-timeline-header-left">
                          <div className="txn-timeline-title">{stage.name || stage.id}</div>
                          {statusText && <div className={`txn-milestone-status-text ${iconStatus}`}>{statusText}</div>}
                        </div>
                        <div className="txn-timeline-date">
                          {isDone && stage.completedAt ? formatUtcNaiveInstantForViewer(stage.completedAt).dateLabel : null}
                          {stage.roleLabel && <span className="txn-role-label">{stage.roleLabel}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        {/* Active Listing checklist renders ABOVE the generated milestones
            on ACTIVE/UNDER_CONTRACT/CLOSING transactions, so the sequence
            reads: 5 static setup items first (Upload LA → MLS ID → Upload
            PA → Parse/Process → Process & Generate), then the parsed PA
            timeline below. Suppressed on CLOSED — spec Row 5: the whole
            active timeline (including checklist) is treated as locked
            history behind the "Previous timeline" toggle, with only
            POST_CLOSING items visible as live work. */}
        {hasActiveListingTimeline && !isClosedTransaction
          && renderActiveListingStages(activeListingStages, deduplicatedTimelineData.length > 0)}
        {hasActiveBuyingTimeline && !isClosedTransaction
          && renderActiveBuyingStages(activeBuyingStages, deduplicatedTimelineData.length > 0)}
        {eventsAboveAllMilestones.map(entry => renderCalendarEntryNode(entry, true))}
        {hasPhaseGrouping && (
          <button
            type="button"
            className="txn-sr-docs-toggle txn-timeline-history-toggle"
            onClick={() => setHistoryCollapsed(prev => !prev)}
            aria-expanded={!historyCollapsed}
          >
            <span className="txn-sr-docs-toggle-text">Previous timeline</span>
            <span className="txn-sr-docs-count">{historyItemCount}</span>
            <span className="txn-sr-docs-toggle-spacer" />
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {historyCollapsed
                ? <polyline points="6 9 12 15 18 9" />
                : <polyline points="18 15 12 9 6 15" />}
            </svg>
          </button>
        )}
        {deduplicatedTimelineData.map((item, index) => {
          // When phase grouping is active AND history is collapsed, hide pre-close
          // rows so only the actionable Post-Closing set remains visible. Toggle
          // above (Previous timeline) restores them without shifting the list order.
          const isHistoryRow = hasPhaseGrouping && (item.originalData?.phase || 'ACTIVE') !== 'POST_CLOSING';
          if (isHistoryRow && historyCollapsed) return null;

          const m = item.originalData;
          const milestoneActualId = m?.id || item.id;
          const statusText = m ? getMilestoneStatusText(m) : '';
          const assignedRoleLabel = m ? formatAssignedRole(m.assignedRole || ownerRoleFromRoleAccess(m?.metadata?.roleAccess)) : null;
          const isSkipped = item.status === 'skipped';
          const isCompleted = item.status === 'completed';
          const isInProgress = item.status === 'in-progress';
          const isOverdue = item.status === 'overdue';
          // Determine if user can act on this milestone (mirrors mobile canActOnMilestone)
          // CLOSE_OF_ESCROW is always actionable for agents regardless of roleAccess
          const isClosingMilestone = m?.type === 'CLOSE_OF_ESCROW' || m?.type === 'CLOSING'
            || m?.name?.toLowerCase() === 'close of escrow';
          const milestoneRoleAccess = m?.metadata?.roleAccess;
          // Agents / TC are actionable on EVERY milestone by default — the own-side
          // agent drives all the work. Principals (Buyer/Seller) stay gated by
          // roleAccess so they only act on their own tasks (e.g. buyer → deposit).
          const canActOnMilestone = isClosingMilestone
            ? isAgent  // agents always own close of escrow
            : isAgent
              ? true
              : milestoneRoleKey
                ? (milestoneRoleAccess ? milestoneRoleAccess[milestoneRoleKey] === 'ACTIONABLE' : false)
                : false;
          // Post-Closing rows stay actionable on a CLOSED transaction (spec item #4:
          // Agent + TC can edit Post-Closing dates and mark items complete). The
          // roleAccess check above already restricts to ACTIONABLE roles for Post-
          // Closing rows, so bypassing the isClosedTransaction gate here doesn't leak
          // edit rights to buyer/seller/opposite-side agent.
          const isPostClosingRow = (m?.phase || 'ACTIVE') === 'POST_CLOSING';
          const isActionable = canActOnMilestone
            && (item.status === 'pending' || isInProgress || isOverdue)
            && !isCancelledTransaction
            && (!isClosedTransaction || isPostClosingRow);

          // Date to show: completedDate (green) when done, dueDate otherwise.
          // When neither is set and the milestone carries metadata.anchorPending
          // (anchor=ACCEPTANCE with a stashed offsetDays), surface it as a
          // "Date pending" chip so the agent knows the row is intentional and
          // will auto-fill once they supply the acceptance date via the banner
          // above.
          const completedDate = m?.completedDate || m?.completedAt;
          const dueDate = m?.dueDate;
          const anchorPending = m?.metadata?.anchorPending;
          const isAnchorPending = !dueDate && anchorPending?.anchor === 'ACCEPTANCE';
          const displayDate = isCompleted && completedDate
            ? formatDate(completedDate)
            : (dueDate ? formatDate(dueDate) : (isAnchorPending ? null : item.date));
          const dateIsCompletion = isCompleted && !!completedDate;

          // Connecting line color class
          const lineClass = isCompleted ? 'txn-timeline-line-completed'
            : isInProgress ? 'txn-timeline-line-inprogress'
            : isSkipped ? 'txn-timeline-line-skipped'
            : isOverdue ? 'txn-timeline-line-overdue'
            : '';
          const trailingEvents = eventsAfterMilestoneIndex[index] || [];


          const handleMarkComplete = () => {
            setOpenMarkAsId(null);
            onCompleteMilestone && onCompleteMilestone(milestoneActualId);
          };

          const handleMarkNA = () => {
            setOpenMarkAsId(null);
            onSkipMilestone && onSkipMilestone(milestoneActualId);
          };

          return (
            <React.Fragment key={item.id}>
            <div className="txn-timeline-item">
              {/* Status icon */}
              <div className={`txn-timeline-icon ${item.status}`}>
                {renderMilestoneIcon(item.status)}
              </div>
              {/* Connecting line */}
              {index !== deduplicatedTimelineData.length - 1 && (
                <div className={`txn-timeline-line ${lineClass}`}></div>
              )}

              <div className="txn-timeline-content-box">
                <div className="txn-timeline-header">
                  <div className="txn-timeline-header-left">
                    <div className="txn-timeline-title">{item.title}</div>
                    <div className={`txn-milestone-status-text ${item.status}`}>{statusText}</div>
                  </div>
                  <div className="txn-timeline-date">
                    {(() => {
                      // Same phase-aware exception as isActionable above: Post-Closing
                      // dates stay editable on a CLOSED transaction so the agent/TC can
                      // adjust deadlines (spec item #4).
                      const isDateEditable = isAgent && !isCompleted && !isSkipped && m?.status !== 'WAIVED' && dueDate && (!isClosedTransaction || isPostClosingRow);
                      if (isDateEditable) {
                        return isDateSaving && editingDateMilestone?.id === milestoneActualId ? (
                          <span className="txn-date-saving">Saving…</span>
                        ) : (
                          <button
                            className={`txn-date-edit-btn${isOverdue ? ' txn-date-overdue' : ''}`}
                            onClick={() => handleDatePress(m)}
                            disabled={isDateSaving}
                          >
                            <span>{displayDate}</span>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                        );
                      }
                      if (displayDate) {
                        return (
                          <span className={dateIsCompletion ? 'txn-date-completed' : isOverdue ? 'txn-date-overdue' : ''}>
                            {displayDate}
                          </span>
                        );
                      }
                      if (isAnchorPending) {
                        return <span className="txn-date-pending">Date pending</span>;
                      }
                      return null;
                    })()}
                    {assignedRoleLabel && (
                      <span className="txn-role-label">{assignedRoleLabel}</span>
                    )}
                  </div>
                </div>

                <div className="txn-timeline-actions">
                  {/* Mark as dropdown — visible and fully enabled (no sequential lock) for any
                      milestone the user's role owns, matching the Prospect timeline's default.
                      The wrapper itself is now unconditional (not just when isActionable) so the
                      Notes trigger below still gets a right-aligned slot on milestones the current
                      role can't act on. */}
                  <div className="txn-milestone-right-actions">
                    {isActionable && (
                      <>
                        <div className="txn-mark-as-wrapper" ref={openMarkAsId === item.id ? markAsRef : null}>
                          <button
                            className="txn-timeline-button txn-primary-btn"
                            onClick={() => setOpenMarkAsId(openMarkAsId === item.id ? null : item.id)}
                          >
                            Mark as
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 4 }}>
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </button>
                          {openMarkAsId === item.id && (
                            <div className="txn-mark-as-dropdown">
                              <button className="txn-mark-as-option" onClick={handleMarkComplete}>
                                <span className="txn-mark-as-icon txn-mark-as-complete">✓</span>
                                Complete
                              </button>
                              <button className="txn-mark-as-option" onClick={handleMarkNA}>
                                <span className="txn-mark-as-icon txn-mark-as-na">—</span>
                                N/A
                              </button>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    {/* Notes — private to the viewer, scoped to this specific milestone
                        (milestoneActualId), same pattern as the calendar/service-request
                        entries above so every timeline section (not just Services) gets the
                        icon+badge trigger. Same underlying record shown in My Notes. */}
                    <NotesPanel
                      linkType="TRANSACTION"
                      linkId={milestoneActualId}
                      linkLabel={item.title}
                      transactionId={transaction?.id}
                      transactionName={transaction?.transactionName || transaction?.propertyAddress}
                      portalTargetId={`txn-notes-target-${item.id}`}
                    />
                  </div>

                  {/* N/A badge — shown to all roles when milestone is skipped */}
                  {isSkipped && (
                    <span className="txn-na-badge">N/A</span>
                  )}

                  {/* View Only — shown when API says this role has READ_ONLY access */}
                  {/* {!canActOnMilestone && !isSkipped && (
                    <span className="txn-timeline-readonly-badge">View Only</span>
                  )} */}
                </div>
                {/* Portal target for this milestone's Notes toggle+stack — see the
                    NotesPanel usage above. Empty by default; NotesPanel fills it via a
                    React portal only when this entry has notes or the composer is open. */}
                <div id={`txn-notes-target-${item.id}`} className="txn-notes-block" />
              </div>

              {/* Divider between milestone cards */}
              {index !== deduplicatedTimelineData.length - 1 && (
                <div className="txn-milestone-divider"></div>
              )}
            </div>
            {trailingEvents.map((entry, eventIndex) => renderCalendarEntryNode(
              entry,
              index < deduplicatedTimelineData.length - 1 || eventIndex < trailingEvents.length - 1
            ))}
            </React.Fragment>
          );
        })}

      </div>

      {/* "Ready to close?" — Convert to Closed, shown once Close of Escrow is the last
          milestone on the active timeline (Purchase and Listing alike). Completing the
          Close of Escrow milestone itself does NOT flip the transaction status; only
          this explicit action does. */}
      {showConvertToClosedCard && (
        <div className="txn-convert-active-card txn-convert-closed-card">
          <div className="txn-convert-active-header">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 22h16"/><path d="M6 18V9l6-4 6 4v9"/><path d="M10 22V14h4v8"/></svg>
            <span className="txn-convert-active-title">Ready to close?</span>
          </div>
          <p className="txn-convert-active-desc">
            When the deal has funded and recorded, convert this transaction to
            Closed to complete the timeline and move it to the Closed tab.
          </p>
          <button
            className="txn-timeline-button txn-primary-btn txn-convert-active-btn"
            onClick={() => setShowConvertClosedModal(true)}
            disabled={isConvertingToClosed}
          >
            {isConvertingToClosed ? 'Converting…' : (
              <>
                Convert to Closed
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6 }}><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </>
            )}
          </button>
        </div>
      )}

      {/* Convert to Closed confirmation — mirrors mobile's "Convert to Closed?" popup */}
      {showConvertClosedModal && (
        <div className="txn-date-modal-overlay" onClick={() => !isConvertingToClosed && setShowConvertClosedModal(false)}>
          <div className="txn-confirm-send-modal" onClick={e => e.stopPropagation()}>
            <div className="txn-confirm-send-icon-wrap">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 22h16"/><path d="M6 18V9l6-4 6 4v9"/><path d="M10 22V14h4v8"/></svg>
            </div>
            <div className="txn-confirm-send-title">Convert to Closed?</div>
            <p className="txn-confirm-send-body">
              This will mark <strong>{transaction?.transactionName || 'this transaction'}</strong> as
              Closed and move it to the Closed tab. You won't be able to edit milestones once closed.
            </p>
            <p className="txn-confirm-send-body">
              A <strong>$100 closure fee</strong> will be charged to your default payment method.
              You can also pay later from the Payments screen.
            </p>
            <div className="txn-confirm-send-actions">
              <button
                className="txn-date-modal-cancel txn-confirm-send-btn"
                onClick={() => setShowConvertClosedModal(false)}
                disabled={isConvertingToClosed}
              >
                Cancel
              </button>
              <button
                className="txn-date-modal-save txn-confirm-send-btn"
                onClick={async () => {
                  await onConvertToClosed?.();
                  setShowConvertClosedModal(false);
                }}
                disabled={isConvertingToClosed}
              >
                {isConvertingToClosed ? 'Converting…' : 'Convert'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Milestone date-edit modal (matches mobile TimelineTab date picker) ── */}
      {editingDateMilestone && (
        <div className="txn-date-modal-overlay" onClick={() => !isDateSaving && setEditingDateMilestone(null)}>
          <div className="txn-date-modal" onClick={e => e.stopPropagation()}>
            <div className="txn-date-modal-header">
              <span className="txn-date-modal-title">Change Date</span>
              <button
                className="txn-date-modal-close"
                onClick={() => !isDateSaving && setEditingDateMilestone(null)}
                disabled={isDateSaving}
              >✕</button>
            </div>
            <div className="txn-date-modal-milestone-name">{editingDateMilestone.name}</div>
            <input
              type="date"
              className="txn-date-modal-input"
              value={editingDate}
              onChange={e => setEditingDate(e.target.value)}
              disabled={isDateSaving}
            />
            <div className="txn-date-modal-actions">
              <button
                className="txn-date-modal-cancel"
                onClick={() => setEditingDateMilestone(null)}
                disabled={isDateSaving}
              >Cancel</button>
              <button
                className="txn-date-modal-save"
                onClick={saveMilestoneDate}
                disabled={isDateSaving || !editingDate}
              >{isDateSaving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {changeDateModal}
      {assignSpModal}
      {srDetailsModal}
      {checklistModal}
    </div>
  );
};

export default TransactionTimeline;
